import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trash2, Power, ExternalLink, ChevronRight, Image as ImageIcon, Video, LayoutGrid, Star, Zap, LogOut, ShieldOff, Upload, ArrowUp, ArrowDown, Camera, Film, FileText, ListChecks, Rocket, BarChart3, MousePointerClick, Languages, CheckCircle2, Loader2, Package, Inbox, Edit3, X, Check, Sparkles, Mail, Search, Send, RotateCcw, ChevronDown, Eye, Clock } from "lucide-react";
import AdvertStudioPublic from "@/components/AdvertStudioPublic";
import RestoreWizard from "@/components/RestoreWizard";
import SeoHealthPanel from "@/components/admin/SeoHealthPanel";
import PlansEditor from "@/components/PlansEditor";
import MarkdownEditor from "@/components/MarkdownEditor";
import { adminDiscoverApps, adminDiscoveryStatus, adminFindAppEmail, adminSendAppOutreach, adminAppOutreachLog, adminOutreachBatch, adminOutreachBatchStatus, adminListClaimEdits, adminApproveClaimEdit, adminRejectClaimEdit, adminNotifyAppPublished, adminInviteCompleteProfile, getApp, adminAppsVisibility, adminOutreachStatus, adminReplyContact, adminReplySponsor, adminOutreachFollowups, adminSendFollowup, adminOutreachFollowupBatch, adminOutreachReset, adminOutreachOptOut } from "@/lib/api";
import AdminHealthBanner from "@/components/AdminHealthBanner";
import {
  adminListAds, adminCreateAd, adminUpdateAd, adminDeleteAd, adminAiGenerateAds,
  adminListReviews, adminDeleteReview, adminSetReviewStatus,
  adminListAdvertSubmissions, adminApproveAdvert, adminRejectAdvert, adminDeleteAdvertSubmission,
  adminListBoosts, adminCreateBoost, adminUpdateBoost, adminDeleteBoost,
  adminMe, adminReset2FA, getAdminToken, setAdminToken,
  adminUploadImage, getAppMedia, adminCreateMedia, adminUpdateMedia,
  adminDeleteMedia, adminReorderMedia, mediaUrl,
  adminListicleTargets, adminQueueListicle, adminQueueAllListicles,
  adminClicksSummary, adminSubmissionsAnalytics, adminBannerAnalytics,
  adminPrewarmI18n, adminI18nStatus,
  adminCreateApp, adminUpdateApp, adminDeleteApp, adminListAllApps, adminToggleActive, adminVerifyOwner,
  adminListSubmissions, adminApproveSubmission, adminRejectSubmission, adminDeleteSubmission, adminUpdateSubmission, adminUpdateInvoice, adminTogglePremium, adminInboxSummary,
  adminListContact, adminUpdateContact, adminDeleteContact,
  adminListSponsors, adminUpdateSponsor, adminDeleteSponsor,
  adminListAuctions, adminCreateAuction, adminPatchAuction, adminForceCloseAuction, adminDeleteAuction, adminListAuctionBids, adminPushAuctionBanner,
  adminGetEmailSettings, adminSetEmailSettings, adminEmailTest, adminListSubscribers, adminNewsletterSendNow, adminNewsletterHistory,
  adminListClaims, adminDecideClaim,
  adminListDiscounts, adminCreateDiscount, adminDeleteDiscount,
  adminListBackups, adminCreateBackup, adminDeleteBackup, adminRunWeeklyBackup, adminRestoreHistory,
  adminDataIntegrity, adminDataIntegrityDelete,
  adminIndexNowPing,
  adminSeoBoostStatus, adminSeoBoostRunNow,
  adminContentQuality,
  adminDigestStatus, adminDigestRunNow, adminDigestPreview,
  adminEmailSendTest,
  listApps, listQueue,
} from "@/lib/api";
import { EN_BUNDLE, stableStringifyBundle, computeBundleHash } from "@/context/LanguageContext";

const PLACEMENTS = [
  "homepage-banner", "homepage-square", "homepage-video", "homepage-featured",
  "directory-banner", "directory-square",
  "category-banner", "category-square",
  "blog-banner", "blog-square",
  "compare-top", "compare-middle", "compare-bottom", "compare-square",
];
const AD_TYPES = ["banner", "square", "video", "featured"];
const TYPE_ICON = { banner: ImageIcon, square: LayoutGrid, video: Video, featured: Star };

const Tab = ({ value, current, onClick, children, testId }) => (
  <button
    type="button"
    onClick={() => onClick(value)}
    data-testid={testId}
    className={`px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
      current === value ? "border-[#2563EB] text-[#2563EB]" : "border-transparent text-[#64748B] hover:text-[#0F172A]"
    }`}
  >
    {children}
  </button>
);

const AdminPage = () => {
  // Parse `?tab=…` once so deep-links from notification emails (e.g.
  // "Review in admin →" → /admin?tab=claim-reviews&id=…) land on the
  // correct tab instead of dumping the admin on the default Ads view.
  const _initialTab = (() => {
    if (typeof window === "undefined") return "ads";
    try {
      const p = new URLSearchParams(window.location.search);
      const t = (p.get("tab") || "").trim();
      const allowed = new Set([
        "ads", "boosts", "apps", "submissions", "auctions", "contact", "media",
        "listicles", "analytics", "i18n", "newsletter", "claims", "discounts",
        "reviews", "advert-queue", "claim-reviews", "advert-studio", "backup", "data-integrity", "seo-health",
      ]);
      return allowed.has(t) ? t : "ads";
    } catch { return "ads"; }
  })();
  const [tab, setTab] = useState(_initialTab);
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  const [pendingSubs, setPendingSubs] = useState(0);
  const [pendingClaimEdits, setPendingClaimEdits] = useState(0);
  const [inbox, setInbox] = useState({ items: [], total: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    if (!getAdminToken()) { navigate("/admin/login"); return; }
    adminMe()
      .then((u) => { setMe(u); setChecking(false); })
      .catch(() => { setAdminToken(null); navigate("/admin/login"); });
  }, [navigate]);

  // Poll pending submissions + global inbox summary every 60s (and on tab switch)
  useEffect(() => {
    if (checking || !me) return;
    let cancelled = false;
    const load = () => {
      adminListSubmissions("pending")
        .then((rows) => { if (!cancelled) setPendingSubs((rows || []).length); })
        .catch(() => {});
      adminListClaimEdits("pending_review")
        .then((d) => { if (!cancelled) setPendingClaimEdits(d?.pending_count || 0); })
        .catch(() => {});
      adminInboxSummary()
        .then((d) => { if (!cancelled) setInbox(d || { items: [], total: 0 }); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [checking, me, tab]);

  const logout = () => { setAdminToken(null); navigate("/admin/login"); };
  const reset2FA = async () => {
    if (!window.confirm("Reset 2FA? You will need to re-scan a new QR code on next login.")) return;
    await adminReset2FA();
    setAdminToken(null);
    navigate("/admin/login");
  };

  if (checking) {
    return (
      <main className="hac-container py-20 text-center text-[#94A3B8]" data-testid="admin-loading">
        Checking authentication…
      </main>
    );
  }

  return (
    <main>
      <AdminHealthBanner />
      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <div className="hac-container py-10 max-w-6xl">
          <nav className="flex items-center gap-2 text-xs text-[#64748B] mb-4">
            <Link to="/" className="hover:text-[#2563EB]">Home</Link>
            <ChevronRight size={12} />
            <span className="text-[#0F172A] font-medium">Admin Console</span>
          </nav>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl md:text-5xl text-[#0F172A] leading-tight mb-2">Admin Console</h1>
              <p className="text-[#64748B]">Manage ads, app boosts and content.{me?.email && (<span className="ml-2 text-xs text-[#94A3B8]">Signed in as <strong>{me.email}</strong></span>)}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={reset2FA} className="px-3 py-2 text-xs rounded-full border border-[#E2E8F0] text-[#64748B] hover:border-[#EF4444] hover:text-[#EF4444] inline-flex items-center gap-1.5" data-testid="admin-reset-2fa">
                <ShieldOff size={12} /> Reset 2FA
              </button>
              <button onClick={logout} className="px-3 py-2 text-xs rounded-full border border-[#E2E8F0] text-[#64748B] hover:border-[#2563EB] hover:text-[#2563EB] inline-flex items-center gap-1.5" data-testid="admin-logout">
                <LogOut size={12} /> Sign out
              </button>
            </div>
          </div>
          {/* ===== Inbox summary strip ===== */}
          {inbox.total > 0 && (
            <div className="mt-6 bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-300 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="admin-inbox-strip">
              <div className="flex items-center gap-2 shrink-0">
                <Inbox size={16} className="text-amber-700" />
                <span className="text-sm font-bold text-amber-900">{inbox.total} pending item{inbox.total === 1 ? "" : "s"}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 flex-1">
                {inbox.items.filter((i) => i.count > 0).map((i) => (
                  <button
                    key={i.key}
                    onClick={() => setTab(i.tab)}
                    data-testid={`admin-inbox-pill-${i.key}`}
                    className="text-xs bg-white hover:bg-amber-200 border border-amber-300 text-amber-900 px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 transition-colors"
                  >
                    {i.label}
                    <span className="bg-amber-600 text-white text-[10px] font-bold px-1.5 rounded-full min-w-[18px] text-center">{i.count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-1 mt-6 border-b border-[#E2E8F0] -mb-px flex-wrap">
            <Tab value="ads" current={tab} onClick={setTab} testId="admin-tab-ads">Ads</Tab>
            <Tab value="boosts" current={tab} onClick={setTab} testId="admin-tab-boosts">App boosts</Tab>
            <Tab value="apps" current={tab} onClick={setTab} testId="admin-tab-apps">Apps</Tab>
            <Tab value="submissions" current={tab} onClick={setTab} testId="admin-tab-submissions">Submissions{pendingSubs > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold" data-testid="admin-tab-submissions-badge">{pendingSubs}</span>}</Tab>
            <Tab value="auctions" current={tab} onClick={setTab} testId="admin-tab-auctions">Auctions</Tab>
            <Tab value="contact" current={tab} onClick={setTab} testId="admin-tab-contact">Contact</Tab>
            <Tab value="media" current={tab} onClick={setTab} testId="admin-tab-media">Screenshots</Tab>
            <Tab value="listicles" current={tab} onClick={setTab} testId="admin-tab-listicles">Auto-listicles</Tab>
            <Tab value="analytics" current={tab} onClick={setTab} testId="admin-tab-analytics">Analytics</Tab>
            <Tab value="i18n" current={tab} onClick={setTab} testId="admin-tab-i18n">Translations</Tab>
            <Tab value="newsletter" current={tab} onClick={setTab} testId="admin-tab-newsletter">Newsletter</Tab>
            <Tab value="claims" current={tab} onClick={setTab} testId="admin-tab-claims">Claims</Tab>
            <Tab value="discounts" current={tab} onClick={setTab} testId="admin-tab-discounts">Discounts</Tab>
            <Tab value="reviews" current={tab} onClick={setTab} testId="admin-tab-reviews">Reviews</Tab>
            <Tab value="advert-queue" current={tab} onClick={setTab} testId="admin-tab-advert-queue">Advert queue</Tab>
            <Tab value="claim-reviews" current={tab} onClick={setTab} testId="admin-tab-claim-reviews">Claim reviews{pendingClaimEdits > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-orange-500 text-white text-[10px] font-bold" data-testid="admin-tab-claim-reviews-badge">{pendingClaimEdits}</span>}</Tab>
            <Tab value="advert-studio" current={tab} onClick={setTab} testId="admin-tab-advert-studio">Advert Studio</Tab>
            <Tab value="backup" current={tab} onClick={setTab} testId="admin-tab-backup">Backup</Tab>
            <Tab value="seo-health" current={tab} onClick={setTab} testId="admin-tab-seo-health">SEO health</Tab>
            <Tab value="data-integrity" current={tab} onClick={setTab} testId="admin-tab-data-integrity">Data integrity</Tab>
          </div>
        </div>
      </section>
      <section className="hac-container py-10 max-w-6xl">
        {tab === "ads" && <AdsTab />}
        {tab === "boosts" && <BoostsTab />}
        {tab === "apps" && <AppsTab />}
        {tab === "submissions" && <SubmissionsTab />}
        {tab === "auctions" && <AuctionsTab />}
        {tab === "contact" && <ContactTab />}
        {tab === "media" && <MediaTab />}
        {tab === "listicles" && <ListiclesTab />}
        {tab === "analytics" && <AnalyticsTab />}
        {tab === "i18n" && <TranslationsTab />}
        {tab === "newsletter" && <NewsletterTab />}
        {tab === "claims" && <ClaimsTab />}
        {tab === "discounts" && <DiscountsTab />}
        {tab === "reviews" && <ReviewsModerationTab />}
        {tab === "advert-queue" && <AdvertQueueTab />}
        {tab === "claim-reviews" && <ClaimReviewsTab />}
        {tab === "advert-studio" && (
          <div data-testid="advert-studio-tab" className="-mx-6 md:-mx-0">
            <AdvertStudioPublic isNl={false} admin={true} />
          </div>
        )}
        {tab === "backup" && <BackupTab />}
        {tab === "seo-health" && <SeoHealthPanel />}
        {tab === "data-integrity" && <DataIntegrityTab />}
      </section>
    </main>
  );
};

const AdsTab = () => {
  const [ads, setAds] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const emptyForm = {
    type: "banner", title: "", subtitle: "", image_url: "", video_url: "",
    target_url: "", placements: ["homepage-banner"], ends_at: "", active: true,
    targeting: { regions: [], exclude_apps: [], only_show_apps: [], exclude_premium_apps: false, free_apps_only: false },
  };
  const [form, setForm] = useState(emptyForm);
  const refresh = () => adminListAds().then(setAds).catch(() => setAds([]));
  useEffect(() => { refresh(); }, []);

  const startEdit = (ad) => {
    setEditingId(ad.id);
    const t = ad.targeting || {};
    setForm({
      type: ad.type || "banner",
      title: ad.title || "",
      subtitle: ad.subtitle || "",
      image_url: ad.image_url || "",
      video_url: ad.video_url || "",
      target_url: ad.target_url || "",
      placements: ad.placements && ad.placements.length ? [...ad.placements] : ["homepage-banner"],
      ends_at: ad.ends_at ? new Date(ad.ends_at).toISOString().slice(0, 16) : "",
      active: ad.active !== false,
      targeting: {
        regions: [...(t.regions || [])],
        exclude_apps: [...(t.exclude_apps || [])],
        only_show_apps: [...(t.only_show_apps || [])],
        exclude_premium_apps: !!t.exclude_premium_apps,
        free_apps_only: !!t.free_apps_only,
      },
    });
    // Scroll form into view (mobile / small screens)
    setTimeout(() => document.querySelector('[data-testid="ad-form-card"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.target_url) return;
    const payload = { ...form };
    if (payload.ends_at) payload.ends_at = new Date(payload.ends_at).toISOString();
    else payload.ends_at = null;
    if (editingId) {
      await adminUpdateAd(editingId, payload);
      setEditingId(null);
      setForm(emptyForm);
    } else {
      await adminCreateAd(payload);
      setForm({ ...form, title: "", subtitle: "", image_url: "", video_url: "", target_url: "", ends_at: "" });
    }
    refresh();
  };

  const toggleActive = async (ad) => {
    await adminUpdateAd(ad.id, { active: !ad.active });
    refresh();
  };

  const togglePlacement = (p) => {
    setForm((f) => ({
      ...f,
      placements: f.placements.includes(p) ? f.placements.filter((x) => x !== p) : [...f.placements, p],
    }));
  };

  return (
    <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8">
      <form onSubmit={submit} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 h-fit sticky top-24" data-testid="ad-form-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg text-[#0F172A]" data-testid="ad-form-title">
            {editingId ? "Edit ad" : "Create ad"}
          </h2>
          {editingId && (
            <button type="button" onClick={cancelEdit} className="text-xs text-[#64748B] hover:text-[#EF4444] underline" data-testid="ad-edit-cancel">
              Cancel
            </button>
          )}
        </div>
        <label className="overline block mb-2 text-[#64748B]">Type</label>
        <div className="flex gap-2 mb-4 flex-wrap">
          {AD_TYPES.map((t) => {
            const Icon = TYPE_ICON[t];
            return (
              <button key={t} type="button" onClick={() => setForm({ ...form, type: t })}
                className={`px-3 py-2 text-sm rounded-full border inline-flex items-center gap-1.5 ${
                  form.type === t ? "bg-[#2563EB] text-white border-[#2563EB]" : "bg-white border-[#E2E8F0] text-[#64748B]"
                }`} data-testid={`ad-type-${t}`}>
                <Icon size={14} /> {t}
              </button>
            );
          })}
        </div>

        <label className="overline block mb-1 text-[#64748B]">Title *</label>
        <input className="hac-input mb-3" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="ad-title" />
        <label className="overline block mb-1 text-[#64748B]">Subtitle</label>
        <input className="hac-input mb-3" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} data-testid="ad-subtitle" />
        <label className="overline block mb-1 text-[#64748B]">Image (upload — PNG/JPG/WebP, max 8 MB)</label>
        {form.image_url ? (
          <div className="mb-3 flex items-center gap-3 p-2 border border-[#E2E8F0] rounded-lg bg-[#F8FAFC]">
            <img src={form.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${form.image_url}` : form.image_url} alt="" className="w-16 h-16 object-cover rounded" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-[#64748B] truncate" data-testid="ad-image-url">{form.image_url}</div>
            </div>
            <button type="button" onClick={() => setForm({ ...form, image_url: "" })} className="text-xs text-[#EF4444] hover:underline" data-testid="ad-image-clear">Remove</button>
          </div>
        ) : (
          <label className="hac-input mb-3 flex items-center justify-center gap-2 cursor-pointer text-[#64748B] hover:border-[#2563EB] hover:text-[#2563EB]" data-testid="ad-image-upload-label">
            <Upload size={14} /> {uploadingImage ? "Uploading…" : "Click to upload banner image"}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" disabled={uploadingImage} data-testid="ad-image-upload"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setUploadingImage(true);
                try {
                  const { url } = await adminUploadImage(file);
                  setForm((f) => ({ ...f, image_url: url }));
                } catch (err) { alert(`Upload failed: ${err.response?.data?.detail || err.message}`); }
                finally { setUploadingImage(false); e.target.value = ""; }
              }}
            />
          </label>
        )}
        <label className="overline block mb-1 text-[#64748B]">…or paste an external image URL</label>
        <input className="hac-input mb-3" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://" data-testid="ad-image" />
        <label className="overline block mb-1 text-[#64748B]">Video URL (mp4 of YouTube)</label>
        <input className="hac-input mb-3" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://youtu.be/... of https://.../video.mp4" data-testid="ad-video" />
        <label className="overline block mb-1 text-[#64748B]">Target URL *</label>
        <input className="hac-input mb-3" value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} placeholder="https://" required data-testid="ad-target" />

        <label className="overline block mb-2 text-[#64748B]">Placements</label>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {PLACEMENTS.map((p) => (
            <button key={p} type="button" onClick={() => togglePlacement(p)}
              className={`px-2.5 py-1 text-xs rounded-full border ${
                form.placements.includes(p) ? "bg-[#EFF6FF] border-[#2563EB] text-[#2563EB]" : "bg-white border-[#E2E8F0] text-[#64748B]"
              }`} data-testid={`ad-placement-${p}`}>{p}</button>
          ))}
        </div>

        <details className="border border-[#E2E8F0] rounded-lg mb-4 bg-[#F8FAFC]" data-testid="ad-targeting-section">
          <summary className="cursor-pointer px-3 py-2 text-xs font-bold uppercase tracking-widest text-[#64748B] hover:text-[#2563EB]">
            Targeting (optional)
            {(form.targeting?.regions?.length || form.targeting?.exclude_apps?.length || form.targeting?.only_show_apps?.length || form.targeting?.exclude_premium_apps || form.targeting?.free_apps_only) ? (
              <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">active</span>
            ) : null}
          </summary>
          <div className="px-3 pb-3 space-y-2 text-xs">
            <p className="text-[11px] text-[#64748B] mt-1">Toon deze ad alleen wanneer alle regels matchen. Lege regels = geen beperking.</p>

            <div>
              <label className="overline block mb-1 text-[#64748B]">Regions (comma)</label>
              <input className="hac-input"
                value={(form.targeting?.regions || []).join(",")}
                onChange={(e) => setForm({ ...form, targeting: { ...form.targeting, regions: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } })}
                placeholder="bv. Europe,UK,Netherlands"
                data-testid="ad-targeting-regions" />
            </div>

            <div>
              <label className="overline block mb-1 text-[#64748B]">Only show when these apps are on page (comma slugs)</label>
              <input className="hac-input"
                value={(form.targeting?.only_show_apps || []).join(",")}
                onChange={(e) => setForm({ ...form, targeting: { ...form.targeting, only_show_apps: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } })}
                placeholder="equilab,ridely"
                data-testid="ad-targeting-only" />
            </div>

            <div>
              <label className="overline block mb-1 text-[#64748B]">Exclude pages with these apps (comma slugs)</label>
              <input className="hac-input"
                value={(form.targeting?.exclude_apps || []).join(",")}
                onChange={(e) => setForm({ ...form, targeting: { ...form.targeting, exclude_apps: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } })}
                placeholder="bv. equisight,horseanalytics"
                data-testid="ad-targeting-exclude" />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox"
                checked={!!form.targeting?.exclude_premium_apps}
                onChange={(e) => setForm({ ...form, targeting: { ...form.targeting, exclude_premium_apps: e.target.checked } })}
                data-testid="ad-targeting-no-premium" />
              <span>Niet tonen wanneer een Premium-app op de pagina staat (anti-kannibalisatie)</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox"
                checked={!!form.targeting?.free_apps_only}
                onChange={(e) => setForm({ ...form, targeting: { ...form.targeting, free_apps_only: e.target.checked } })}
                data-testid="ad-targeting-free-only" />
              <span>Alleen tonen wanneer elke app op de pagina een gratis versie heeft</span>
            </label>
          </div>
        </details>

        <label className="overline block mb-1 text-[#64748B]">Ends at (optional)</label>
        <input type="datetime-local" className="hac-input mb-4" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="ad-ends" />

        <button type="submit" className="hac-btn-primary w-full justify-center" data-testid="ad-submit">
          {editingId ? <><Check size={14} /> Save changes</> : <><Plus size={14} /> Create ad</>}
        </button>
      </form>

      <div className="space-y-3">
        <h2 className="font-bold text-lg text-[#0F172A] mb-2">{ads.length} ads</h2>
        {ads.map((ad) => (
          <div key={ad.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex items-start gap-4" data-testid={`ad-row-${ad.id}`}>
            <div className="w-16 h-16 shrink-0 bg-[#F1F5F9] rounded-lg flex items-center justify-center overflow-hidden">
              {ad.image_url ? <img src={ad.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${ad.image_url}` : ad.image_url} alt="" className="w-full h-full object-cover" /> :
                ad.video_url ? <Video size={20} className="text-[#64748B]" /> :
                <ImageIcon size={20} className="text-[#94A3B8]" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-widest font-semibold text-[#2563EB] bg-[#EFF6FF] px-2 py-0.5 rounded-full">{ad.type}</span>
                {ad.active ? (
                  <span className="text-[10px] uppercase font-semibold text-[#10B981]">● active</span>
                ) : (
                  <span className="text-[10px] uppercase font-semibold text-[#94A3B8]">○ off</span>
                )}
                {ad.ends_at && <span className="text-[10px] text-[#94A3B8]">ends {new Date(ad.ends_at).toLocaleDateString()}</span>}
              </div>
              <div className="font-bold text-[#0F172A] line-clamp-1">{ad.title}</div>
              <div className="text-xs text-[#94A3B8] line-clamp-1 mt-0.5">{ad.placements.join(" · ")}</div>
              <a href={ad.target_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#2563EB] inline-flex items-center gap-1 mt-1 hover:underline">
                {ad.target_url} <ExternalLink size={10} />
              </a>
            </div>
            <button onClick={() => startEdit(ad)} className={`p-2 ${editingId === ad.id ? "text-[#2563EB]" : "text-[#64748B] hover:text-[#2563EB]"}`} data-testid={`ad-edit-${ad.id}`} title="Edit">
              <Edit3 size={16} />
            </button>
            <button onClick={() => toggleActive(ad)} className="text-[#64748B] hover:text-[#2563EB] p-2" data-testid={`ad-toggle-${ad.id}`} title="Toggle active">
              <Power size={16} />
            </button>
            <button onClick={async () => { if (editingId === ad.id) cancelEdit(); await adminDeleteAd(ad.id); refresh(); }} className="text-[#94A3B8] hover:text-[#EF4444] p-2" data-testid={`ad-delete-${ad.id}`}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        {ads.length === 0 && <div className="text-center py-12 text-[#94A3B8]">No ads yet.</div>}
      </div>
    </div>
  );
};

const BoostsTab = () => {
  const [apps, setApps] = useState([]);
  const [boosts, setBoosts] = useState([]);
  const [form, setForm] = useState({ slug: "", multiplier: 1.15, note: "", ends_at: "", active: true });

  const refresh = () => {
    listApps({ limit: 100 }).then(setApps);
    adminListBoosts().then(setBoosts).catch(() => setBoosts([]));
  };
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.slug) return;
    const payload = { ...form };
    if (payload.ends_at) payload.ends_at = new Date(payload.ends_at).toISOString();
    else payload.ends_at = null;
    await adminCreateBoost(payload);
    refresh();
  };

  const boostMap = Object.fromEntries(boosts.map((b) => [b.slug, b]));

  return (
    <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8">
      <form onSubmit={submit} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 h-fit sticky top-24">
        <h2 className="font-bold text-lg text-[#0F172A] mb-4">Boost an app</h2>
        <p className="text-xs text-[#64748B] mb-4">
          A boost multiplies the app's composite score. 1.0 = no boost, 1.20 = +20% ranking lift.
        </p>
        <label className="overline block mb-1 text-[#64748B]">App *</label>
        <select className="hac-input mb-3" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required data-testid="boost-app">
          <option value="">Select an app…</option>
          {apps.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
        </select>
        <label className="overline block mb-1 text-[#64748B]">Multiplier</label>
        <input type="number" step="0.01" min="1.0" max="2.0" className="hac-input mb-3" value={form.multiplier} onChange={(e) => setForm({ ...form, multiplier: parseFloat(e.target.value) || 1 })} data-testid="boost-multiplier" />
        <label className="overline block mb-1 text-[#64748B]">Note</label>
        <input className="hac-input mb-3" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. Sponsored Q1 2026" data-testid="boost-note" />
        <label className="overline block mb-1 text-[#64748B]">Ends at (optional)</label>
        <input type="datetime-local" className="hac-input mb-4" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="boost-ends" />
        <button type="submit" className="hac-btn-primary w-full justify-center" data-testid="boost-submit">
          <Zap size={14} /> Apply boost
        </button>
      </form>

      <div>
        <h2 className="font-bold text-lg text-[#0F172A] mb-4">{boosts.length} active boosts</h2>
        <div className="space-y-2">
          {apps.map((a) => {
            const b = boostMap[a.slug];
            return (
              <div key={a.slug} className="bg-white border border-[#E2E8F0] rounded-xl px-4 py-3 flex items-center gap-3" data-testid={`boost-row-${a.slug}`}>
                <span className="w-9 h-9 flex items-center justify-center text-white font-bold rounded-md text-sm" style={{ backgroundColor: a.color || "#2563EB" }}>{a.name.charAt(0)}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#0F172A]">{a.name}</div>
                  <div className="text-xs text-[#94A3B8]">Score: <span className="font-mono">{a.score?.toFixed(1)}</span>
                  {b && <span className="ml-3 inline-flex items-center gap-1 text-[#2563EB]">
                    <Zap size={11} /> {b.multiplier}× {b.active ? "" : "(off)"} {b.note && `· ${b.note}`}
                  </span>}
                  </div>
                </div>
                {b && (
                  <>
                    <button onClick={async () => { await adminUpdateBoost(a.slug, { ...b, active: !b.active }); refresh(); }}
                      className="text-[#64748B] hover:text-[#2563EB] p-2" data-testid={`boost-toggle-${a.slug}`} title="Toggle">
                      <Power size={14} />
                    </button>
                    <button onClick={async () => { await adminDeleteBoost(a.slug); refresh(); }}
                      className="text-[#94A3B8] hover:text-[#EF4444] p-2" data-testid={`boost-delete-${a.slug}`}>
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const MediaTab = () => {
  const [apps, setApps] = useState([]);
  const [slug, setSlug] = useState("");
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ caption: "", feature_label: "", video_url: "" });

  useEffect(() => { listApps({ limit: 100 }).then(setApps); }, []);
  useEffect(() => {
    if (!slug) { setMedia([]); return; }
    getAppMedia(slug).then(setMedia).catch(() => setMedia([]));
  }, [slug]);

  const refresh = () => slug && getAppMedia(slug).then(setMedia);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !slug) return;
    setUploading(true);
    try {
      const { url } = await adminUploadImage(file);
      await adminCreateMedia(slug, {
        type: "image",
        url,
        caption: form.caption,
        feature_label: form.feature_label,
      });
      setForm({ ...form, caption: "", feature_label: "" });
      refresh();
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onAddVideo = async (e) => {
    e.preventDefault();
    if (!slug || !form.video_url) return;
    await adminCreateMedia(slug, {
      type: "video",
      url: form.video_url,
      caption: form.caption,
      feature_label: form.feature_label,
    });
    setForm({ caption: "", feature_label: "", video_url: "" });
    refresh();
  };

  const move = async (idx, dir) => {
    const next = [...media];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setMedia(next);
    await adminReorderMedia(slug, next.map((m) => m.id));
  };

  const updateField = async (id, field, value) => {
    await adminUpdateMedia(slug, id, { [field]: value });
    setMedia((arr) => arr.map((m) => (m.id === id ? { ...m, [field]: value } : m)));
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await adminDeleteMedia(slug, id);
    refresh();
  };

  return (
    <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8">
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 h-fit lg:sticky lg:top-24 space-y-5">
        <h2 className="font-bold text-lg text-[#0F172A]">Manage screenshots & videos</h2>

        <div>
          <label className="overline block mb-1 text-[#64748B]">App *</label>
          <select className="hac-input" value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="media-app">
            <option value="">Select an app…</option>
            {apps.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
          </select>
        </div>

        <div className="border-t border-[#E2E8F0] pt-5 space-y-3">
          <label className="overline block text-[#64748B]">Caption (optional)</label>
          <input className="hac-input" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} placeholder="e.g. Stable dashboard" data-testid="media-caption" />
          <label className="overline block text-[#64748B]">Feature label (optional)</label>
          <input className="hac-input" value={form.feature_label} onChange={(e) => setForm({ ...form, feature_label: e.target.value })} placeholder="e.g. Stable management" data-testid="media-feature-label" />
        </div>

        <div className="border-t border-[#E2E8F0] pt-5">
          <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-[#0F172A]">
            <Camera size={14} className="text-[#2563EB]" /> Upload screenshot
          </div>
          <label className={`hac-btn-primary justify-center w-full cursor-pointer ${(!slug || uploading) ? "opacity-50 pointer-events-none" : ""}`}>
            <Upload size={14} /> {uploading ? "Uploading…" : "Choose image"}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onUpload} disabled={!slug || uploading} data-testid="media-upload-input" />
          </label>
          <p className="text-[11px] text-[#94A3B8] mt-1.5">PNG · JPG · WebP · GIF — max 8 MB.</p>
        </div>

        <form onSubmit={onAddVideo} className="border-t border-[#E2E8F0] pt-5">
          <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-[#0F172A]">
            <Film size={14} className="text-[#2563EB]" /> Add video embed
          </div>
          <input className="hac-input mb-2" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="YouTube / Vimeo URL" data-testid="media-video-url" />
          <button type="submit" disabled={!slug || !form.video_url} className="hac-btn-secondary w-full justify-center disabled:opacity-50" data-testid="media-add-video">
            <Plus size={14} /> Add video
          </button>
        </form>
      </div>

      <div>
        <h2 className="font-bold text-lg text-[#0F172A] mb-4">
          {slug ? `${media.length} items for ${apps.find((a) => a.slug === slug)?.name || slug}` : "Pick an app first"}
        </h2>
        {!slug && <div className="text-center py-12 text-[#94A3B8] border border-dashed border-[#E2E8F0] rounded-2xl">Select an app on the left to manage its gallery.</div>}
        {slug && media.length === 0 && <div className="text-center py-12 text-[#94A3B8] border border-dashed border-[#E2E8F0] rounded-2xl">No screenshots or videos yet for this app.</div>}
        <div className="space-y-3">
          {media.map((m, idx) => (
            <div key={m.id} className="bg-white border border-[#E2E8F0] rounded-xl p-3 flex gap-4 items-start" data-testid={`media-row-${m.id}`}>
              <div className="w-32 h-20 shrink-0 bg-[#F1F5F9] rounded-lg overflow-hidden flex items-center justify-center">
                {m.type === "image" ? (
                  <img src={mediaUrl(m.url)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Film size={28} className="text-[#94A3B8]" />
                )}
              </div>
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-[#2563EB] bg-[#EFF6FF] px-2 py-0.5 rounded-full">
                    {m.type === "image" ? "Image" : (m.provider || "Video")}
                  </span>
                  <span className="text-[10px] text-[#94A3B8]">#{idx + 1}</span>
                </div>
                <input
                  className="hac-input text-sm py-1.5"
                  defaultValue={m.caption}
                  placeholder="Caption"
                  onBlur={(e) => e.target.value !== m.caption && updateField(m.id, "caption", e.target.value)}
                  data-testid={`media-caption-${m.id}`}
                />
                <input
                  className="hac-input text-sm py-1.5"
                  defaultValue={m.feature_label}
                  placeholder="Feature label"
                  onBlur={(e) => e.target.value !== m.feature_label && updateField(m.id, "feature_label", e.target.value)}
                  data-testid={`media-label-${m.id}`}
                />
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-[#64748B] hover:text-[#2563EB] disabled:opacity-30 p-1" data-testid={`media-up-${m.id}`}><ArrowUp size={14} /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === media.length - 1} className="text-[#64748B] hover:text-[#2563EB] disabled:opacity-30 p-1" data-testid={`media-down-${m.id}`}><ArrowDown size={14} /></button>
                <button onClick={() => del(m.id)} className="text-[#94A3B8] hover:text-[#EF4444] p-1" data-testid={`media-delete-${m.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ListiclesTab = () => {
  const [targets, setTargets] = useState([]);
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const [langs, setLangs] = useState(["en"]);
  const [bulkResult, setBulkResult] = useState(null);

  const refresh = () => {
    adminListicleTargets().then(setTargets).catch(() => setTargets([]));
    listQueue().then((items) => setQueue(items.filter((i) => i.kind === "listicle"))).catch(() => setQueue([]));
  };
  useEffect(() => { refresh(); }, []);

  const queueOne = async (label, language) => {
    setBusy(true);
    try {
      await adminQueueListicle(label, language);
      refresh();
    } finally { setBusy(false); }
  };

  const queueAll = async () => {
    if (langs.length === 0) return;
    if (!window.confirm(`Queue listicles for ${targets.length} feature labels × ${langs.length} language(s) = up to ${targets.length * langs.length} articles. Continue?`)) return;
    setBusy(true);
    try {
      const r = await adminQueueAllListicles(langs, 5);
      setBulkResult(r);
      refresh();
    } finally { setBusy(false); }
  };

  const toggleLang = (l) => setLangs((arr) => arr.includes(l) ? arr.filter((x) => x !== l) : [...arr, l]);

  const statusBy = (label) => {
    const matches = queue.filter((q) => q.feature_label === label);
    return matches; // array of {language, status, article_slug}
  };

  const langOptions = [
    { code: "en", label: "English" },
    { code: "nl", label: "Nederlands" },
    { code: "de", label: "Deutsch" },
    { code: "fr", label: "Français" },
    { code: "es", label: "Español" },
  ];

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-br from-[#EFF6FF] to-white border border-[#DBEAFE] rounded-2xl p-6" data-testid="listicle-hero">
        <div className="flex items-start gap-3">
          <Rocket size={20} className="text-[#2563EB] mt-1 shrink-0" />
          <div className="flex-1">
            <h2 className="font-bold text-xl text-[#0F172A] mb-1">Auto-generate "Top 5 apps for {`{feature}`}" listicles</h2>
            <p className="text-sm text-[#64748B] max-w-2xl">
              For every unique feature label across your screenshots, we'll queue an AI-written long-tail SEO article via the existing blog pipeline.
              Articles publish automatically every 60 seconds and appear at <code className="text-xs bg-white px-1.5 py-0.5 rounded border border-[#E2E8F0]">/blog/[slug]</code>.
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-1.5">
            {langOptions.map((l) => (
              <button
                key={l.code}
                onClick={() => toggleLang(l.code)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${langs.includes(l.code) ? "bg-[#2563EB] text-white border-[#2563EB]" : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#2563EB]"}`}
                data-testid={`listicle-lang-${l.code}`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <button
            onClick={queueAll}
            disabled={busy || targets.length === 0 || langs.length === 0}
            className="hac-btn-primary ml-auto disabled:opacity-50"
            data-testid="listicle-queue-all"
          >
            <ListChecks size={14} /> Queue all ({targets.length} × {langs.length})
          </button>
        </div>

        {bulkResult && (
          <div className="mt-4 text-sm bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#0F172A]" data-testid="listicle-bulk-result">
            ✓ Queued <strong>{bulkResult.queued}</strong> new article{bulkResult.queued === 1 ? "" : "s"} — skipped <strong>{bulkResult.skipped}</strong> already in pipeline.
          </div>
        )}
      </div>

      <div>
        <h3 className="font-bold text-lg text-[#0F172A] mb-3">Feature labels detected ({targets.length})</h3>
        {targets.length === 0 ? (
          <div className="text-center py-12 text-[#94A3B8] border border-dashed border-[#E2E8F0] rounded-2xl">
            No labeled screenshots yet. Add a few via the Screenshots tab — every unique <em>feature label</em> becomes a listicle target.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="listicle-targets">
            {targets.map((t) => {
              const items = statusBy(t.label);
              return (
                <div key={t.label_slug} className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid={`listicle-target-${t.label_slug}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="font-bold text-[#0F172A]">{t.label}</div>
                      <div className="text-xs text-[#94A3B8]">{t.apps_count} app{t.apps_count === 1 ? "" : "s"} tagged · /blog/{t.label_slug}-top-5</div>
                    </div>
                    <button
                      onClick={() => queueOne(t.label, langs[0] || "en")}
                      disabled={busy || langs.length === 0}
                      className="text-xs px-3 py-1.5 rounded-full bg-[#2563EB] text-white hover:bg-[#1D4ED8] inline-flex items-center gap-1 disabled:opacity-50"
                      data-testid={`listicle-queue-${t.label_slug}`}
                    >
                      <Plus size={11} /> Queue
                    </button>
                  </div>
                  {items.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {items.map((q) => (
                        <span
                          key={q.id}
                          className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${
                            q.status === "completed" ? "bg-[#DCFCE7] text-[#16A34A]" :
                            q.status === "processing" ? "bg-[#FEF3C7] text-[#CA8A04]" :
                            q.status === "error" ? "bg-[#FEE2E2] text-[#DC2626]" :
                            "bg-[#E0E7FF] text-[#4338CA]"
                          }`}
                          title={q.error || ""}
                        >
                          {q.language} · {q.status}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-bold text-lg text-[#0F172A] mb-3 flex items-center gap-2">
          <FileText size={16} className="text-[#64748B]" /> Listicle queue ({queue.length})
        </h3>
        {queue.length === 0 ? (
          <div className="text-center py-8 text-[#94A3B8] border border-dashed border-[#E2E8F0] rounded-2xl text-sm">
            No listicles queued yet.
          </div>
        ) : (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl divide-y divide-[#E2E8F0]" data-testid="listicle-queue-list">
            {queue.slice(0, 30).map((q) => (
              <div key={q.id} className="flex items-center gap-3 p-3 text-sm" data-testid={`listicle-queue-${q.id}`}>
                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${
                  q.status === "completed" ? "bg-[#DCFCE7] text-[#16A34A]" :
                  q.status === "processing" ? "bg-[#FEF3C7] text-[#CA8A04]" :
                  q.status === "error" ? "bg-[#FEE2E2] text-[#DC2626]" :
                  "bg-[#E0E7FF] text-[#4338CA]"
                }`}>{q.status}</span>
                <span className="font-semibold text-[#0F172A]">{q.feature_label}</span>
                <span className="text-[10px] uppercase tracking-widest text-[#94A3B8]">{q.language}</span>
                {q.apps_count !== undefined && <span className="text-xs text-[#94A3B8]">{q.apps_count} apps</span>}
                {q.article_slug && (
                  <a href={`/blog/${q.article_slug}`} target="_blank" rel="noreferrer" className="ml-auto text-xs text-[#2563EB] hover:underline inline-flex items-center gap-1">
                    View <ExternalLink size={11} />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Kpi = ({ label, value, sub, accent = false }) => (
  <div className={`p-4 rounded-xl border ${accent ? "bg-amber-100 border-amber-300" : "bg-white border-[#E2E8F0]"}`}>
    <div className="text-[10px] uppercase tracking-widest text-[#64748B] mb-1">{label}</div>
    <div className="text-2xl font-bold text-[#0F172A]">{value}</div>
    {sub && <div className="text-[11px] text-[#94A3B8] mt-1">{sub}</div>}
  </div>
);

const BreakdownCard = ({ title, rows }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
    <div className="text-[10px] uppercase tracking-widest text-[#64748B] mb-2">{title}</div>
    {rows.length === 0 ? <div className="text-xs text-[#94A3B8]">No data</div> : (
      <ul className="space-y-1">
        {rows.map(([k, v]) => (
          <li key={k} className="flex justify-between text-sm">
            <span className="text-[#0F172A] capitalize">{k}</span>
            <span className="font-mono text-[#64748B]">{v}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const AnalyticsTab = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState(null);
  const [banners, setBanners] = useState(null);

  useEffect(() => {
    setLoading(true);
    adminClicksSummary(days)
      .then(setData)
      .catch(() => setData({ total: 0, apps: [] }))
      .finally(() => setLoading(false));
    adminSubmissionsAnalytics(Math.max(days, 90)).then(setSubs).catch(() => setSubs(null));
    adminBannerAnalytics(days).then(setBanners).catch(() => setBanners(null));
  }, [days]);

  const sources = new Set();
  (data?.apps || []).forEach((a) => Object.keys(a.by_source || {}).forEach((s) => sources.add(s)));
  const sourceList = Array.from(sources).sort();
  const max = Math.max(1, ...(data?.apps || []).map((a) => a.total));

  return (
    <div className="space-y-8" data-testid="analytics-panel">
      {/* Submissions / Premium revenue funnel */}
      {subs && (
        <div className="bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-2xl p-6" data-testid="analytics-submissions">
          <div className="flex items-start gap-3 mb-5">
            <Star size={20} className="text-amber-500 fill-amber-500 mt-1 shrink-0" />
            <div className="flex-1">
              <h2 className="font-bold text-xl text-[#0F172A] mb-1">Premium revenue funnel</h2>
              <p className="text-sm text-[#64748B]">Last {subs.days} days. Conversion rate = % of submissions that pick a Premium tier. MRR/ARR only count <em>paid</em> invoices.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Submissions" value={subs.total_submissions} sub={`${(subs.by_status?.pending || 0)} pending review`} />
            <Kpi label="Premium chosen" value={subs.premium_submissions} sub={`${subs.premium_conversion_rate}% conversion`} accent />
            <Kpi label="Paid invoices" value={subs.premium_paid_count} sub={`${subs.premium_paid_rate}% of premium · ${subs.pending_invoices} pending`} />
            <Kpi label="ARR (paid)" value={`€${subs.arr_paid_eur.toLocaleString()}`} sub={`MRR €${subs.mrr_paid_eur.toLocaleString()}`} accent />
          </div>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <BreakdownCard title="By tier" rows={Object.entries(subs.by_tier).map(([k, v]) => [k.replace("_", " · "), v])} />
            <BreakdownCard title="By status" rows={Object.entries(subs.by_status).map(([k, v]) => [k, v])} />
          </div>
        </div>
      )}

      {/* Banner CTR */}
      {banners && (banners.banners || []).length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6" data-testid="analytics-banners">
          <h3 className="font-bold text-lg text-[#0F172A] mb-1">Banner performance</h3>
          <p className="text-xs text-[#64748B] mb-4">Impressions and clicks of segmented banners (last {banners.days} days). Owner-only banner is only shown to visitors who triggered an owner-signal (sponsor/submit/premium page visit, or matching UTM).</p>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-[#64748B]">
              <tr><th className="text-left py-1.5">Banner</th><th className="text-right py-1.5">Impressions</th><th className="text-right py-1.5">Clicks</th><th className="text-right py-1.5">CTR</th></tr>
            </thead>
            <tbody>
              {banners.banners.map((b) => (
                <tr key={b.banner} className="border-t border-[#F1F5F9]">
                  <td className="py-2 text-[#0F172A] font-medium">{b.banner === "owner_premium" ? "Owner → Premium banner" : b.banner}</td>
                  <td className="py-2 text-right font-mono text-[#64748B]">{b.impressions}</td>
                  <td className="py-2 text-right font-mono text-[#64748B]">{b.clicks}</td>
                  <td className="py-2 text-right font-mono text-amber-700 font-semibold">{b.ctr}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-gradient-to-br from-[#EFF6FF] to-white border border-[#DBEAFE] rounded-2xl p-6">
        <div className="flex items-start gap-3">
          <MousePointerClick size={20} className="text-[#2563EB] mt-1 shrink-0" />
          <div className="flex-1">
            <h2 className="font-bold text-xl text-[#0F172A] mb-1">Outbound click analytics</h2>
            <p className="text-sm text-[#64748B] max-w-2xl">
              Every "Visit" / "Open" link on the site routes through <code className="text-xs bg-white px-1.5 py-0.5 rounded border border-[#E2E8F0]">/api/go/{`{slug}`}</code> with UTM parameters
              (<code className="text-xs">utm_source=horseappcompare</code>, <code className="text-xs">utm_medium=</code>blog/detail/feature/spotlight).
              Clicks logged here can be shared with verified advertisers as proof of traffic.
            </p>
          </div>
        </div>
        <div className="mt-4 flex gap-1.5">
          {[7, 30, 90, 365].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${days === d ? "bg-[#2563EB] text-white border-[#2563EB]" : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#2563EB]"}`}
              data-testid={`analytics-range-${d}`}
            >
              {d === 365 ? "1y" : `${d}d`}
            </button>
          ))}
          <div className="ml-auto text-sm text-[#64748B] self-center">
            {loading ? "…" : (<><strong className="text-[#0F172A]">{data?.total || 0}</strong> total clicks</>)}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#94A3B8]">Loading…</div>
      ) : (data?.apps?.length || 0) === 0 ? (
        <div className="text-center py-16 text-[#94A3B8] border border-dashed border-[#E2E8F0] rounded-2xl">
          <BarChart3 size={32} className="mx-auto mb-3 opacity-50" />
          No clicks tracked yet in the last {days} days.
        </div>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
          <table className="w-full text-sm" data-testid="analytics-table">
            <thead className="bg-[#F8FAFC] text-[#64748B] text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">App</th>
                <th className="text-right p-3">Total</th>
                {sourceList.map((s) => (
                  <th key={s} className="text-right p-3">{s}</th>
                ))}
                <th className="text-left p-3 w-1/3">Share</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]">
              {data.apps.map((a) => (
                <tr key={a.slug} data-testid={`analytics-row-${a.slug}`}>
                  <td className="p-3">
                    <a href={`/apps/${a.slug}`} className="font-semibold text-[#0F172A] hover:text-[#2563EB]">{a.name}</a>
                    <div className="text-[10px] uppercase tracking-widest text-[#94A3B8]">/{a.slug}</div>
                  </td>
                  <td className="p-3 text-right font-bold text-[#0F172A]">{a.total}</td>
                  {sourceList.map((s) => (
                    <td key={s} className="p-3 text-right text-[#64748B]">{a.by_source?.[s] || 0}</td>
                  ))}
                  <td className="p-3">
                    <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                      <div className="h-full bg-[#2563EB]" style={{ width: `${(a.total / max) * 100}%` }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const TranslationsTab = () => {
  const [hash, setHash] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);

  // Compute the EN bundle hash once on mount (same algorithm as LanguageContext)
  useEffect(() => {
    computeBundleHash(stableStringifyBundle(EN_BUNDLE)).then(setHash);
  }, []);

  const refresh = async () => {
    if (!hash) return;
    try {
      const s = await adminI18nStatus(hash);
      setStatus(s);
      setRunning(s.job?.status === "running");
    } catch { /* ignore */ }
  };

  useEffect(() => { refresh(); }, [hash]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll every 4s while a job is running
  useEffect(() => {
    if (!running) return;
    const id = setInterval(refresh, 4000);
    return () => clearInterval(id);
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  const startPrewarm = async () => {
    if (!hash) return;
    if (!window.confirm("Translate the entire UI to all 22 supported languages? This calls the AI engine once per language and may take a few minutes. Cached languages are skipped.")) return;
    setBusy(true);
    try {
      await adminPrewarmI18n(EN_BUNDLE, hash, null);
      setRunning(true);
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const total = status?.total_languages ?? 22;
  const done = status?.cached_languages ?? 0;
  const pct = status?.progress ?? 0;
  const errors = status?.job?.errors || {};

  return (
    <div className="space-y-8" data-testid="i18n-panel">
      <div className="bg-gradient-to-br from-[#EFF6FF] to-white border border-[#DBEAFE] rounded-2xl p-6">
        <div className="flex items-start gap-3">
          <Languages size={20} className="text-[#2563EB] mt-1 shrink-0" />
          <div className="flex-1">
            <h2 className="font-bold text-xl text-[#0F172A] mb-1">Pre-warm 22 languages</h2>
            <p className="text-sm text-[#64748B] max-w-2xl">
              The site translates the UI on-demand via our AI engine and caches it in MongoDB.
              Pre-warming now means every visitor — German, French, Polish, Greek, Bulgarian, … — gets an
              instant first page-load instead of waiting for the translation to be computed.
              When you ship new English strings, the source-hash changes and the cache is rebuilt.
            </p>
            {hash && (
              <div className="mt-3 text-[11px] text-[#94A3B8] font-mono">source_hash: <span className="text-[#0F172A]">{hash.slice(0, 16)}…</span></div>
            )}
          </div>
          <button
            onClick={startPrewarm}
            disabled={busy || running || !hash}
            className="hac-btn-primary shrink-0 disabled:opacity-50"
            data-testid="i18n-prewarm-start"
          >
            {running ? (<><Loader2 size={14} className="animate-spin" /> Running…</>) : (<><Rocket size={14} /> Pre-warm all</>)}
          </button>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between text-xs text-[#64748B] mb-2">
            <span><strong className="text-[#0F172A]">{done}</strong> / {total} languages cached</span>
            <span className="font-mono">{pct}%</span>
          </div>
          <div className="h-2 bg-white rounded-full overflow-hidden border border-[#E2E8F0]" data-testid="i18n-progress">
            <div className="h-full bg-[#2563EB] transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-bold text-lg text-[#0F172A] mb-3">Languages ({total})</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="i18n-langs">
          {(status?.languages || []).filter((l) => !l.is_english).map((l) => (
            <div
              key={l.code}
              className={`flex items-center gap-3 p-3 rounded-xl border ${l.cached ? "bg-[#F0FDF4] border-[#BBF7D0]" : "bg-white border-[#E2E8F0]"}`}
              data-testid={`i18n-row-${l.code}`}
            >
              <span className="text-[10px] font-mono uppercase tracking-widest bg-white px-1.5 py-0.5 rounded border border-[#E2E8F0]">
                {l.code}
              </span>
              <span className="flex-1 text-sm font-semibold text-[#0F172A] truncate">{l.name}</span>
              {errors[l.code] ? (
                <span className="text-[10px] text-[#DC2626]" title={errors[l.code]}>Error</span>
              ) : l.cached ? (
                <CheckCircle2 size={14} className="text-[#16A34A]" />
              ) : running ? (
                <Loader2 size={12} className="animate-spin text-[#94A3B8]" />
              ) : (
                <span className="text-[10px] text-[#94A3B8]">Pending</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---- Contact Tab — public form inbox ----------------------------------------

const TOPIC_LABEL = {
  general: "General",
  premium: "Premium / billing",
  support: "Tech support",
  partnership: "Partnership",
  press: "Press",
};

// ---- Sponsor inquiries — leads from /sponsor onboarding form ---------------

/**
 * Reply modal used by both Contact and Sponsor inquiries.
 *
 * Renders the original inbound message as read-only context, lets the
 * admin compose a reply, and on send calls the appropriate `/admin/.../reply`
 * endpoint (SMTP send + status flip to "replied" happens server-side). The
 * parent passes `onSent` to refresh its list.
 */
const ReplyModal = ({ kind, message, onClose, onSent }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isSponsor = kind === "sponsor";
  const initialSubject = (() => {
    if (isSponsor) {
      const company = message.company ? ` — ${message.company}` : "";
      return `Re: HorseAppCompare sponsorship${company}`;
    }
    return `Re: ${message.subject || "your message"}`;
  })();
  const greeting = `Hi ${message.name || ""},`.trim() + (message.name ? "\n\n" : "\n");
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(greeting);

  const send = async () => {
    setBusy(true);
    setErr("");
    try {
      const fn = isSponsor ? adminReplySponsor : adminReplyContact;
      await fn(message.id, body, subject);
      if (onSent) onSent();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to send");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="reply-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-[#E2E8F0] px-6 py-4 flex items-start justify-between gap-3 sticky top-0 bg-white">
          <div>
            <h3 className="font-heading text-xl text-[#0F172A]">Reply via email</h3>
            <p className="text-xs text-[#64748B] mt-0.5">
              To: <strong className="text-[#0F172A]">{message.name || "?"}</strong> &lt;{message.email}&gt;
            </p>
          </div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A] p-1" data-testid="reply-modal-close">✕</button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Original inbound message — read-only context */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] mb-1">Original message</div>
            <div className="bg-[#F8FAFC] border-l-3 border-[#CBD5E1] border-l-2 pl-3 py-2 text-xs text-[#475569] whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto">
              {isSponsor && message.package && <div className="mb-1"><strong>Package:</strong> {message.package}</div>}
              {isSponsor && message.website && <div className="mb-1"><strong>Website:</strong> {message.website}</div>}
              {message.subject && !isSponsor && <div className="mb-1"><strong>Subject:</strong> {message.subject}</div>}
              <div>{message.message || "(no message)"}</div>
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wide text-[#64748B] block mb-1">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="hac-input w-full"
              maxLength={200}
              data-testid="reply-subject"
            />
          </div>

          {/* Body */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wide text-[#64748B] block mb-1">Your reply</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              maxLength={8000}
              placeholder="Write your reply…"
              className="hac-input w-full font-mono text-sm leading-relaxed"
              data-testid="reply-body"
            />
            <p className="text-[10px] text-[#94A3B8] mt-1">{body.length}/8000 · The original message will be appended below your reply as a quoted block.</p>
          </div>

          {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
        </div>

        <div className="border-t border-[#E2E8F0] px-6 py-3 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#64748B] hover:text-[#0F172A]" data-testid="reply-cancel">Cancel</button>
          <button
            onClick={send}
            disabled={busy || !body.trim() || !subject.trim()}
            className="hac-btn-primary inline-flex items-center gap-2 px-5 py-2"
            data-testid="reply-send"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
            {busy ? "Sending…" : "Send reply"}
          </button>
        </div>
      </div>
    </div>
  );
};


const SponsorInquiriesPanel = () => {
  const [items, setItems] = useState([]);
  const [newCount, setNewCount] = useState(0);
  const [filter, setFilter] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const [error, setError] = useState("");
  const [replyTo, setReplyTo] = useState(null);

  const refresh = () => adminListSponsors(filter || undefined)
    .then((d) => { setItems(d.items || []); setNewCount(d.new_count || 0); setError(""); })
    .catch((e) => { setItems([]); setNewCount(0); setError(e?.response?.data?.detail || e.message); });
  useEffect(() => { refresh(); }, [filter]); // eslint-disable-line

  const setStatus = async (id, status) => { await adminUpdateSponsor(id, status); refresh(); };
  const remove = async (id) => { if (!window.confirm("Delete this sponsor inquiry?")) return; await adminDeleteSponsor(id); refresh(); };

  return (
    <div className="bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-2xl p-6 mb-6" data-testid="sponsor-inquiries-panel">
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 shrink-0 bg-amber-500 text-white rounded-xl flex items-center justify-center">
            <Sparkles size={18} />
          </div>
          <div>
            <h2 className="font-bold text-xl text-[#0F172A]">
              Sponsor inquiries
              {newCount > 0 && <span className="ml-2 bg-amber-600 text-white text-xs px-2 py-0.5 rounded-full align-middle" data-testid="sponsor-new-count">{newCount} new</span>}
            </h2>
            <p className="text-xs text-[#64748B] mt-0.5">Leads from the /sponsor onboarding form. Follow up by email.</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {["new", "read", "replied", "archived", ""].map((s) => (
            <button key={s || "all"} onClick={() => setFilter(s)} className={`px-3 py-1 text-xs rounded-full border ${filter === s ? "bg-amber-500 border-amber-500 text-white" : "bg-white border-amber-200 text-amber-800"}`} data-testid={`sponsor-filter-${s || "all"}`}>{s || "all"}</button>
          ))}
          <button onClick={() => setCollapsed((v) => !v)} className="ml-1 px-2 py-1 text-xs rounded border border-amber-200 text-amber-800 bg-white" data-testid="sponsor-toggle-collapse">
            {collapsed ? "Show" : "Hide"}
          </button>
        </div>
      </div>

      {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mb-4">{error}</div>}

      {!collapsed && (
        <>
          {items.length === 0 && !error && (
            <div className="text-center text-[#94A3B8] py-8 text-sm">No sponsor inquiries{filter && ` with status "${filter}"`}.</div>
          )}
          <div className="space-y-3">
            {items.map((s) => {
              return (
                <div key={s.id} className="bg-white border border-amber-200 rounded-xl p-4" data-testid={`sponsor-row-${s.id}`}>
                  <div className="flex items-start gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-bold text-[#0F172A]">{s.company || "(no company)"}</div>
                        <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${s.status === "new" ? "bg-red-100 text-red-700" : s.status === "replied" ? "bg-emerald-100 text-emerald-700" : s.status === "archived" ? "bg-[#F1F5F9] text-[#64748B]" : "bg-blue-100 text-blue-700"}`}>{s.status || "new"}</span>
                        {s.package && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">{s.package}</span>}
                        {Array.isArray(s.replies) && s.replies.length > 0 && (
                          <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">{s.replies.length} {s.replies.length === 1 ? "reply" : "replies"}</span>
                        )}
                      </div>
                      <div className="text-xs text-[#94A3B8] mt-1">
                        <strong className="text-[#0F172A]">{s.name || "?"}</strong> &lt;<a href={`mailto:${s.email}`} className="text-[#2563EB] hover:underline">{s.email}</a>&gt;
                        {s.website && <> · <a href={s.website.startsWith("http") ? s.website : `https://${s.website}`} target="_blank" rel="noreferrer" className="text-[#2563EB] hover:underline">{s.website}</a></>}
                        &nbsp;· {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                      </div>
                      {s.message && <p className="text-sm text-[#0F172A] mt-3 whitespace-pre-wrap leading-relaxed bg-[#F8FAFC] p-3 rounded border border-[#E2E8F0]">{s.message}</p>}
                      {Array.isArray(s.replies) && s.replies.length > 0 && (
                        <details className="mt-3" open={s.status === "unread_reply"}>
                          <summary className="cursor-pointer text-xs text-[#64748B] hover:text-[#0F172A]">
                            Show {s.replies.length} {s.replies.length === 1 ? "message" : "messages"} in thread
                            {s.unread_inbound > 0 && <span className="ml-2 inline-block px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded">{s.unread_inbound} new</span>}
                          </summary>
                          <div className="space-y-2 mt-2">
                            {s.replies.map((r) => {
                              const inbound = r.direction === "inbound";
                              const ts = inbound ? r.received_at : r.sent_at;
                              return (
                                <div key={r.id} className={inbound ? "bg-blue-50/60 border-l-2 border-blue-500 pl-3 py-2 text-xs text-[#475569]" : "bg-emerald-50/50 border-l-2 border-emerald-400 pl-3 py-2 text-xs text-[#475569]"}>
                                  <div className="text-[10px] text-[#94A3B8] mb-1 inline-flex items-center gap-2">
                                    <span className={`px-1.5 py-0.5 rounded font-bold uppercase ${inbound ? "bg-blue-200 text-blue-900" : "bg-emerald-200 text-emerald-900"}`}>{inbound ? "↓ Incoming" : "↑ Sent"}</span>
                                    <span>{ts ? new Date(ts).toLocaleString() : ""}</span>
                                    {r.by && !inbound && <span>· by {r.by}</span>}
                                    {inbound && r.from && <span>· from {r.from}</span>}
                                  </div>
                                  <div className="font-semibold text-[#0F172A] mb-1">{r.subject}</div>
                                  <div className="whitespace-pre-wrap leading-relaxed">{r.body}</div>
                                </div>
                              );
                            })}
                          </div>
                        </details>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <button onClick={() => setReplyTo(s)} className="hac-btn-primary text-xs px-3 py-1.5" data-testid={`sponsor-reply-${s.id}`}>Reply</button>
                      {(s.status === "new" || !s.status) && <button onClick={() => setStatus(s.id, "read")} className="text-xs px-3 py-1.5 rounded border border-blue-300 text-blue-700 hover:bg-blue-50" data-testid={`sponsor-read-${s.id}`}>Mark read</button>}
                      {s.status !== "archived" && <button onClick={() => setStatus(s.id, "archived")} className="text-xs px-3 py-1.5 rounded border border-[#E2E8F0] text-[#64748B] hover:bg-[#F8FAFC]" data-testid={`sponsor-archive-${s.id}`}>Archive</button>}
                      <button onClick={() => remove(s.id)} className="text-xs px-3 py-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50" data-testid={`sponsor-delete-${s.id}`}>Delete</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {replyTo && (
        <ReplyModal
          kind="sponsor"
          message={replyTo}
          onClose={() => setReplyTo(null)}
          onSent={() => { setReplyTo(null); refresh(); }}
        />
      )}
    </div>
  );
};

const ContactTab = () => {
  const [items, setItems] = useState([]);
  const [newCount, setNewCount] = useState(0);
  const [filter, setFilter] = useState("");
  const [replyTo, setReplyTo] = useState(null); // open ReplyModal for this message

  const refresh = () => adminListContact(filter || undefined).then((d) => { setItems(d.items || []); setNewCount(d.new_count || 0); }).catch(() => { setItems([]); setNewCount(0); });
  useEffect(() => { refresh(); }, [filter]); // eslint-disable-line

  const setStatus = async (id, status) => { await adminUpdateContact(id, status); refresh(); };
  const remove = async (id) => { if (!window.confirm("Delete this message?")) return; await adminDeleteContact(id); refresh(); };

  return (
    <div data-testid="contact-tab">
      <SponsorInquiriesPanel />
      <div className="flex items-center justify-between mb-6 mt-10">
        <div>
          <h2 className="font-bold text-2xl text-[#0F172A]">Contact inbox{newCount > 0 && <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full align-middle" data-testid="contact-new-count">{newCount} new</span>}</h2>
          <p className="text-sm text-[#64748B] mt-1">Messages from the public /contact form.</p>
        </div>
        <div className="flex gap-1.5">
          {[ "new", "read", "replied", "archived", ""].map((s) => (
            <button key={s || "all"} onClick={() => setFilter(s)} className={`px-3 py-1.5 text-xs rounded-full border ${filter === s ? "bg-[#2563EB] border-[#2563EB] text-white" : "bg-white border-[#E2E8F0] text-[#64748B]"}`} data-testid={`contact-filter-${s || "all"}`}>{s || "all"}</button>
          ))}
        </div>
      </div>

      {items.length === 0 && <div className="text-center text-[#94A3B8] py-10">No messages {filter && `with status "${filter}"`}.</div>}

      <div className="space-y-3">
        {items.map((m) => {
          return (
            <div key={m.id} className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid={`contact-row-${m.id}`}>
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold text-[#0F172A]">{m.subject || "(no subject)"}</div>
                    <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${m.status === "new" ? "bg-red-100 text-red-700" : m.status === "replied" ? "bg-emerald-100 text-emerald-700" : m.status === "archived" ? "bg-[#F1F5F9] text-[#64748B]" : "bg-blue-100 text-blue-700"}`}>{m.status}</span>
                    <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-[#EFF6FF] text-[#2563EB] font-semibold">{TOPIC_LABEL[m.topic] || m.topic}</span>
                    {Array.isArray(m.replies) && m.replies.length > 0 && (
                      <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">{m.replies.length} {m.replies.length === 1 ? "reply" : "replies"}</span>
                    )}
                  </div>
                  <div className="text-xs text-[#94A3B8] mt-1">
                    <strong className="text-[#0F172A]">{m.name || "?"}</strong> &lt;<a href={`mailto:${m.email}`} className="text-[#2563EB] hover:underline">{m.email}</a>&gt;
                    &nbsp;· {new Date(m.submitted_at).toLocaleString()}
                  </div>
                  <p className="text-sm text-[#0F172A] mt-3 whitespace-pre-wrap leading-relaxed bg-[#F8FAFC] p-3 rounded border border-[#E2E8F0]">{m.message}</p>
                  {Array.isArray(m.replies) && m.replies.length > 0 && (
                    <details className="mt-3" open={m.status === "unread_reply"}>
                      <summary className="cursor-pointer text-xs text-[#64748B] hover:text-[#0F172A]">
                        Show {m.replies.length} {m.replies.length === 1 ? "message" : "messages"} in thread
                        {m.unread_inbound > 0 && <span className="ml-2 inline-block px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded">{m.unread_inbound} new</span>}
                      </summary>
                      <div className="space-y-2 mt-2">
                        {m.replies.map((r) => {
                          const inbound = r.direction === "inbound";
                          const ts = inbound ? r.received_at : r.sent_at;
                          return (
                            <div key={r.id} className={inbound ? "bg-blue-50/60 border-l-2 border-blue-500 pl-3 py-2 text-xs text-[#475569]" : "bg-emerald-50/50 border-l-2 border-emerald-400 pl-3 py-2 text-xs text-[#475569]"}>
                              <div className="text-[10px] text-[#94A3B8] mb-1 inline-flex items-center gap-2">
                                <span className={`px-1.5 py-0.5 rounded font-bold uppercase ${inbound ? "bg-blue-200 text-blue-900" : "bg-emerald-200 text-emerald-900"}`}>{inbound ? "↓ Incoming" : "↑ Sent"}</span>
                                <span>{ts ? new Date(ts).toLocaleString() : ""}</span>
                                {r.by && !inbound && <span>· by {r.by}</span>}
                                {inbound && r.from && <span>· from {r.from}</span>}
                              </div>
                              <div className="font-semibold text-[#0F172A] mb-1">{r.subject}</div>
                              <div className="whitespace-pre-wrap leading-relaxed">{r.body}</div>
                            </div>
                          );
                        })}
                      </div>
                    </details>
                  )}
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button onClick={() => setReplyTo(m)} className="hac-btn-primary text-xs px-3 py-1.5" data-testid={`contact-reply-${m.id}`}>Reply</button>
                  {m.status === "new" && <button onClick={() => setStatus(m.id, "read")} className="text-xs px-3 py-1.5 rounded border border-blue-300 text-blue-700 hover:bg-blue-50" data-testid={`contact-read-${m.id}`}>Mark read</button>}
                  {m.status !== "archived" && <button onClick={() => setStatus(m.id, "archived")} className="text-xs px-3 py-1.5 rounded border border-[#E2E8F0] text-[#64748B] hover:bg-[#F8FAFC]" data-testid={`contact-archive-${m.id}`}>Archive</button>}
                  <button onClick={() => remove(m.id)} className="text-xs px-3 py-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50" data-testid={`contact-delete-${m.id}`}>Delete</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {replyTo && (
        <ReplyModal
          kind="contact"
          message={replyTo}
          onClose={() => setReplyTo(null)}
          onSent={() => { setReplyTo(null); refresh(); }}
        />
      )}
    </div>
  );
};

// ---- Auctions Tab — admin management of weekly ad auctions -----------------

const AuctionsTab = () => {
  const [items, setItems] = useState([]);
  const [bidsBySlug, setBidsBySlug] = useState({});
  const [creating, setCreating] = useState(false);
  const [pushBannerFor, setPushBannerFor] = useState(null); // auction object
  const [pushForm, setPushForm] = useState({ title: "", subtitle: "", image_url: "", target_url: "", duration_days: 7 });
  const [pushUploading, setPushUploading] = useState(false);
  const [form, setForm] = useState({
    title: "Featured App of the Week",
    starts_at: new Date(Date.now() + 60_000).toISOString().slice(0, 16),
    ends_at: new Date(Date.now() + 7 * 86400_000).toISOString().slice(0, 16),
    starting_price_eur: 49,
    list_price_eur: 499,
    min_increment_eur: 25,
    placement: "homepage_banner",
    description: "",
  });

  const refresh = () => adminListAuctions().then(setItems).catch(() => setItems([]));
  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await adminCreateAuction({
        ...form,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: new Date(form.ends_at).toISOString(),
      });
      setCreating(false);
      refresh();
    } catch (err) { alert(`Failed: ${err.response?.data?.detail || err.message}`); }
  };

  const forceClose = async (id) => {
    if (!window.confirm("Force close this auction and pick winner now?")) return;
    await adminForceCloseAuction(id);
    refresh();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this auction and all its bids?")) return;
    await adminDeleteAuction(id);
    refresh();
  };
  const setInvoice = async (id, invoice_status) => {
    await adminPatchAuction(id, { invoice_status });
    refresh();
  };
  const editPrice = async (a) => {
    const cur = a.starting_price_eur ?? 49;
    const raw = window.prompt(`Set starting price (€) for "${a.title}":`, String(cur));
    if (raw === null) return;
    const v = parseFloat(raw);
    if (!Number.isFinite(v) || v < 0) { window.alert("Invalid amount"); return; }
    await adminPatchAuction(a.id, { starting_price_eur: v });
    refresh();
  };
  const loadBids = async (id) => {
    if (bidsBySlug[id]) { setBidsBySlug({ ...bidsBySlug, [id]: null }); return; }
    const bids = await adminListAuctionBids(id);
    setBidsBySlug({ ...bidsBySlug, [id]: bids });
  };

  const openPushBanner = (a) => {
    setPushBannerFor(a);
    setPushForm({
      title: a.title || "Featured App of the Week",
      subtitle: a.winner_email ? `Winner: ${a.winner_email.split("@")[0]}` : "",
      image_url: "",
      target_url: "",
      duration_days: 7,
    });
  };
  const pushBanner = async (e) => {
    e.preventDefault();
    try {
      await adminPushAuctionBanner(pushBannerFor.id, pushForm);
      setPushBannerFor(null);
      refresh();
      alert("Banner is now live on homepage ✓");
    } catch (err) { alert(`Push failed: ${err.response?.data?.detail || err.message}`); }
  };
  const onPushUpload = async (file) => {
    if (!file) return;
    setPushUploading(true);
    try {
      const { url } = await adminUploadImage(file);
      const abs = url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
      setPushForm((f) => ({ ...f, image_url: abs }));
    } catch (err) { alert(`Upload failed: ${err.response?.data?.detail || err.message}`); }
    finally { setPushUploading(false); }
  };

  const statusColor = {
    open: "bg-emerald-100 text-emerald-700",
    almost_closed: "bg-amber-100 text-amber-700",
    scheduled: "bg-blue-100 text-blue-700",
    closed: "bg-[#F1F5F9] text-[#64748B]",
    won: "bg-purple-100 text-purple-700",
  };

  return (
    <div data-testid="auctions-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-bold text-2xl text-[#0F172A]">Weekly Ad Auctions</h2>
          <p className="text-sm text-[#64748B] mt-1">Each Monday 00:00 UTC a new auction auto-starts. You can also create one manually below.</p>
        </div>
        <button onClick={() => setCreating(!creating)} className="hac-btn-primary" data-testid="auctions-new">
          <Plus size={14} /> {creating ? "Cancel" : "New auction"}
        </button>
      </div>

      {creating && (
        <form onSubmit={create} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 mb-6 grid sm:grid-cols-2 gap-4" data-testid="auction-create-form">
          <div className="sm:col-span-2"><label className="overline block mb-1 text-[#64748B]">Title</label><input className="hac-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
          <div><label className="overline block mb-1 text-[#64748B]">Starts at (UTC)</label><input type="datetime-local" className="hac-input" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} required /></div>
          <div><label className="overline block mb-1 text-[#64748B]">Ends at (UTC)</label><input type="datetime-local" className="hac-input" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} required /></div>
          <div><label className="overline block mb-1 text-[#64748B]">Starting price (€) — minimum opening bid</label><input type="number" step="1" className="hac-input" value={form.starting_price_eur} onChange={(e) => setForm({ ...form, starting_price_eur: parseFloat(e.target.value || "0") })} required /></div>
          <div><label className="overline block mb-1 text-[#64748B]">List price (€) — displayed "richtprijs"</label><input type="number" step="1" className="hac-input" value={form.list_price_eur} onChange={(e) => setForm({ ...form, list_price_eur: parseFloat(e.target.value || "0") })} required /></div>
          <div><label className="overline block mb-1 text-[#64748B]">Min increment (€)</label><input type="number" step="1" className="hac-input" value={form.min_increment_eur} onChange={(e) => setForm({ ...form, min_increment_eur: parseFloat(e.target.value || "0") })} required /></div>
          <div className="sm:col-span-2"><label className="overline block mb-1 text-[#64748B]">Description</label><textarea className="hac-input min-h-[60px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="sm:col-span-2 flex gap-2"><button type="submit" className="hac-btn-primary"><Check size={14} /> Create</button><button type="button" onClick={() => setCreating(false)} className="hac-btn-secondary">Cancel</button></div>
        </form>
      )}

      {pushBannerFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setPushBannerFor(null)} data-testid="push-banner-modal">
          <form onSubmit={pushBanner} className="bg-white rounded-2xl max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-1">
              <h3 className="font-bold text-xl text-[#0F172A] inline-flex items-center gap-2"><Rocket size={18} className="text-amber-500" /> Push winner to homepage</h3>
              <button type="button" onClick={() => setPushBannerFor(null)} className="text-[#64748B] hover:text-[#0F172A]"><X size={18} /></button>
            </div>
            <p className="text-xs text-[#64748B] mb-5">Winner: <strong>{pushBannerFor.winner_email}</strong> · €{pushBannerFor.winner_amount_eur?.toFixed(0)} · {pushBannerFor.title}</p>
            <div className="space-y-3">
              <div><label className="overline block mb-1 text-[#64748B]">Headline</label><input required className="hac-input" value={pushForm.title} onChange={(e) => setPushForm({ ...pushForm, title: e.target.value })} data-testid="push-title" /></div>
              <div><label className="overline block mb-1 text-[#64748B]">Subtitle</label><input className="hac-input" value={pushForm.subtitle} onChange={(e) => setPushForm({ ...pushForm, subtitle: e.target.value })} data-testid="push-subtitle" /></div>
              <div>
                <label className="overline block mb-1 text-[#64748B]">Banner image (1200×400 recommended)</label>
                {pushForm.image_url && <img src={pushForm.image_url} alt="" className="w-full max-h-32 object-cover rounded mb-2 border border-[#E2E8F0]" />}
                <label className="hac-input flex items-center justify-center gap-2 cursor-pointer text-[#64748B] hover:text-[#2563EB]">
                  <Upload size={14} /> {pushUploading ? "Uploading…" : (pushForm.image_url ? "Replace image" : "Upload from winner's email attachment")}
                  <input type="file" accept="image/*" className="hidden" disabled={pushUploading} onChange={(e) => onPushUpload(e.target.files?.[0])} data-testid="push-upload" />
                </label>
                <input className="hac-input mt-2" placeholder="or paste image URL" value={pushForm.image_url} onChange={(e) => setPushForm({ ...pushForm, image_url: e.target.value })} data-testid="push-image-url" />
              </div>
              <div><label className="overline block mb-1 text-[#64748B]">Click-through URL</label><input required type="url" className="hac-input" placeholder="https://winner-app.com" value={pushForm.target_url} onChange={(e) => setPushForm({ ...pushForm, target_url: e.target.value })} data-testid="push-target-url" /></div>
              <div><label className="overline block mb-1 text-[#64748B]">Duration (days)</label><input type="number" min="1" max="60" className="hac-input" value={pushForm.duration_days} onChange={(e) => setPushForm({ ...pushForm, duration_days: parseInt(e.target.value || "7") })} data-testid="push-duration" /></div>
            </div>
            <div className="mt-5 p-3 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-900">
              Other active homepage banners will be deactivated automatically. The new banner expires after {pushForm.duration_days} days.
            </div>
            <div className="flex gap-2 mt-5 pt-4 border-t border-[#E2E8F0]">
              <button type="submit" className="hac-btn-primary bg-amber-500 hover:bg-amber-600 border-amber-500 disabled:opacity-60" disabled={pushUploading || !pushForm.image_url || !pushForm.target_url} data-testid="push-submit"><Rocket size={14} /> Publish to homepage</button>
              <button type="button" onClick={() => setPushBannerFor(null)} className="hac-btn-secondary">Cancel</button>
            </div>
          </form>
        </div>
      )}


      <div className="space-y-3">
        {items.length === 0 && <div className="text-center text-[#94A3B8] py-10">No auctions yet.</div>}
        {items.map((a) => (
          <div key={a.id} className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid={`auction-row-${a.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-bold text-[#0F172A]">{a.title}</span>
                  <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${statusColor[a.status] || "bg-[#F1F5F9] text-[#64748B]"}`}>{a.status}</span>
                  {a.auto_created && <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">auto</span>}
                  {a.invoice_status && <span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded ${a.invoice_status === "paid" ? "bg-emerald-100 text-emerald-700" : a.invoice_status === "sent" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"}`}>invoice: {a.invoice_status}</span>}
                </div>
                <div className="text-xs text-[#64748B] font-mono">
                  {new Date(a.starts_at).toLocaleString()} → {new Date(a.ends_at).toLocaleString()}
                </div>
                <div className="text-sm mt-2 flex gap-4 flex-wrap">
                  <span>Top bid: <strong className="text-amber-700">{a.current_highest_bid_eur != null ? `€${a.current_highest_bid_eur.toFixed(0)}` : "—"}</strong></span>
                  <span className="text-[#64748B]">Bids: {a.bid_count}</span>
                  <span className="text-[#64748B]">Min next: €{a.min_next_bid_eur.toFixed(0)}</span>
                  <span className="text-[#64748B]">Increment: €{a.min_increment_eur.toFixed(0)}</span>
                </div>
                {a.winner_email && (
                  <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800">
                    <Trash2 className="hidden" /> Winner: <strong>{a.winner_email}</strong> · €{a.winner_amount_eur?.toFixed(0)}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <button onClick={() => loadBids(a.id)} className="text-xs px-3 py-1.5 rounded border border-[#E2E8F0] text-[#64748B] hover:bg-[#F8FAFC]" data-testid={`auction-bids-${a.id}`}>{bidsBySlug[a.id] ? "Hide bids" : "View bids"}</button>
                {(a.status === "open" || a.status === "almost_closed" || a.status === "scheduled") && (
                  <button onClick={() => editPrice(a)} className="text-xs px-3 py-1.5 rounded border border-[#E2E8F0] text-[#64748B] hover:bg-[#F8FAFC]" data-testid={`auction-edit-price-${a.id}`}>Edit start €</button>
                )}
                {(a.status === "open" || a.status === "almost_closed" || a.status === "scheduled") && (
                  <button onClick={() => forceClose(a.id)} className="text-xs px-3 py-1.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`auction-close-${a.id}`}>Force close</button>
                )}
                {a.status === "won" && a.invoice_status !== "paid" && (
                  <>
                    {a.invoice_status !== "sent" && <button onClick={() => setInvoice(a.id, "sent")} className="text-xs px-3 py-1.5 rounded border border-blue-300 text-blue-700 hover:bg-blue-50" data-testid={`auction-invoice-sent-${a.id}`}>Mark invoice sent</button>}
                    <button onClick={() => setInvoice(a.id, "paid")} className="text-xs px-3 py-1.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-semibold" data-testid={`auction-invoice-paid-${a.id}`}>Mark paid ✓</button>
                  </>
                )}
                {a.status === "won" && a.invoice_status === "paid" && !a.banner_published && (
                  <button onClick={() => openPushBanner(a)} className="text-xs px-3 py-1.5 rounded bg-amber-500 text-white hover:bg-amber-600 font-semibold inline-flex items-center gap-1" data-testid={`auction-push-banner-${a.id}`}>
                    <Rocket size={12} /> Push to homepage
                  </button>
                )}
                {a.banner_published && (
                  <span className="text-[10px] uppercase font-mono px-2 py-1 rounded bg-emerald-100 text-emerald-700 text-center" data-testid={`auction-banner-live-${a.id}`}>
                    Banner live{a.banner_ends_at && ` · expires ${new Date(a.banner_ends_at).toLocaleDateString()}`}
                  </span>
                )}
                <button onClick={() => remove(a.id)} className="text-xs px-3 py-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50" data-testid={`auction-delete-${a.id}`}>Delete</button>
              </div>
            </div>
            {bidsBySlug[a.id] && (
              <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
                <div className="text-[10px] uppercase tracking-widest text-[#64748B] mb-2">Bid log ({bidsBySlug[a.id].length})</div>
                <table className="w-full text-xs">
                  <thead className="text-[#64748B]"><tr><th className="text-left">Bidder</th><th className="text-left">Company</th><th className="text-right">Amount</th><th className="text-right">Placed</th></tr></thead>
                  <tbody>
                    {bidsBySlug[a.id].map((b, i) => (
                      <tr key={i} className="border-t border-[#F1F5F9]"><td className="py-1.5">{b.bidder_email}</td><td className="py-1.5 text-[#64748B]">{b.bidder_company || "—"}</td><td className={`py-1.5 text-right font-bold tabular-nums ${i === 0 ? "text-amber-700" : "text-[#0F172A]"}`}>€{b.amount_eur.toFixed(0)}</td><td className="py-1.5 text-right text-[#94A3B8]">{new Date(b.placed_at).toLocaleString()}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminPage;

// ---- Apps Tab — full CRUD for the apps catalogue ---------------------------

const REGIONS = ["Worldwide", "Europe", "USA", "Australia", "Asia", "Africa", "South America"];
const AUDIENCES = ["owners", "riders", "trainers", "breeders", "stable_managers", "vets", "farriers"];

const EMPTY_APP = {
  slug: "", name: "", tagline: "",
  description: "",
  logo: "", website: "", founded: 2026, country: "",
  languages: ["EN"],
  platforms: { ios: false, android: false, web: false },
  pricing: { free_tier: false, premium: false, monthly_usd: 0, yearly_usd: 0, enterprise: null },
  plans: [],
  regions: ["Worldwide"], target_audience: [], rating: 0, review_count: 0,
  features: [], color: "#2563EB",
};

const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 64);

// ---- Admin app logo — handles broken URLs with onError fallback so the
// directory list never shows the awful "Anolla.." alt-text crumb when an
// uploaded logo URL 404s.

const AdminAppLogo = ({ app, isInactive }) => {
  const [broken, setBroken] = useState(false);
  const initial = (app.name || "?").trim().charAt(0).toUpperCase();
  if (!app.logo || broken) {
    return (
      <div
        className={`w-12 h-12 shrink-0 rounded-lg flex items-center justify-center text-white font-bold text-lg ${isInactive ? "grayscale opacity-70" : ""}`}
        style={{ backgroundColor: app.color || "#0F172A" }}
        title={app.name}
        data-testid={`apps-logo-${app.slug}`}
      >
        {initial}
      </div>
    );
  }
  return (
    <div className={`w-12 h-12 shrink-0 rounded-lg overflow-hidden bg-[#F1F5F9] ${isInactive ? "grayscale opacity-70" : ""}`}>
      <img
        src={app.logo}
        alt=""
        className="w-full h-full object-cover"
        loading="lazy"
        onError={() => setBroken(true)}
        data-testid={`apps-logo-${app.slug}`}
      />
    </div>
  );
};

const AppsTab = () => {
  const [apps, setApps] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | "<slug>"
  const [form, setForm] = useState(EMPTY_APP);
  const [taxonomy, setTaxonomy] = useState({}); // {category: [feature_id, ...]}
  const [busy, setBusy] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [outreachSlug, setOutreachSlug] = useState(null);
  const [pitchSlug, setPitchSlug] = useState(null);
  const [visibility, setVisibility] = useState({}); // {slug: {total_combinations, base_pages, breakdown}}
  const [sortBy, setSortBy] = useState("name"); // 'name' | 'reach' | 'score'
  const [minReach, setMinReach] = useState(0);
  // {slug: {sent_at, recipient, status, error}} — loaded once on mount, reloaded after each send
  const [outreachStatus, setOutreachStatus] = useState({});

  const refresh = () => adminListAllApps().then(setApps).catch(() => setApps([]));
  const refreshVisibility = () =>
    adminAppsVisibility()
      .then((d) => setVisibility(d.visibility || {}))
      .catch(() => setVisibility({}));
  const refreshOutreachStatus = () =>
    adminOutreachStatus()
      .then((d) => setOutreachStatus(d.items || {}))
      .catch(() => setOutreachStatus({}));

  useEffect(() => {
    refresh();
    refreshVisibility();
    refreshOutreachStatus();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meta/features`)
      .then((r) => r.json()).then((d) => setTaxonomy(d.categories || d)).catch(() => setTaxonomy({}));
  }, []);

  const startNew = () => { setForm({ ...EMPTY_APP }); setEditing("new"); };
  const startEdit = (a) => {
    setForm({
      ...EMPTY_APP, ...a,
      platforms: { ...EMPTY_APP.platforms, ...(a.platforms || {}) },
      pricing: { ...EMPTY_APP.pricing, ...(a.pricing || {}) },
      plans: Array.isArray(a.plans) ? a.plans : [],
      regions: a.regions || [], target_audience: a.target_audience || [], features: a.features || [],
    });
    setEditing(a.slug);
  };
  const cancel = () => { setEditing(null); setForm(EMPTY_APP); };

  const toggleArr = (key, val) =>
    setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));

  const onLogoUpload = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const { url } = await adminUploadImage(file);
      const abs = url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
      setForm((f) => ({ ...f, logo: abs }));
    } catch (err) { alert(`Upload failed: ${err.response?.data?.detail || err.message}`); }
    finally { setUploadingLogo(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, slug: form.slug || slugify(form.name) };
    if (!payload.name || !payload.slug) { alert("Name + slug are required"); setBusy(false); return; }
    try {
      if (editing === "new") await adminCreateApp(payload);
      else await adminUpdateApp(editing, payload);
      cancel();
      refresh();
    } catch (err) { alert(`Save failed: ${err.response?.data?.detail || err.message}`); }
    finally { setBusy(false); }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete app '${slug}'? This also removes its screenshots and boosts.`)) return;
    await adminDeleteApp(slug);
    refresh();
  };

  const togglePremium = async (a) => {
    const next = !a.premium;
    let tier = a.premium_tier || "monthly";
    if (next) {
      const t = window.prompt("Premium tier: type 'monthly' or 'yearly'", tier);
      if (!t) return;
      tier = t === "yearly" ? "yearly" : "monthly";
    }
    try {
      await adminTogglePremium(a.slug, next, tier);
      refresh();
    } catch (err) { alert(`Failed: ${err.response?.data?.detail || err.message}`); }
  };

  const toggleActive = async (a) => {
    const next = !(a.active === false ? false : true);
    let reason = null;
    if (!next) {
      reason = window.prompt(`Reason for deactivating '${a.name}' (optional — e.g. "unpaid invoice"):`, "") || null;
    } else if (!window.confirm(`Re-activate '${a.name}'? It will reappear in the directory, rankings and sitemap immediately.`)) {
      return;
    }
    try {
      await adminToggleActive(a.slug, next, reason);
      refresh();
    } catch (err) { alert(`Failed: ${err.response?.data?.detail || err.message}`); }
  };

  const [seoBoostBusy, setSeoBoostBusy] = useState(null);
  const [contentQualityOpen, setContentQualityOpen] = useState(false);
  const seoBoost = async (a) => {
    const base = "https://horseappcompare.com";
    const urls = [
      `${base}/apps/${a.slug}`,
      `${base}/apps`,
      `${base}/rankings`,
      `${base}/categories`,
    ];
    setSeoBoostBusy(a.slug);
    try {
      const res = await adminIndexNowPing(urls);
      alert(`✓ SEO Boost sent\n\nPinged IndexNow with ${res.count} URLs for '${a.name}'.\n\nBing & Yandex will index within minutes. Google reads these signals indirectly.`);
    } catch (err) {
      alert(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSeoBoostBusy(null);
    }
  };

  const notifyPublished = async (a) => {
    const already = a.published_notified_at
      ? `Already notified on ${new Date(a.published_notified_at).toLocaleDateString()} (${a.published_notified_email}). Send again?`
      : `Send a "Your app is now live" email to the registered owner/submitter of '${a.name}'?`;
    if (!window.confirm(already)) return;
    try {
      const r = await adminNotifyAppPublished(a.slug);
      alert(`✅ Sent to ${r.sent_to}`);
      refresh();
    } catch (err) {
      alert(`Failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const inviteCompleteProfile = async (a) => {
    if (!a.verified_owner_email) {
      alert("This app isn't claimed yet. Use the outreach mail icon to invite the owner to claim first.");
      return;
    }
    const already = a.complete_profile_invite_sent_at
      ? `Already invited on ${new Date(a.complete_profile_invite_sent_at).toLocaleDateString()}.\n\nSend another invitation to ${a.verified_owner_email}?\n\n(Backend will reject if last invite was <24h ago.)`
      : `Send a "Complete your profile" invitation to ${a.verified_owner_email}?\n\nThe email includes a one-time magic-link login + checklist of what's still missing on ${a.name}.`;
    if (!window.confirm(already)) return;
    try {
      const r = await adminInviteCompleteProfile(a.slug);
      alert(`✅ Sent to ${r.sent_to}${r.smtp_ready ? "" : "\n\n(SMTP not configured in this env — invite logged but email skipped.)"}`);
      refresh();
    } catch (err) {
      alert(`Failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  if (editing) {
    return (
      <form onSubmit={submit} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 max-w-4xl" data-testid="apps-form">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-bold text-2xl text-[#0F172A]">{editing === "new" ? "Create new app" : `Edit ${form.name || editing}`}</h2>
          <button type="button" onClick={cancel} className="text-sm text-[#64748B] hover:text-[#0F172A] inline-flex items-center gap-1" data-testid="apps-form-cancel"><X size={14} /> Cancel</button>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="overline block mb-1 text-[#64748B]">Name *</label>
            <input className="hac-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: editing === "new" ? slugify(e.target.value) : form.slug })} required data-testid="apps-form-name" />
          </div>
          <div>
            <label className="overline block mb-1 text-[#64748B]">Slug</label>
            <input className="hac-input" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} disabled={editing !== "new"} placeholder="auto-from-name" data-testid="apps-form-slug" />
          </div>
          <div className="md:col-span-2">
            <label className="overline block mb-1 text-[#64748B]">Tagline</label>
            <input className="hac-input" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="One-line pitch (English)" data-testid="apps-form-tagline" />
            <p className="text-[11px] text-[#94A3B8] mt-1">English only — auto-translated to 22 languages by the i18n pipeline.</p>
          </div>
          <div className="md:col-span-2">
            <label className="overline block mb-1 text-[#64748B]">Description</label>
            <MarkdownEditor value={form.description} onChange={(v) => setForm({ ...form, description: v })} maxLength={1500} placeholder="Use **bold**, *italic*, lists and links for a richer page (English)." testId="apps-form-description" />
          </div>

          <div className="md:col-span-2">
            <label className="overline block mb-1 text-[#64748B]">Logo</label>
            {form.logo && <img src={form.logo} alt="logo" className="w-16 h-16 rounded-lg border border-[#E2E8F0] object-cover mb-2" />}
            <div className="flex items-center gap-2">
              <label className="hac-input flex-1 flex items-center justify-center gap-2 cursor-pointer text-[#64748B] hover:text-[#2563EB]">
                <Upload size={14} /> {uploadingLogo ? "Uploading…" : "Upload logo"}
                <input type="file" accept="image/*" className="hidden" disabled={uploadingLogo} onChange={(e) => onLogoUpload(e.target.files?.[0])} data-testid="apps-form-logo-upload" />
              </label>
              <input className="hac-input flex-1" placeholder="or paste URL" value={form.logo} onChange={(e) => setForm({ ...form, logo: e.target.value })} data-testid="apps-form-logo-url" />
            </div>
          </div>

          <div>
            <label className="overline block mb-1 text-[#64748B]">Website</label>
            <input className="hac-input" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" data-testid="apps-form-website" />
          </div>
          <div>
            <label className="overline block mb-1 text-[#64748B]">Country</label>
            <input className="hac-input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="apps-form-country" />
          </div>
          <div>
            <label className="overline block mb-1 text-[#64748B]">Founded</label>
            <input type="number" className="hac-input" value={form.founded} onChange={(e) => setForm({ ...form, founded: parseInt(e.target.value || "0") })} data-testid="apps-form-founded" />
          </div>
          <div>
            <label className="overline block mb-1 text-[#64748B]">Accent colour</label>
            <input type="color" className="hac-input h-11" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="apps-form-color" />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="overline block mb-2 text-[#64748B]">Platforms</label>
            {["ios", "android", "web"].map((p) => (
              <label key={p} className="flex items-center gap-2 text-sm mb-1.5 cursor-pointer">
                <input type="checkbox" checked={form.platforms[p]} onChange={(e) => setForm({ ...form, platforms: { ...form.platforms, [p]: e.target.checked } })} data-testid={`apps-form-platform-${p}`} /> {p}
              </label>
            ))}
          </div>
          <div>
            <label className="overline block mb-2 text-[#64748B]">Listing flags</label>
            <label className="flex items-center gap-2 text-sm mb-1.5"><input type="checkbox" checked={form.pricing.free_tier} onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, free_tier: e.target.checked } })} data-testid="apps-form-free" /> Show {`"Free tier"`} badge</label>
            <label className="flex items-center gap-2 text-sm mb-1.5"><input type="checkbox" checked={form.pricing.premium} onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, premium: e.target.checked } })} data-testid="apps-form-premium" /> Show {`"Premium"`} badge</label>
            <div className="text-[10px] text-[#94A3B8] italic mt-1 leading-snug">
              Detailed tiers go in the {`"Pricing tiers"`} section below — these flags only control the badge labels in the directory.
            </div>
          </div>
          <div>
            <label className="overline block mb-2 text-[#64748B]">Ratings</label>
            <input type="number" step="0.1" placeholder="Rating 0-5" className="hac-input mb-2" value={form.rating} onChange={(e) => setForm({ ...form, rating: parseFloat(e.target.value || "0") })} data-testid="apps-form-rating" />
            <input type="number" placeholder="Review count" className="hac-input" value={form.review_count} onChange={(e) => setForm({ ...form, review_count: parseInt(e.target.value || "0") })} data-testid="apps-form-reviews" />
          </div>
        </div>

        <div className="mb-6">
          <label className="overline block mb-2 text-[#64748B]">Regions</label>
          <div className="flex flex-wrap gap-2">
            {REGIONS.map((r) => (
              <button key={r} type="button" onClick={() => toggleArr("regions", r)} className={`px-3 py-1.5 text-xs rounded-full border ${form.regions.includes(r) ? "bg-[#2563EB] border-[#2563EB] text-white" : "bg-white border-[#E2E8F0] text-[#64748B]"}`} data-testid={`apps-form-region-${r}`}>{r}</button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <label className="overline block mb-2 text-[#64748B]">Target audiences</label>
          <div className="flex flex-wrap gap-2">
            {AUDIENCES.map((a) => (
              <button key={a} type="button" onClick={() => toggleArr("target_audience", a)} className={`px-3 py-1.5 text-xs rounded-full border ${form.target_audience.includes(a) ? "bg-[#2563EB] border-[#2563EB] text-white" : "bg-white border-[#E2E8F0] text-[#64748B]"}`} data-testid={`apps-form-aud-${a}`}>{a}</button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-baseline justify-between mb-2">
            <label className="overline text-[#64748B]">Pricing tiers</label>
            <span className="text-[10px] text-[#94A3B8] italic">
              Free / Flat / Per-unit · per horse, user, stall, rider, ride or custom
            </span>
          </div>
          <PlansEditor value={form.plans || []} onChange={(plans) => setForm({ ...form, plans })} isNl={false} />
        </div>
        <div className="mb-6">
          <label className="overline block mb-3 text-[#64748B]">Features ({form.features.length} selected)</label>
          <div className="space-y-3">
            {Object.entries(taxonomy).map(([cat, feats]) => (
              <div key={cat} className="border border-[#E2E8F0] rounded-xl p-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-[#2563EB] mb-2">{cat}</div>
                <div className="flex flex-wrap gap-1.5">
                  {feats.map((f) => (
                    <button key={f} type="button" onClick={() => toggleArr("features", f)} className={`px-2.5 py-1 text-[11px] rounded-full border ${form.features.includes(f) ? "bg-[#EFF6FF] border-[#2563EB] text-[#2563EB]" : "bg-white border-[#E2E8F0] text-[#64748B]"}`} data-testid={`apps-form-feat-${f}`}>{f}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3 pt-4 border-t border-[#E2E8F0]">
          <button type="submit" disabled={busy} className="hac-btn-primary" data-testid="apps-form-submit">
            <Check size={14} /> {busy ? "Saving…" : (editing === "new" ? "Create app" : "Save changes")}
          </button>
          <button type="button" onClick={cancel} className="hac-btn-secondary" data-testid="apps-form-cancel-btn">Cancel</button>
        </div>
      </form>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-bold text-2xl text-[#0F172A]">{apps.length} apps in directory</h2>
          <p className="text-sm text-[#64748B] mt-1">Add, edit or remove apps. Changes appear instantly in directory, rankings, comparisons and sitemap (all 22 languages).</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setContentQualityOpen(true)} className="bg-white border border-[#E2E8F0] hover:border-[#2563EB] text-[#0F172A] px-4 py-2 rounded-full text-sm inline-flex items-center gap-1.5" data-testid="apps-content-quality-btn"><FileText size={14} /> Content quality</button>
          <button onClick={startNew} className="hac-btn-primary" data-testid="apps-new"><Plus size={14} /> New app</button>
        </div>
      </div>
      {contentQualityOpen && <ContentQualityModal onClose={() => setContentQualityOpen(false)} onEdit={(slug) => { setContentQualityOpen(false); const app = apps.find((a) => a.slug === slug); if (app) startEdit(app); }} />}

      <DiscoverAppsPanel onComplete={refresh} />
      <OutreachBatchPanel />
      <OutreachFollowupsPanel onActioned={refreshOutreachStatus} />

      {/* ===== Sort + filter controls ===== */}
      <div className="flex flex-wrap items-center gap-3 mb-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-4 py-3" data-testid="apps-controls">
        <span className="text-xs font-medium text-[#0F172A]">Sort:</span>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          data-testid="apps-sort"
          className="text-xs border border-[#E2E8F0] rounded-lg px-2 py-1 bg-white"
        >
          <option value="name">A → Z (name)</option>
          <option value="reach">Reach ↓ (visibility)</option>
          <option value="score">Score ↓ (ranking)</option>
          <option value="status">Status (inactive first)</option>
        </select>
        <span className="text-xs font-medium text-[#0F172A] ml-2">Min reach:</span>
        <input
          type="number"
          min={0}
          step={50}
          value={minReach}
          onChange={(e) => setMinReach(Math.max(0, parseInt(e.target.value) || 0))}
          data-testid="apps-min-reach"
          className="text-xs border border-[#E2E8F0] rounded-lg px-2 py-1 bg-white w-20"
        />
        <span className="text-[10px] text-[#94A3B8]">URLs</span>
        <div className="flex-1" />
        <span className="text-xs text-[#64748B]" data-testid="apps-filter-count">
          {apps.filter((a) => (visibility[a.slug]?.total_combinations || 0) >= minReach).length} matching
        </span>
      </div>

      <div className="space-y-2" data-testid="apps-list">
        {apps.filter((a) => (visibility[a.slug]?.total_combinations || 0) >= minReach).length === 0 && minReach > 0 && (
          <div className="text-center text-sm text-[#64748B] py-12 border border-dashed border-[#CBD5E1] rounded-xl" data-testid="apps-empty-filter">
            No apps match this filter. Lower the <strong>Min reach</strong> threshold to show more.
          </div>
        )}
        {apps
          .filter((a) => (visibility[a.slug]?.total_combinations || 0) >= minReach)
          .sort((a, b) => {
            if (sortBy === "reach") return (visibility[b.slug]?.total_combinations || 0) - (visibility[a.slug]?.total_combinations || 0);
            if (sortBy === "score") return (b.score || 0) - (a.score || 0);
            if (sortBy === "status") return (a.active === false ? 0 : 1) - (b.active === false ? 0 : 1);
            return (a.name || "").localeCompare(b.name || "");
          })
          .map((a) => {
          const isInactive = a.active === false;
          const vis = visibility[a.slug];
          return (
          <div key={a.slug} className={`group border rounded-xl px-4 py-3 flex items-center gap-4 transition-colors ${isInactive ? "bg-[#F8FAFC] border-dashed border-[#CBD5E1] opacity-80" : "bg-white border-[#E2E8F0] hover:border-[#94A3B8] hover:shadow-sm"}`} data-testid={`apps-row-${a.slug}`}>
            {/* Logo with onError fallback so broken URLs don't show the alt-text crumb */}
            <AdminAppLogo app={a} isInactive={isInactive} />

            {/* Identity column — name + slug/category/country meta */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-bold text-[#0F172A] text-[15px] truncate max-w-[260px]" title={a.name}>{a.name}</div>
                {a.country && <span className="text-xs text-[#64748B]">· {a.country}</span>}
              </div>
              <div className="text-[11px] text-[#94A3B8] font-mono truncate mt-0.5">
                {a.slug}
                {typeof a.score === "number" && ` · score ${a.score.toFixed ? a.score.toFixed(1) : a.score}`}
                {(a.categories || []).slice(0, 2).length > 0 && ` · ${(a.categories || []).slice(0, 2).join(", ")}`}
              </div>
            </div>

            {/* Status badges column — fixed-ish width, right-aligned cluster */}
            <div className="hidden lg:flex items-center gap-1 shrink-0">
              {isInactive ? (
                <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-red-100 text-red-700" data-testid={`apps-inactive-badge-${a.slug}`} title={a.deactivation_reason ? `Disabled — ${a.deactivation_reason}` : "Disabled"}>Disabled</span>
              ) : (
                <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Live</span>
              )}
              {a.premium && (
                <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 inline-flex items-center gap-1" title={`Premium ${a.premium_tier || "monthly"}`}>
                  <Star size={9} className="fill-current" /> Premium
                </span>
              )}
              {a.verified_owner_email && (
                <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 inline-flex items-center gap-1" title={`Claimed by ${a.verified_owner_email}`}>
                  <CheckCircle2 size={9} /> Claimed
                </span>
              )}
              {vis && vis.total_combinations > 0 && (
                <button
                  type="button"
                  onClick={() => setPitchSlug(a.slug)}
                  title={`${vis.total_combinations.toLocaleString("en-US")} URLs · click to pitch`}
                  data-testid={`apps-reach-pill-${a.slug}`}
                  className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-gradient-to-r from-amber-100 to-amber-200 text-amber-900 border border-amber-300 px-2 py-0.5 rounded-full hover:from-amber-200 hover:to-amber-300 transition-colors"
                >
                  <Rocket size={9} /> {vis.total_combinations.toLocaleString("en-US")}
                </button>
              )}
            </div>

            {/* Action cluster 1: outreach / publisher comms */}
            <div className="flex items-center gap-0.5 shrink-0 border-l border-[#E2E8F0] pl-2 ml-1">
              {(() => {
                const ostat = outreachStatus[a.slug];
                const sent = !!(ostat && ostat.status === "sent");
                const failed = !!(ostat && ostat.status && ostat.status !== "sent");
                const sentAtFmt = sent && ostat.sent_at
                  ? new Date(ostat.sent_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                  : "";
                const claimed = !!a.verified_owner_email;
                const attempts = ostat?.attempts || (sent ? 1 : 0);
                const maxAttempts = ostat?.max_attempts || 3;
                const eligibleNow = !!(ostat && ostat.eligible_now);
                const exhausted = !!(ostat && ostat.exhausted);
                const nextFmt = ostat?.next_eligible_at
                  ? new Date(ostat.next_eligible_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
                  : "";
                const title = sent
                  ? `Sent ${attempts}/${maxAttempts} · last ${sentAtFmt} to ${ostat.recipient}${claimed ? " · already claimed" : eligibleNow ? " · follow-up eligible now" : exhausted ? " · max attempts reached" : ` · next eligible ${nextFmt}`}`
                  : failed
                  ? `Last send failed: ${ostat.error || ostat.status}`
                  : "Find email & send claim invitation";
                const cls = sent
                  ? (eligibleNow
                      ? "p-1.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 relative"
                      : "p-1.5 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 relative")
                  : failed
                  ? "p-1.5 rounded bg-red-50 text-red-600 hover:bg-red-100 relative"
                  : "p-1.5 rounded hover:bg-orange-50 text-[#64748B] hover:text-[#EA580C]";
                return (
                  <button
                    onClick={() => setOutreachSlug(a.slug)}
                    className={cls}
                    title={title}
                    data-testid={`apps-outreach-${a.slug}`}
                  >
                    <Mail size={14} />
                    {sent && attempts > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-1 bg-[#0F172A] text-white text-[9px] font-bold rounded-full ring-2 ring-white flex items-center justify-center font-mono" data-testid={`apps-outreach-attempts-${a.slug}`}>
                        {attempts}
                      </span>
                    )}
                    {!sent && failed && (
                      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" aria-hidden />
                    )}
                  </button>
                );
              })()}
              <button
                onClick={async () => {
                  const current = a.verified_owner_email || "";
                  const email = window.prompt(`Set verified owner email for ${a.name}. Empty to un-verify.`, current);
                  if (email === null) return;
                  const cleaned = email.trim().toLowerCase();
                  try { await adminVerifyOwner(a.slug, cleaned || null); refresh(); }
                  catch (e) { alert(e?.response?.data?.detail || e.message || "Update failed"); }
                }}
                className={`p-1.5 rounded hover:bg-emerald-50 ${a.verified_owner_email ? "text-emerald-600" : "text-[#64748B] hover:text-emerald-600"}`}
                title={a.verified_owner_email ? `Claimed by ${a.verified_owner_email}` : "Mark as claimed"}
                data-testid={`apps-verify-owner-${a.slug}`}
              ><CheckCircle2 size={14} /></button>
              <button
                onClick={() => notifyPublished(a)}
                className={`p-1.5 rounded hover:bg-blue-50 ${a.published_notified_at ? "text-emerald-600" : "text-[#64748B] hover:text-blue-600"}`}
                title={a.published_notified_at ? `Already notified ${a.published_notified_email}` : 'Email owner: "Your app is now live"'}
                data-testid={`apps-notify-published-${a.slug}`}
              ><Send size={14} /></button>
              {a.verified_owner_email && (
                <button
                  onClick={() => inviteCompleteProfile(a)}
                  className={`p-1.5 rounded hover:bg-violet-50 ${a.complete_profile_invite_sent_at ? "text-violet-600" : "text-[#64748B] hover:text-violet-600"}`}
                  title={a.complete_profile_invite_sent_at
                    ? `Invited ${new Date(a.complete_profile_invite_sent_at).toLocaleDateString()} — send again`
                    : 'Email owner: "Complete your profile" (magic-link + checklist)'}
                  data-testid={`apps-invite-complete-${a.slug}`}
                ><Sparkles size={14} /></button>
              )}
            </div>

            {/* Action cluster 2: visibility / promo */}
            <div className="flex items-center gap-0.5 shrink-0 border-l border-[#E2E8F0] pl-2">
              <button onClick={() => toggleActive(a)} className={`p-1.5 rounded ${isInactive ? "text-red-500 hover:bg-emerald-50 hover:text-emerald-500" : "text-emerald-500 hover:bg-red-50 hover:text-red-500"}`} title={isInactive ? "Re-activate" : "Disable"} data-testid={`apps-active-${a.slug}`}><Power size={14} /></button>
              <button onClick={() => togglePremium(a)} className={`p-1.5 rounded hover:bg-amber-50 ${a.premium ? "text-amber-500" : "text-[#64748B] hover:text-amber-500"}`} title={a.premium ? "Deactivate premium" : "Activate premium"} data-testid={`apps-premium-${a.slug}`}><Star size={14} className={a.premium ? "fill-current" : ""} /></button>
              <button
                onClick={() => seoBoost(a)}
                disabled={seoBoostBusy === a.slug}
                className={`p-1.5 rounded hover:bg-blue-50 ${seoBoostBusy === a.slug ? "text-[#94A3B8]" : "text-[#64748B] hover:text-[#2563EB]"}`}
                title="SEO Boost (IndexNow ping)"
                data-testid={`apps-seo-boost-${a.slug}`}
              >{seoBoostBusy === a.slug ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />}</button>
            </div>

            {/* Action cluster 3: destructive / edit */}
            <div className="flex items-center gap-0.5 shrink-0 border-l border-[#E2E8F0] pl-2">
              <button onClick={() => startEdit(a)} className="p-1.5 rounded hover:bg-blue-50 text-[#64748B] hover:text-[#2563EB]" title="Edit listing" data-testid={`apps-edit-${a.slug}`}><Edit3 size={14} /></button>
              <button onClick={() => remove(a.slug)} className="p-1.5 rounded hover:bg-red-50 text-[#64748B] hover:text-[#EF4444]" title="Delete app" data-testid={`apps-delete-${a.slug}`}><Trash2 size={14} /></button>
            </div>
          </div>
        );})}
      </div>
      {outreachSlug && (
        <OutreachModal
          slug={outreachSlug}
          appName={apps.find((x) => x.slug === outreachSlug)?.name || outreachSlug}
          website={apps.find((x) => x.slug === outreachSlug)?.website}
          onClose={() => { setOutreachSlug(null); refreshOutreachStatus(); }}
        />
      )}
      {pitchSlug && (
        <PitchReachModal
          slug={pitchSlug}
          app={apps.find((x) => x.slug === pitchSlug)}
          visibility={visibility[pitchSlug]}
          onClose={() => setPitchSlug(null)}
          onOutreach={() => {
            setPitchSlug(null);
            setOutreachSlug(pitchSlug);
          }}
        />
      )}
    </div>
  );
};

// ---- Outreach Modal — find email + send claim invite ----------------------

const OutreachModal = ({ slug, appName, website, onClose }) => {
  const [candidates, setCandidates] = useState([]);
  const [scanned, setScanned] = useState(null);
  const [selected, setSelected] = useState("");
  const [custom, setCustom] = useState("");
  const [log, setLog] = useState({ last_find: null, sends: [] });
  const [busy, setBusy] = useState(false);

  const refreshLog = async () => {
    try {
      const d = await adminAppOutreachLog(slug);
      setLog(d);
      if (d.last_find?.found_emails?.length) {
        setCandidates(d.last_find.found_emails);
        setSelected(d.last_find.found_emails[0]);
        setScanned(d.last_find);
      }
    } catch (_) {/* ignore */}
  };

  useEffect(() => { refreshLog(); /* eslint-disable-next-line */ }, [slug]);

  const scan = async () => {
    setBusy(true);
    try {
      const r = await adminFindAppEmail(slug);
      setCandidates(r.emails || []);
      setScanned(r);
      if ((r.emails || []).length) setSelected(r.emails[0]);
      await refreshLog();
    } catch (e) {
      alert(`Scan failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const send = async () => {
    const target = (custom || selected || "").trim();
    if (!target) return alert("Pick or enter an email");
    if (!window.confirm(`Send claim invitation for ${appName} to ${target}?`)) return;
    setBusy(true);
    try {
      await adminSendAppOutreach(slug, target);
      alert(`Sent to ${target}!`);
      setCustom("");
      await refreshLog();
    } catch (e) {
      alert(`Send failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const lastSendSuccess = (log.sends || []).find((s) => s.status === "sent");

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="outreach-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#E2E8F0]">
          <div>
            <div className="text-[10px] uppercase font-mono tracking-wide text-[#94A3B8]">Owner outreach</div>
            <h3 className="font-bold text-lg text-[#0F172A]">{appName}</h3>
            {website && <a href={website} target="_blank" rel="noreferrer" className="text-xs text-[#2563EB] hover:underline">{website} <ExternalLink size={10} className="inline" /></a>}
          </div>
          <button onClick={onClose} className="text-[#64748B] hover:text-[#0F172A]" data-testid="outreach-close"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-4">
          {lastSendSuccess && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900" data-testid="outreach-last-sent">
              ✅ Already sent on {new Date(lastSendSuccess.sent_at).toLocaleDateString()} to <strong>{lastSendSuccess.email}</strong>
            </div>
          )}

          {/* Step 1: scan */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wide text-[#64748B] mb-2">Step 1: discover contact email</div>
            <button onClick={scan} disabled={busy || !website} className="hac-btn-secondary" data-testid="outreach-scan">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
              {scanned ? "Re-scan website" : "Scan website for emails"}
            </button>
            {scanned && (
              <div className="text-[11px] text-[#64748B] mt-2 font-mono">
                Pages scanned: {scanned.pages_scanned} · Found: {(scanned.emails || candidates).length}
              </div>
            )}
          </div>

          {/* Step 2: pick or manually enter recipient — always shown after first scan
              attempt so the admin can still send when the scanner found 0 hits. */}
          {scanned && (
            <div className="space-y-2">
              <div className="text-[10px] font-bold uppercase tracking-wide text-[#64748B]">
                Step 2: {candidates.length > 0 ? "pick or enter recipient" : "enter recipient manually"}
              </div>
              {candidates.length === 0 && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 leading-snug">
                  No emails were detected on the website. Enter one manually below — typical patterns: <span className="font-mono">info@</span>, <span className="font-mono">hello@</span>, <span className="font-mono">contact@</span> + domain.
                </div>
              )}
              {candidates.map((e, i) => (
                <label key={e} className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer text-sm ${selected === e ? "border-[#EA580C] bg-orange-50" : "border-[#E2E8F0] hover:border-[#CBD5E1]"}`} data-testid={`outreach-candidate-${i}`}>
                  <input type="radio" name="email" value={e} checked={selected === e} onChange={() => { setSelected(e); setCustom(""); }} />
                  <span className="font-mono">{e}</span>
                  {i === 0 && <span className="ml-auto text-[9px] uppercase font-bold text-[#EA580C]">recommended</span>}
                </label>
              ))}
              <input
                type="email"
                value={custom}
                onChange={(e) => { setCustom(e.target.value); if (e.target.value) setSelected(""); }}
                placeholder={candidates.length > 0 ? "…or enter a custom email" : "e.g. info@example.com"}
                className="hac-input w-full text-sm"
                data-testid="outreach-custom"
              />
            </div>
          )}

          {/* Step 3: send — also always shown once scan completed */}
          {scanned && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wide text-[#64748B] mb-2">Step 3: send invitation</div>
              <button onClick={send} disabled={busy || (!selected && !custom.trim())} className="hac-btn-primary" data-testid="outreach-send">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
                Send claim invitation
              </button>
              <p className="text-[11px] text-[#64748B] mt-2 leading-relaxed">
                Email body: brief, professional invite with the listing URL + claim link. If the recipient's email domain matches their website, the claim is auto-trusted.
              </p>
            </div>
          )}

          {(log.sends || []).length > 0 && (
            <details>
              <summary className="text-xs text-[#64748B] cursor-pointer">Send history ({log.sends.length} attempt{log.sends.length === 1 ? "" : "s"})</summary>
              <ul className="mt-2 text-[11px] font-mono space-y-1 text-[#475569]">
                {log.sends.map((s, i) => (
                  <li key={s.id || s.sent_at}>
                    <span className="inline-block min-w-[28px] text-[#94A3B8]">#{(log.sends.length - i)}</span>
                    {new Date(s.sent_at).toLocaleString()} · {s.status === "sent" ? "✅" : "❌"} {s.email}
                    {s.is_followup ? <span className="ml-1 text-[#2563EB]">follow-up</span> : null}
                    {s.error ? ` · ${s.error}` : ""}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Claim Reviews Tab — admin reviews owner-submitted edits side-by-side ----

const ClaimReviewsTab = () => {
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [filter, setFilter] = useState("pending_review");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const d = await adminListClaimEdits(filter);
      setItems(d.items || []);
      setPendingCount(d.pending_count || 0);
    } catch (e) {/* ignore */}
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (id, name) => {
    if (!window.confirm(`Approve edits for ${name}? They will be merged into the live app and it will be set active.`)) return;
    setBusy(true);
    try { await adminApproveClaimEdit(id); await refresh(); }
    catch (e) { alert(`Approve failed: ${e.response?.data?.detail || e.message}`); }
    finally { setBusy(false); }
  };

  const reject = async (id, name) => {
    const reason = window.prompt(`Reject edits for ${name}? Optional reason (will be stored, not emailed to owner yet):`);
    if (reason === null) return;
    setBusy(true);
    try { await adminRejectClaimEdit(id, reason || ""); await refresh(); }
    catch (e) { alert(`Reject failed: ${e.response?.data?.detail || e.message}`); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="claim-reviews-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-bold text-2xl text-[#0F172A]">Claim reviews</h2>
          <p className="text-sm text-[#64748B] mt-1">Owners who clicked their outreach email and submitted updates. Approve to merge into the live app.</p>
        </div>
        <div className="flex gap-2 items-center">
          <span className="text-xs text-[#64748B]">{pendingCount} pending</span>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="hac-input text-sm" data-testid="claim-reviews-filter">
            <option value="pending_review">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="">All</option>
          </select>
        </div>
      </div>

      {items.length === 0 && (
        <div className="text-center py-12 text-sm text-[#94A3B8]">No claim edits in this status yet.</div>
      )}

      <div className="space-y-6">
        {items.map((edit) => (
          <ClaimReviewCard key={edit.id} edit={edit} onApprove={() => approve(edit.id, edit.slug)} onReject={() => reject(edit.id, edit.slug)} busy={busy} />
        ))}
      </div>
    </div>
  );
};

const ClaimReviewCard = ({ edit, onApprove, onReject, busy }) => {
  const [app, setApp] = useState(null);
  useEffect(() => {
    getApp(edit.slug).then(setApp).catch(() => setApp(null));
  }, [edit.slug]);

  const proposed = edit.proposed || {};
  const fields = [
    { key: "tagline", label: "Tagline" },
    { key: "description", label: "Description", textarea: true },
    { key: "website", label: "Website" },
    { key: "country", label: "Country" },
    { key: "founded", label: "Founded" },
  ];

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden" data-testid={`claim-card-${edit.id}`}>
      <div className="flex items-center justify-between p-4 bg-[#0F172A] text-white">
        <div className="flex items-center gap-3 min-w-0">
          {app?.logo && <img src={app.logo} alt="" className="w-10 h-10 rounded-lg object-cover bg-white" />}
          <div className="min-w-0">
            <div className="font-bold truncate">{app?.name || edit.slug}</div>
            <div className="text-xs text-white/70 truncate">
              from <span className="font-mono">{edit.email}</span> · {new Date(edit.submitted_at).toLocaleString()}
              {edit.contact_name && ` · ${edit.contact_name}${edit.contact_role ? ` (${edit.contact_role})` : ""}`}
            </div>
          </div>
        </div>
        {edit.status === "pending_review" && (
          <div className="flex gap-2 shrink-0">
            <button onClick={onReject} disabled={busy} className="px-3 py-1.5 rounded-full text-xs bg-white/10 hover:bg-red-500 text-white inline-flex items-center gap-1" data-testid={`claim-reject-${edit.id}`}>
              <X size={12} /> Reject
            </button>
            <button onClick={onApprove} disabled={busy} className="px-3 py-1.5 rounded-full text-xs bg-emerald-500 hover:bg-emerald-600 text-white inline-flex items-center gap-1 font-bold" data-testid={`claim-approve-${edit.id}`}>
              <Check size={12} /> Approve & publish
            </button>
          </div>
        )}
        {edit.status !== "pending_review" && (
          <span className={`text-[10px] uppercase font-mono px-2 py-1 rounded ${edit.status === "approved" ? "bg-emerald-500/30 text-emerald-200" : "bg-red-500/30 text-red-200"}`}>
            {edit.status}
          </span>
        )}
      </div>

      {edit.note && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900">
          <strong>Note from owner:</strong> {edit.note}
        </div>
      )}

      <div className="p-4 space-y-4">
        {/* Logo diff */}
        {("logo" in proposed) && (
          <DiffRow label="Logo">
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-[10px] uppercase text-[#94A3B8] mb-1">Current</div>
                {app?.logo ? <img src={app.logo} alt="" className="w-16 h-16 object-cover rounded-lg border border-[#E2E8F0]" /> : <div className="w-16 h-16 bg-[#F1F5F9] rounded-lg" />}
              </div>
              <div className="text-[#94A3B8]">→</div>
              <div className="text-center">
                <div className="text-[10px] uppercase text-emerald-600 mb-1 font-bold">Proposed</div>
                <img src={proposed.logo} alt="" className="w-16 h-16 object-cover rounded-lg border-2 border-emerald-400" />
              </div>
            </div>
          </DiffRow>
        )}

        {/* Screenshots diff */}
        {("screenshots" in proposed) && (
          <DiffRow label={`Screenshots (${proposed.screenshots?.length || 0} proposed)`}>
            <div className="grid grid-cols-6 gap-2">
              {(proposed.screenshots || []).slice(0, 6).map((s) => (
                <img key={s} src={s} alt="" className="aspect-[9/16] object-cover rounded border-2 border-emerald-400" />
              ))}
            </div>
          </DiffRow>
        )}

        {/* Text diffs */}
        {fields.map((f) => {
          if (!(f.key in proposed)) return null;
          const current = app?.[f.key] ?? "";
          const proposedVal = proposed[f.key] ?? "";
          if (String(current).trim() === String(proposedVal).trim()) return null;
          return (
            <DiffRow key={f.key} label={f.label}>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div className="bg-red-50 border border-red-200 rounded p-2">
                  <div className="text-[10px] uppercase text-red-700 mb-1 font-bold">Current</div>
                  <div className="text-[#475569] whitespace-pre-wrap break-words">{current || <em className="text-[#94A3B8]">(empty)</em>}</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                  <div className="text-[10px] uppercase text-emerald-700 mb-1 font-bold">Proposed</div>
                  <div className="text-[#0F172A] whitespace-pre-wrap break-words">{proposedVal || <em className="text-[#94A3B8]">(empty)</em>}</div>
                </div>
              </div>
            </DiffRow>
          );
        })}
      </div>
    </div>
  );
};

const DiffRow = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide font-bold text-[#64748B] mb-2">{label}</div>
    {children}
  </div>
);


// ---- PitchReachModal — preview reach + 1-click pitch ----------------------

const PitchReachModal = ({ slug, app, visibility, onClose, onOutreach }) => {
  const vis = visibility || {};
  const total = vis.total_combinations || 0;
  const base = vis.base_pages || 0;
  const langs = vis.languages || 22;
  const b = vis.breakdown || {};
  const name = app?.name || slug;

  const pitchText =
    `${name} is now indexed on ${total.toLocaleString("en-US")} URLs across HorseAppCompare — ` +
    `${base.toLocaleString("en-US")} unique pages × ${langs} languages, including ` +
    `${b.comparison || 0} head-to-head comparisons, ${b.categories || 0} category pages, ` +
    `${b.regions || 0} region pages and ${b.listicles || 0} blog articles. ` +
    `Fully translated & SEO-indexed.`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pitchText);
      // light feedback
      const el = document.querySelector('[data-testid="pitch-copy-btn"]');
      if (el) {
        const old = el.innerText;
        el.innerText = "✓ Copied";
        setTimeout(() => { el.innerText = old; }, 1500);
      }
    } catch (e) {
      window.prompt("Copy this pitch:", pitchText);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="pitch-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Rocket size={18} className="text-amber-600" />
            <h2 className="font-heading text-lg text-[#0F172A]">{name} — reach pitch</h2>
          </div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]" data-testid="pitch-close"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          {/* Hero number */}
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-300 rounded-xl p-6 text-center">
            <div className="text-xs uppercase tracking-widest font-bold text-amber-800 mb-1">Indexed on</div>
            <div className="text-5xl font-heading font-bold text-[#0F172A]" data-testid="pitch-total">{total.toLocaleString("en-US")} URLs</div>
            <div className="text-sm text-amber-800 mt-2">{base.toLocaleString("en-US")} unique pages &times; {langs} languages</div>
          </div>

          {/* Breakdown grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="pitch-breakdown">
            {[
              ["Comparisons", b.comparison, "head-to-head pages"],
              ["Categories", b.categories, "category pages"],
              ["Regions", b.regions, "region pages"],
              ["Listicles", b.listicles, "blog mentions"],
              ["Ranking", b.ranking, "Top 100 page"],
              ["Sitemap", b.sitemap, "canonical entry"],
            ].map(([label, n, sub]) => (
              <div key={label} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
                <div className="text-xs text-[#64748B]">{label}</div>
                <div className="text-xl font-heading font-bold text-[#0F172A]">{(n || 0).toLocaleString("en-US")}</div>
                <div className="text-[10px] text-[#94A3B8]">{sub}</div>
              </div>
            ))}
          </div>

          {/* Pitch sentence */}
          <div className="bg-[#0F172A] text-white rounded-xl p-4 text-sm leading-relaxed" data-testid="pitch-sentence">
            {pitchText}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2 justify-end pt-2 border-t border-[#E2E8F0]">
            <button
              type="button"
              onClick={copy}
              data-testid="pitch-copy-btn"
              className="inline-flex items-center justify-center gap-2 text-sm border border-[#E2E8F0] text-[#0F172A] rounded-full px-5 py-2 hover:bg-[#F8FAFC] transition-colors"
            >
              <ExternalLink size={14} /> Copy pitch
            </button>
            <button
              type="button"
              onClick={onOutreach}
              data-testid="pitch-outreach-btn"
              className="inline-flex items-center justify-center gap-2 text-sm bg-[#EA580C] text-white rounded-full px-5 py-2 hover:bg-[#C2410C] transition-colors"
            >
              <Mail size={14} /> Send outreach with this pitch →
            </button>
          </div>
          <p className="text-[10px] text-[#94A3B8] text-center">
            The pitch automatically appears in the outreach email's visibility block. Clicking &quot;Send outreach&quot; opens the email composer for {name}.
          </p>
        </div>
      </div>
    </div>
  );
};



// ---- Outreach Batch Panel — bulk send to all unclaimed apps -----------------

const OutreachBatchPanel = () => {
  const [latest, setLatest] = useState(null);
  const [totalSent, setTotalSent] = useState(0);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const d = await adminOutreachBatchStatus();
      setLatest(d.latest);
      setTotalSent(d.total_sent_all_time || 0);
    } catch (_) {/* ignore */}
  };
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 4000);
    return () => clearInterval(id);
  }, []);

  const start = async (dryRun) => {
    if (!dryRun && !window.confirm("This will send REAL emails to every unclaimed app's contact email. Continue?")) return;
    setBusy(true);
    try { await adminOutreachBatch(dryRun); await refresh(); }
    catch (e) { alert(`Failed: ${e.response?.data?.detail || e.message}`); }
    finally { setBusy(false); }
  };

  const running = latest?.status === "running";
  const progress = running && latest?.total ? Math.round(((latest.sent + latest.skipped) / latest.total) * 100) : 0;

  return (
    <div className="mb-8 rounded-2xl border border-dashed border-[#FED7AA] bg-gradient-to-br from-[#FFF7ED] via-[#FFEDD5] to-white p-5" data-testid="outreach-batch-panel">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#EA580C] text-white flex items-center justify-center shrink-0">
          <Mail size={18} />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-[#0F172A] mb-1">Outreach: invite app owners to claim their listing</h3>
          <p className="text-xs text-[#475569] leading-relaxed max-w-2xl">
            For every unclaimed app with a website, scrape the contact email and send a personalised invitation. Already-sent apps are skipped automatically. <strong>Total sent all-time:</strong> {totalSent}
          </p>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => start(true)} disabled={busy || running} className="hac-btn-secondary" data-testid="outreach-batch-dry">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} Dry-run (find emails only)
        </button>
        <button onClick={() => start(false)} disabled={busy || running} className="hac-btn-primary" data-testid="outreach-batch-send">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} {running ? "Running…" : "Send to all unclaimed apps"}
        </button>
      </div>
      {running && (
        <div className="mt-4 bg-white rounded-xl border border-[#FED7AA] p-3" data-testid="outreach-batch-progress">
          <div className="flex items-center justify-between text-xs text-[#475569] mb-2">
            <span>Processing <strong>{latest?.last_slug || "…"}</strong> · sent {latest?.sent || 0} · found {latest?.found || 0} · skipped {latest?.skipped || 0} of {latest?.total || "?"}</span>
            <span className="font-mono">{progress}%</span>
          </div>
          <div className="h-2 bg-[#FED7AA] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#EA580C] to-[#C2410C] transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}
      {latest && latest.status === "done" && (
        <div className="mt-3 text-xs text-[#475569]">
          {latest.dry_run ? "🔍 Dry-run:" : "✅"} Last run: found <strong>{latest.found}</strong> emails, sent <strong>{latest.sent}</strong>, skipped {latest.skipped}, errors {latest.errors}.
        </div>
      )}
    </div>
  );
};

// ---- Follow-ups panel — auto-resend claim invites weekly ------------------

const OutreachFollowupsPanel = ({ onActioned }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [rowBusy, setRowBusy] = useState({});

  const refresh = async () => {
    try {
      const d = await adminOutreachFollowups();
      setData(d);
    } catch (_) { /* ignore */ }
  };
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, []);

  if (!data) return null;
  const eligible = data.eligible || [];
  const upcoming = data.upcoming || [];

  const runBatch = async (dryRun) => {
    if (!dryRun && !window.confirm(`Send follow-ups to ${eligible.length} eligible app(s) now?`)) return;
    setBusy(true);
    try {
      await adminOutreachFollowupBatch(dryRun);
      setTimeout(async () => { await refresh(); onActioned?.(); }, 1500);
    } catch (e) {
      alert(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const sendOne = async (slug, force = false) => {
    setRowBusy((s) => ({ ...s, [slug]: true }));
    try {
      await adminSendFollowup(slug, force);
      await refresh();
      onActioned?.();
    } catch (e) {
      alert(`${slug}: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRowBusy((s) => ({ ...s, [slug]: false }));
    }
  };

  const reset = async (slug) => {
    if (!window.confirm(`Reset all outreach attempts for "${slug}"? They'll be eligible for a fresh first-time invite.`)) return;
    try { await adminOutreachReset(slug); await refresh(); onActioned?.(); }
    catch (e) { alert(`Failed: ${e.response?.data?.detail || e.message}`); }
  };

  const optOut = async (slug) => {
    const email = window.prompt(`Opt-out address for "${slug}" (leave blank to opt-out the slug only):`, "");
    if (email === null) return;
    try { await adminOutreachOptOut(slug, email); await refresh(); onActioned?.(); }
    catch (e) { alert(`Failed: ${e.response?.data?.detail || e.message}`); }
  };

  return (
    <div className="mb-8 rounded-2xl border border-dashed border-[#BFDBFE] bg-gradient-to-br from-[#EFF6FF] via-[#DBEAFE] to-white p-5" data-testid="outreach-followups-panel">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#2563EB] text-white flex items-center justify-center shrink-0">
          <Clock size={18} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-[#0F172A]">Claim follow-ups · weekly auto-reminder</h3>
            <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded-full bg-white border border-[#BFDBFE] text-[#1D4ED8]">{data.cooldown_days}d cooldown · max {data.max_attempts}</span>
            <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded-full bg-white border border-[#BFDBFE] text-[#1D4ED8]">Next auto-run: {data.next_auto_run}</span>
          </div>
          <p className="text-xs text-[#475569] leading-relaxed max-w-2xl mt-1">
            Apps that received a claim invite earlier but didn&apos;t respond. After {data.cooldown_days} days the system can send a follow-up (max {data.max_attempts} attempts total). Runs automatically every Monday at 05:00 UTC, or trigger manually here.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-2xl text-[#0F172A] font-bold tabular-nums">{eligible.length}</span>
          <span className="text-xs text-[#64748B] uppercase tracking-wider">eligible now</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-2xl text-[#0F172A] tabular-nums">{upcoming.length}</span>
          <span className="text-xs text-[#64748B] uppercase tracking-wider">upcoming</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-2xl text-[#94A3B8] tabular-nums">{data.exhausted_count || 0}</span>
          <span className="text-xs text-[#64748B] uppercase tracking-wider">exhausted</span>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap mb-3">
        <button onClick={() => runBatch(true)} disabled={busy || eligible.length === 0} className="hac-btn-secondary text-xs" data-testid="followup-dry-run">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} Dry-run
        </button>
        <button onClick={() => runBatch(false)} disabled={busy || eligible.length === 0} className="hac-btn-primary text-xs" data-testid="followup-run-now">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Send all eligible follow-ups now
        </button>
        <button onClick={() => setExpanded(!expanded)} className="text-xs px-3 py-1.5 rounded-full border border-[#BFDBFE] text-[#1D4ED8] hover:bg-white" data-testid="followup-toggle-details">
          {expanded ? "Hide" : "Show"} per-app details
        </button>
      </div>

      {expanded && (
        <div className="bg-white rounded-xl border border-[#BFDBFE] divide-y divide-[#E2E8F0] overflow-hidden">
          {eligible.length === 0 && upcoming.length === 0 && (
            <div className="px-4 py-6 text-center text-xs text-[#94A3B8]">No follow-ups tracked yet — start by sending first-time outreach above.</div>
          )}
          {eligible.map((row) => (
            <div key={row.slug} className="px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`followup-row-${row.slug}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm text-[#0F172A]">{row.name}</span>
                  <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">eligible</span>
                  <span className="text-[10px] font-mono text-[#64748B]">{row.attempts}/{row.max_attempts}</span>
                </div>
                <div className="text-[11px] text-[#64748B] font-mono">{row.last_email} · last sent {new Date(row.last_sent_at).toLocaleDateString()}</div>
              </div>
              <button onClick={() => sendOne(row.slug)} disabled={rowBusy[row.slug]} className="text-xs px-3 py-1.5 rounded-full bg-[#2563EB] text-white hover:bg-[#1D4ED8] disabled:opacity-50" data-testid={`followup-send-${row.slug}`}>
                {rowBusy[row.slug] ? "Sending…" : `Send #${row.attempts + 1}`}
              </button>
              <button onClick={() => optOut(row.slug)} className="text-xs px-2 py-1.5 rounded-full border border-[#E2E8F0] text-[#64748B] hover:border-[#EF4444] hover:text-[#EF4444]" title="Opt this app out of future outreach" data-testid={`followup-optout-${row.slug}`}>Opt-out</button>
              <button onClick={() => reset(row.slug)} className="text-xs px-2 py-1.5 rounded-full border border-[#E2E8F0] text-[#64748B] hover:border-[#2563EB] hover:text-[#2563EB]" title="Reset attempt counter" data-testid={`followup-reset-${row.slug}`}>Reset</button>
            </div>
          ))}
          {upcoming.map((row) => (
            <div key={row.slug} className="px-4 py-3 flex items-center gap-3 flex-wrap bg-[#F8FAFC]/50" data-testid={`followup-upcoming-${row.slug}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm text-[#0F172A]">{row.name}</span>
                  <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#64748B]">in {row.days_until.toFixed(1)}d</span>
                  <span className="text-[10px] font-mono text-[#64748B]">{row.attempts}/{row.max_attempts}</span>
                </div>
                <div className="text-[11px] text-[#94A3B8] font-mono">{row.last_email} · last sent {new Date(row.last_sent_at).toLocaleDateString()}</div>
              </div>
              <button onClick={() => sendOne(row.slug, true)} disabled={rowBusy[row.slug]} className="text-xs px-3 py-1.5 rounded-full border border-[#2563EB] text-[#2563EB] hover:bg-[#EFF6FF] disabled:opacity-50" title="Override cooldown and send now" data-testid={`followup-force-${row.slug}`}>
                {rowBusy[row.slug] ? "Sending…" : "Force send"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DiscoverAppsPanel = ({ onComplete }) => {
  const [count, setCount] = useState(8);
  const [focus, setFocus] = useState("");
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [pending, setPending] = useState(0);
  const [busy, setBusy] = useState(false);

  const fetchStatus = async () => {
    try {
      const data = await adminDiscoveryStatus();
      setLatest(data.latest);
      setHistory(data.history || []);
      setPending(data.pending_count || 0);
    } catch (_) {
      /* ignore polling errors */
    }
  };

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 4000);
    return () => clearInterval(id);
  }, []);

  // When a job moves from `running` → `done`, refresh the parent app list.
  useEffect(() => {
    if (latest?.status === "done" && latest?.inserted > 0) onComplete?.();
  }, [latest?.status, latest?.inserted]); // eslint-disable-line

  const start = async () => {
    setBusy(true);
    try {
      await adminDiscoverApps({ count, focus });
      await fetchStatus();
    } catch (err) {
      alert(`Discovery failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const running = latest?.status === "running";
  const progress =
    running && latest?.candidates_total
      ? Math.round(((latest.inserted + (latest.skipped || 0)) / latest.candidates_total) * 100)
      : 0;

  return (
    <div className="mb-8 rounded-2xl border border-dashed border-[#C7D2FE] bg-gradient-to-br from-[#EEF2FF] via-[#F5F3FF] to-white p-5" data-testid="apps-discover-panel">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#4F46E5] text-white flex items-center justify-center shrink-0">
          <Sparkles size={18} />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-[#0F172A] mb-1">Discover new horse apps via AI</h3>
          <p className="text-xs text-[#475569] leading-relaxed max-w-2xl">
            Claude scans its knowledge of equestrian software, picks products that are <strong>not yet in your directory</strong>, and auto-fills the full profile (tagline EN+NL, description, country, platforms, pricing, regions, features). New apps land as <em>disabled</em> so you can review before publishing.
          </p>
        </div>
        {pending > 0 && (
          <span className="text-[10px] font-mono uppercase px-2 py-1 rounded bg-amber-100 text-amber-800 shrink-0" data-testid="apps-discover-pending-count">
            {pending} pending review
          </span>
        )}
      </div>

      <div className="grid sm:grid-cols-[120px_1fr_auto] gap-3 items-end">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wide text-[#64748B] mb-1">Count</label>
          <input
            type="number"
            min="1"
            max="25"
            value={count}
            onChange={(e) => setCount(parseInt(e.target.value || "8", 10))}
            disabled={running || busy}
            className="hac-input w-full"
            data-testid="apps-discover-count"
          />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wide text-[#64748B] mb-1">Focus area (optional)</label>
          <input
            type="text"
            value={focus}
            onChange={(e) => setFocus(e.target.value)}
            placeholder="e.g. breeding software, Asian market, AI horse apps"
            disabled={running || busy}
            className="hac-input w-full"
            data-testid="apps-discover-focus"
          />
        </div>
        <button
          onClick={start}
          disabled={busy || running}
          className="hac-btn-primary"
          data-testid="apps-discover-start"
        >
          {busy || running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {running ? "Running…" : busy ? "Starting…" : "Discover now"}
        </button>
      </div>

      {running && (
        <div className="mt-4 bg-white rounded-xl border border-[#E2E8F0] p-3" data-testid="apps-discover-progress">
          <div className="flex items-center justify-between text-xs text-[#475569] mb-2">
            <span>
              Researching <strong>{latest?.last_app || "…"}</strong> ·{" "}
              {latest?.inserted || 0}/{latest?.candidates_total || "?"} enriched
            </span>
            <span className="font-mono">{progress}%</span>
          </div>
          <div className="h-2 bg-[#E2E8F0] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {latest && latest.status === "done" && (
        <div className="mt-4 text-xs text-[#475569]" data-testid="apps-discover-result">
          ✅ Last run added <strong>{latest.inserted}</strong> apps, skipped {latest.skipped} duplicates.
          {latest.errors?.length > 0 && <span className="text-red-600 ml-2">({latest.errors.length} errors)</span>}
        </div>
      )}

      {history.length > 1 && (
        <details className="mt-3">
          <summary className="text-xs text-[#64748B] cursor-pointer hover:text-[#0F172A]">Recent runs</summary>
          <ul className="mt-2 text-xs text-[#475569] space-y-1">
            {history.slice(0, 5).map((j) => (
              <li key={j.id} className="font-mono">
                {new Date(j.started_at).toLocaleString()} · {j.status} · +{j.inserted || 0} / {j.candidates_total || 0}
                {j.focus ? ` · "${j.focus}"` : ""}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

// ---- Submissions Tab — review pending app submissions ----------------------

const SubmissionsTab = () => {
  const [subs, setSubs] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const refresh = () => adminListSubmissions(filter || undefined).then(setSubs).catch(() => setSubs([]));
  useEffect(() => { refresh(); }, [filter]); // eslint-disable-line

  const approve = async (id, force = false, overwrite = false) => {
    setBusyId(id);
    try {
      const res = await adminApproveSubmission(id, force, overwrite);
      alert(`Published as /apps/${res.slug} ${res.overwritten ? "(overwrote existing app) " : ""}✓`);
      refresh();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      if (err.response?.status === 402 && window.confirm(`${detail}\n\nForce approve anyway (will publish as Premium without invoice paid)?`)) {
        return approve(id, true, overwrite);
      }
      if (err.response?.status === 409 && /already exists/i.test(detail) && window.confirm(`${detail}\n\nOverwrite the existing live app with this submission's content?\n\nPreserved on the live app: claims, premium status, owner email, click counters.\nReplaced: name, description, pricing, features, screenshots, score.`)) {
        return approve(id, force, true);
      }
      alert(`Approve failed: ${detail}`);
    }
    finally { setBusyId(null); }
  };
  const reject = async (id) => {
    if (!window.confirm("Reject this submission?")) return;
    await adminRejectSubmission(id);
    refresh();
  };
  const remove = async (id) => {
    if (!window.confirm("Permanently delete this submission?")) return;
    await adminDeleteSubmission(id);
    refresh();
  };
  const setInvoice = async (id, status) => {
    setBusyId(id);
    try { await adminUpdateInvoice(id, status); refresh(); }
    catch (err) { alert(`Invoice update failed: ${err.response?.data?.detail || err.message}`); }
    finally { setBusyId(null); }
  };

  return (
    <div data-testid="submissions-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-bold text-2xl text-[#0F172A]">App submissions</h2>
          <p className="text-sm text-[#64748B] mt-1">Apps submitted by their owners via the public form. Approve to publish them in the live directory.</p>
        </div>
        <div className="flex gap-1.5">
          {["pending", "approved", "rejected", ""].map((s) => (
            <button key={s || "all"} onClick={() => setFilter(s)} className={`px-3 py-1.5 text-xs rounded-full border ${filter === s ? "bg-[#2563EB] border-[#2563EB] text-white" : "bg-white border-[#E2E8F0] text-[#64748B]"}`} data-testid={`subs-filter-${s || "all"}`}>{s || "all"}</button>
          ))}
        </div>
      </div>

      {subs.length === 0 && <div className="text-center text-[#94A3B8] py-10">No submissions {filter && `with status "${filter}"`}.</div>}

      <div className="space-y-3">
        {subs.map((s) => {
          const isOpen = expandedId === s.id;
          return (
          <div key={s.id} className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden" data-testid={`subs-row-${s.id}`}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setExpandedId(isOpen ? null : s.id)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpandedId(isOpen ? null : s.id); } }}
              className="w-full text-left p-5 hover:bg-[#F8FAFC] transition-colors cursor-pointer"
              data-testid={`subs-toggle-${s.id}`}
            >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-[#F1F5F9] flex items-center justify-center">
                {s.app?.logo ? <img src={s.app.logo} alt="" className="w-full h-full object-cover" /> : <Inbox size={22} className="text-[#94A3B8]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-bold text-lg text-[#0F172A]">{s.app?.name}</div>
                  <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${s.status === "pending" ? "bg-amber-100 text-amber-700" : s.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{s.status}</span>
                  {s.tier && s.tier !== "standard" && (
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-amber-400 text-amber-900 inline-flex items-center gap-1" data-testid={`subs-tier-${s.id}`}>
                      <Star size={10} className="fill-current" /> Premium · {s.tier === "premium_yearly" ? "yearly €990" : "monthly €99"}
                    </span>
                  )}
                  {s.tier && s.tier !== "standard" && s.invoice_status && (
                    <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${s.invoice_status === "paid" ? "bg-emerald-100 text-emerald-700" : s.invoice_status === "sent" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"}`} data-testid={`subs-invoice-${s.id}`}>
                      invoice: {s.invoice_status}
                    </span>
                  )}
                  {s.discount_code && s.discount_valid && (
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 inline-flex items-center gap-1" data-testid={`subs-discount-${s.id}`}>
                      🎟️ {s.discount_code} (-{s.discount_percent_off}%)
                    </span>
                  )}
                  {s.discount_code && !s.discount_valid && (
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-100 text-red-700" data-testid={`subs-discount-invalid-${s.id}`}>
                      🎟️ {s.discount_code} (invalid)
                    </span>
                  )}
                  <span className="text-[11px] text-[#94A3B8] font-mono">→ /apps/{s.app?.slug}</span>
                  <span className="ml-auto text-[#64748B] text-xs inline-flex items-center gap-1">{isOpen ? "▾ details" : "▸ klik voor details"}</span>
                </div>
                {s.app?.tagline && <div className="text-sm text-[#64748B] mt-1">{s.app.tagline}</div>}
                <div className="text-xs text-[#94A3B8] mt-2">
                  Submitted by <strong>{s.submitter?.name || "?"}</strong> &lt;{s.submitter?.email}&gt;
                  {s.submitter?.role && <> · {s.submitter.role}</>}
                  &nbsp;· {new Date(s.submitted_at).toLocaleString()}
                </div>
                {s.billing && (s.billing.company || s.billing.address) && (
                  <div className="text-xs text-[#0F172A] mt-2 bg-amber-50 border border-amber-200 p-2 rounded">
                    <div className="font-semibold mb-0.5">Billing</div>
                    <div>{s.billing.company}{s.billing.vat_number && ` · VAT ${s.billing.vat_number}`}</div>
                    <div className="text-[#64748B]">{s.billing.address}{s.billing.postal_code && `, ${s.billing.postal_code}`} {s.billing.city}{s.billing.country && `, ${s.billing.country}`}</div>
                  </div>
                )}
                {s.notes && <div className="text-xs text-[#64748B] mt-2 italic bg-[#F8FAFC] p-2 rounded border border-[#E2E8F0]">"{s.notes}"</div>}
                {(s.screenshot_urls || []).length > 0 && (
                  <div className="flex gap-2 mt-3 overflow-x-auto">
                    {s.screenshot_urls.map((u, i) => (
                      <img key={i} src={u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u} alt="" className="h-20 rounded border border-[#E2E8F0]" />
                    ))}
                  </div>
                )}
                {s.status === "pending" && s.tier !== "standard" && s.invoice_status !== "paid" && (
                  <div className="mt-3 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                    <span className="text-[11px] text-[#64748B] self-center">Invoice flow:</span>
                    {s.invoice_status !== "sent" && (
                      <button onClick={() => setInvoice(s.id, "sent")} disabled={busyId === s.id} className="text-[11px] px-2 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50" data-testid={`subs-invoice-sent-${s.id}`}>Mark invoice sent</button>
                    )}
                    <button onClick={() => setInvoice(s.id, "paid")} disabled={busyId === s.id} className="text-[11px] px-2 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-semibold" data-testid={`subs-invoice-paid-${s.id}`}>Mark paid ✓</button>
                  </div>
                )}
              </div>
              {s.status === "pending" && (
                <div className="flex flex-col gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => approve(s.id)} disabled={busyId === s.id} className="hac-btn-primary text-xs px-3 py-1.5 disabled:opacity-50" data-testid={`subs-approve-${s.id}`}><Check size={12} /> Approve & publish</button>
                  <button onClick={() => reject(s.id)} className="hac-btn-secondary text-xs px-3 py-1.5" data-testid={`subs-reject-${s.id}`}><X size={12} /> Reject</button>
                </div>
              )}
              <button onClick={(e) => { e.stopPropagation(); remove(s.id); }} className="p-1.5 text-[#64748B] hover:text-[#EF4444] self-start" data-testid={`subs-delete-${s.id}`}><Trash2 size={14} /></button>
            </div>
            </div>

            {isOpen && (
              <div className="border-t border-[#E2E8F0] bg-[#F8FAFC] p-5 space-y-4" data-testid={`subs-detail-${s.id}`}>
                {/* All app fields */}
                <div>
                  <h4 className="text-xs uppercase tracking-widest text-[#94A3B8] font-bold mb-2">App details</h4>
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                    {Object.entries(s.app || {}).filter(([, v]) => v !== null && v !== "" && (!Array.isArray(v) || v.length > 0)).map(([k, v]) => (
                      <div key={k} className="flex gap-2 py-1 border-b border-[#E2E8F0]">
                        <dt className="text-[#64748B] font-mono text-xs w-32 shrink-0 uppercase">{k}</dt>
                        <dd className="text-[#0F172A] break-words flex-1">{
                          typeof v === "object" ? (Array.isArray(v) ? v.join(", ") : JSON.stringify(v))
                          : typeof v === "boolean" ? (v ? "yes" : "no")
                          : (k === "website" || k === "logo") && typeof v === "string" && v.startsWith("http") ? <a href={v} target="_blank" rel="noreferrer" className="text-[#2563EB] hover:underline break-all">{v}</a>
                          : String(v)
                        }</dd>
                      </div>
                    ))}
                  </dl>
                </div>

                {/* Submitter */}
                <div>
                  <h4 className="text-xs uppercase tracking-widest text-[#94A3B8] font-bold mb-2">Submitter</h4>
                  <dl className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                    {Object.entries(s.submitter || {}).filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} className="bg-white border border-[#E2E8F0] rounded p-2">
                        <div className="text-[10px] text-[#94A3B8] uppercase font-mono">{k}</div>
                        <div className="text-[#0F172A] text-sm break-words">{String(v)}</div>
                      </div>
                    ))}
                  </dl>
                </div>

                {/* Billing */}
                {s.billing && Object.values(s.billing).some(v => v) && (
                  <div>
                    <h4 className="text-xs uppercase tracking-widest text-[#94A3B8] font-bold mb-2">Billing</h4>
                    <dl className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                      {Object.entries(s.billing).filter(([, v]) => v).map(([k, v]) => (
                        <div key={k} className="bg-white border border-[#E2E8F0] rounded p-2">
                          <div className="text-[10px] text-[#94A3B8] uppercase font-mono">{k}</div>
                          <div className="text-[#0F172A] text-sm break-words">{String(v)}</div>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}

                {/* Notes */}
                {s.notes && (
                  <div>
                    <h4 className="text-xs uppercase tracking-widest text-[#94A3B8] font-bold mb-2">Notes from submitter</h4>
                    <div className="bg-white border border-[#E2E8F0] rounded p-3 text-sm text-[#0F172A] italic">"{s.notes}"</div>
                  </div>
                )}

                {/* Screenshots */}
                {(s.screenshot_urls || []).length > 0 && (
                  <div>
                    <h4 className="text-xs uppercase tracking-widest text-[#94A3B8] font-bold mb-2">Screenshots ({s.screenshot_urls.length})</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {s.screenshot_urls.map((u, i) => (
                        <a key={i} href={u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u} target="_blank" rel="noreferrer" className="block">
                          <img src={u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u} alt={`Screenshot ${i + 1}`} className="w-full h-40 object-cover rounded border border-[#E2E8F0] hover:border-[#2563EB] transition-colors" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Status / meta */}
                <div className="text-xs text-[#94A3B8] pt-2 border-t border-[#E2E8F0]">
                  <span className="font-mono">id: {s.id}</span>
                  {s.submitted_at && <span className="ml-3">submitted: {new Date(s.submitted_at).toLocaleString()}</span>}
                  {s.tier && <span className="ml-3">tier: {s.tier}</span>}
                  {s.invoice_status && <span className="ml-3">invoice: {s.invoice_status}</span>}
                </div>
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
};



const NewsletterTab = () => {
  const [settings, setSettings] = useState({ smtp_host: "", smtp_port: 587, smtp_user: "", smtp_password: "", smtp_security: "starttls", from_email: "", from_name: "HorseAppCompare" });
  const [savedMsg, setSavedMsg] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [testMsg, setTestMsg] = useState("");
  const [subs, setSubs] = useState({ items: [], stats: { total: 0, active: 0, pending: 0 } });
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sendResult, setSendResult] = useState("");

  const loadAll = async () => {
    try {
      const [s, sb, h] = await Promise.all([adminGetEmailSettings(), adminListSubscribers(), adminNewsletterHistory()]);
      setSettings({ ...settings, ...s });
      setSubs(sb);
      setHistory(h.items || []);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { loadAll(); }, []);

  const save = async (e) => {
    e?.preventDefault();
    setSavedMsg("");
    try {
      await adminSetEmailSettings(settings);
      setSavedMsg("✅ Opgeslagen");
      setTimeout(() => setSavedMsg(""), 2500);
    } catch (err) { setSavedMsg("❌ " + (err?.response?.data?.detail || err.message)); }
  };

  const sendTest = async () => {
    setTestMsg("");
    if (!testEmail.includes("@")) { setTestMsg("Ongeldig adres"); return; }
    setBusy(true);
    try {
      await adminEmailTest(testEmail);
      setTestMsg(`✅ Test-mail verzonden naar ${testEmail}`);
    } catch (err) {
      setTestMsg("❌ " + (err?.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };

  const sendNow = async () => {
    if (!window.confirm("Newsletter direct verzenden naar alle actieve abonnees?")) return;
    setBusy(true); setSendResult("");
    try {
      const r = await adminNewsletterSendNow();
      setSendResult(`✅ Verzonden: ${r.sent}, fouten: ${r.errors}${r.skipped_reason ? ` (skip: ${r.skipped_reason})` : ""}`);
      loadAll();
    } catch (err) {
      setSendResult("❌ " + (err?.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="newsletter-tab">
      {/* SMTP settings */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-bold text-lg text-[#0F172A]">📧 SMTP-instellingen (verzending)</h3>
          {settings._source === "env" && <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider" title="Komt uit backend/.env — sla op om te overschrijven">🔧 via .env</span>}
          {settings._source === "admin" && <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">✅ via admin</span>}
        </div>
        {settings._source === "env" && (
          <p className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">💡 Deze instellingen komen uit <code className="font-mono bg-white px-1.5 py-0.5 rounded">backend/.env</code>. Pas ze daar aan voor permanente config, óf vul dit form in om de .env tijdelijk te overschrijven.</p>
        )}
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="overline block mb-1">SMTP host *</label><input className="hac-input" required value={settings.smtp_host} onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })} placeholder="smtp.gmail.com" data-testid="smtp-host" /></div>
          <div><label className="overline block mb-1">Poort *</label><input type="number" className="hac-input" required value={settings.smtp_port} onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 0 })} placeholder="587" data-testid="smtp-port" /></div>
          <div><label className="overline block mb-1">Gebruikersnaam (IMAP/SMTP login)</label><input className="hac-input" value={settings.smtp_user} onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })} placeholder="info@horseappcompare.com" data-testid="smtp-user" /></div>
          <div><label className="overline block mb-1">Wachtwoord (of app-password)</label><input type="password" className="hac-input" value={settings.smtp_password === "***SET***" ? "" : settings.smtp_password} onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })} placeholder={settings.smtp_password === "***SET***" ? "(opgeslagen — leeg laten om te behouden)" : "••••••"} data-testid="smtp-password" /></div>
          <div><label className="overline block mb-1">Beveiliging</label><select className="hac-input" value={settings.smtp_security} onChange={(e) => setSettings({ ...settings, smtp_security: e.target.value })} data-testid="smtp-security"><option value="starttls">STARTTLS (port 587)</option><option value="ssl">SSL/TLS (port 465)</option><option value="none">Geen</option></select></div>
          <div><label className="overline block mb-1">Afzender naam</label><input className="hac-input" value={settings.from_name} onChange={(e) => setSettings({ ...settings, from_name: e.target.value })} data-testid="smtp-fromname" /></div>
          <div className="md:col-span-2"><label className="overline block mb-1">Afzender e-mail *</label><input type="email" required className="hac-input" value={settings.from_email} onChange={(e) => setSettings({ ...settings, from_email: e.target.value })} placeholder="info@horseappcompare.com" data-testid="smtp-fromemail" /></div>

          {/* IMAP — optional, enables the 5-min inbox poller for two-way threads */}
          <div className="md:col-span-2 border-t border-[#E2E8F0] pt-4 mt-2">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#64748B] mb-2">IMAP inbox-poller (optioneel)</div>
            <p className="text-[11px] text-[#94A3B8] mb-3 leading-relaxed">Vul deze velden in om inkomende replies elke 5 min op te halen en automatisch onder het juiste bericht te plaatsen. Gebruik dezelfde mailbox als je SMTP afzender.</p>
          </div>
          <div><label className="overline block mb-1">IMAP host</label><input className="hac-input" value={settings.imap_host || ""} onChange={(e) => setSettings({ ...settings, imap_host: e.target.value })} placeholder="imap.gmail.com" data-testid="imap-host" /></div>
          <div><label className="overline block mb-1">IMAP poort</label><input type="number" className="hac-input" value={settings.imap_port || 993} onChange={(e) => setSettings({ ...settings, imap_port: parseInt(e.target.value || "993", 10) })} data-testid="imap-port" /></div>
          <div><label className="overline block mb-1">IMAP gebruiker</label><input className="hac-input" value={settings.imap_user || ""} onChange={(e) => setSettings({ ...settings, imap_user: e.target.value })} placeholder="info@horseappcompare.com" data-testid="imap-user" /></div>
          <div><label className="overline block mb-1">IMAP wachtwoord</label><input type="password" className="hac-input" value={settings.imap_password || ""} onChange={(e) => setSettings({ ...settings, imap_password: e.target.value })} placeholder="••••••" data-testid="imap-password" /></div>
          <div><label className="overline block mb-1">IMAP map</label><input className="hac-input" value={settings.imap_folder || "INBOX"} onChange={(e) => setSettings({ ...settings, imap_folder: e.target.value })} data-testid="imap-folder" /></div>
          <div className="flex items-end"><label className="inline-flex items-center gap-2 text-xs"><input type="checkbox" checked={settings.imap_ssl !== false} onChange={(e) => setSettings({ ...settings, imap_ssl: e.target.checked })} data-testid="imap-ssl" />Gebruik SSL/TLS</label></div>
          <div className="md:col-span-2 flex items-center gap-2 flex-wrap">
            <button type="button" onClick={async () => {
              try {
                const fd = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email/imap-test`, { method: "POST", credentials: "include", headers: { Authorization: `Bearer ${localStorage.getItem("hac_admin_token") || ""}` } });
                const j = await fd.json();
                alert(j.ok ? `IMAP OK — ${j.folder}: ${j.total} berichten, ${j.unread} ongelezen` : `IMAP fout: ${j.reason || "—"}`);
              } catch (e) { alert(`IMAP fout: ${e.message}`); }
            }} className="text-xs px-3 py-1.5 rounded border border-[#E2E8F0] text-[#64748B] hover:border-[#2563EB] hover:text-[#2563EB]" data-testid="imap-test">Test IMAP-connectie</button>
            <button type="button" onClick={async () => {
              try {
                const fd = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email/imap-poll-now`, { method: "POST", credentials: "include", headers: { Authorization: `Bearer ${localStorage.getItem("hac_admin_token") || ""}` } });
                const j = await fd.json();
                if (j.skipped) alert("IMAP nog niet geconfigureerd."); else alert(`Poll resultaat: ${j.fetched || 0} opgehaald, ${j.matched || 0} aan threads gekoppeld.`);
              } catch (e) { alert(`Poll fout: ${e.message}`); }
            }} className="text-xs px-3 py-1.5 rounded border border-[#E2E8F0] text-[#64748B] hover:border-[#2563EB] hover:text-[#2563EB]" data-testid="imap-poll-now">Poll nu</button>
          </div>

          <div className="md:col-span-2 flex items-center gap-3 pt-2"><button type="submit" className="bg-[#2563EB] text-white px-5 py-2.5 rounded-full text-sm font-medium" data-testid="smtp-save">Opslaan</button>{savedMsg && <span className="text-sm">{savedMsg}</span>}</div>
        </form>
      </div>

      {/* Test send */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
        <h3 className="font-bold text-lg text-[#0F172A] mb-3">🧪 Test verzending</h3>
        <div className="flex gap-2 items-center">
          <input type="email" className="hac-input flex-1" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@email.com" data-testid="test-email-input" />
          <button onClick={sendTest} disabled={busy} className="bg-[#0F172A] text-white px-5 py-2.5 rounded-full text-sm disabled:opacity-60" data-testid="test-email-send">{busy ? "..." : "Verzend test"}</button>
        </div>
        {testMsg && <p className="text-sm mt-2">{testMsg}</p>}
      </div>

      {/* Subscribers */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-[#0F172A]">📬 Abonnees</h3>
          <div className="text-xs text-[#64748B]">Totaal: <strong>{subs.stats.total}</strong> · Actief: <strong className="text-emerald-600">{subs.stats.active}</strong> · Pending: <strong className="text-amber-600">{subs.stats.pending}</strong></div>
        </div>
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase text-[#94A3B8] border-b border-[#E2E8F0]"><tr><th className="text-left py-2">E-mail</th><th>Status</th><th>Taal</th><th>Bron</th><th>Ingeschreven</th></tr></thead>
            <tbody>
              {subs.items.map((s, i) => (
                <tr key={i} className="border-b border-[#F1F5F9]" data-testid={`subscriber-${i}`}>
                  <td className="py-2 text-[#0F172A]">{s.email}</td>
                  <td className="text-center">{s.status === "active" ? "✅" : s.status === "pending" ? "⏳" : "❌"}</td>
                  <td className="text-center text-xs">{(s.language || "en").toUpperCase()}</td>
                  <td className="text-center text-xs text-[#64748B]">{s.source}</td>
                  <td className="text-center text-xs text-[#64748B]">{(s.subscribed_at || "").slice(0, 10)}</td>
                </tr>
              ))}
              {subs.items.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-[#94A3B8]">Nog geen abonnees</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Send now + history */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
        <h3 className="font-bold text-lg text-[#0F172A] mb-3">🚀 Wekelijkse nieuwsbrief</h3>
        <p className="text-sm text-[#64748B] mb-3">Automatisch elke maandag om 10:00 UTC. Of nu handmatig verzenden:</p>
        <button onClick={sendNow} disabled={busy} className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 rounded-full text-sm disabled:opacity-60" data-testid="newsletter-sendnow">🔥 Verzend nu</button>
        {sendResult && <p className="text-sm mt-3">{sendResult}</p>}
        {history.length > 0 && (
          <div className="mt-5 border-t border-[#E2E8F0] pt-4">
            <h4 className="text-xs uppercase text-[#94A3B8] mb-2">Verzend-historie</h4>
            <ul className="space-y-1 text-xs">
              {history.map((h, i) => <li key={i} className="text-[#64748B]">{(h.sent_at || "").slice(0, 16).replace("T", " ")} UTC — <strong className="text-[#0F172A]">{h.sent}</strong> verzonden, {h.errors} fouten ({h.items_count} items)</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

const ClaimsTab = () => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try { const d = await adminListClaims(); setItems(d.items || []); } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const decide = async (claim, decision) => {
    const note = decision === "reject" ? (window.prompt("Reden voor afwijzing (optioneel):") || "") : "";
    if (decision === "approve" && !window.confirm(`Goedkeuren claim van ${claim.email} voor ${claim.app_name}?`)) return;
    setBusy(claim.id); setMsg("");
    try {
      await adminDecideClaim(claim.id, decision, note);
      setMsg(`✅ ${decision === "approve" ? "Goedgekeurd" : "Afgewezen"}: ${claim.app_name}`);
      load();
    } catch (e) { setMsg("❌ " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(null); }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6" data-testid="claims-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-lg text-[#0F172A]">🛡️ Publisher Claims</h3>
        <span className="text-xs text-[#64748B]">Totaal: <strong>{items.length}</strong></span>
      </div>
      {msg && <p className="text-sm mb-3">{msg}</p>}
      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {items.length === 0 && <p className="text-[#94A3B8] text-sm py-6 text-center">Geen claims</p>}
        {items.map((c) => {
          const isPending = c.status === "pending_admin_review" || c.status === "pending_email_verification";
          const badge = c.status === "verified" ? "bg-emerald-100 text-emerald-700" :
            c.status === "rejected" ? "bg-red-100 text-red-700" :
            c.status === "pending_email_verification" ? "bg-blue-100 text-blue-700" :
            "bg-amber-100 text-amber-700";
          return (
            <div key={c.id} className="border border-[#E2E8F0] rounded-xl p-4" data-testid={`claim-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <strong className="text-[#0F172A]">{c.app_name}</strong>
                    <span className="text-xs text-[#94A3B8]">/{c.app_slug}</span>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${badge}`}>{c.status.replace(/_/g, " ")}</span>
                    {c.domain_match && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">✓ domain match</span>}
                  </div>
                  <p className="text-sm text-[#0F172A]">{c.email} <span className="text-xs text-[#64748B]">· {c.relationship}</span></p>
                  {c.note && <p className="text-sm text-[#64748B] mt-1 italic">"{c.note}"</p>}
                  <p className="text-[11px] text-[#94A3B8] mt-1">{(c.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
                {isPending && (
                  <div className="flex gap-2 shrink-0">
                    <button disabled={busy === c.id} onClick={() => decide(c, "approve")} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-3 py-1.5 rounded-full text-xs" data-testid={`claim-approve-${c.id}`}>✓ Goedkeuren</button>
                    <button disabled={busy === c.id} onClick={() => decide(c, "reject")} className="bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-3 py-1.5 rounded-full text-xs" data-testid={`claim-reject-${c.id}`}>✗ Afwijzen</button>
                  </div>
                )}
              </div>
              {c.admin_note && <p className="text-xs text-[#64748B] bg-slate-50 rounded px-2 py-1 mt-2">Admin: {c.admin_note}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
};


const DiscountsTab = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ code: "", percent_off: 10, description: "", active: true, max_uses: 0, expires_at: "" });
  const [msg, setMsg] = useState("");

  const load = async () => {
    try { const d = await adminListDiscounts(); setItems(d.items || []); } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e?.preventDefault();
    setMsg("");
    try {
      await adminCreateDiscount({
        code: form.code.trim().toUpperCase(),
        percent_off: Math.max(0, Math.min(100, parseInt(form.percent_off) || 0)),
        description: form.description,
        active: !!form.active,
        max_uses: parseInt(form.max_uses) || 0,
        expires_at: form.expires_at || null,
      });
      setMsg(`✅ ${form.code.toUpperCase()} opgeslagen`);
      setForm({ code: "", percent_off: 10, description: "", active: true, max_uses: 0, expires_at: "" });
      load();
    } catch (err) { setMsg("❌ " + (err?.response?.data?.detail || err.message)); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Verwijder code ${code}?`)) return;
    try { await adminDeleteDiscount(code); load(); } catch (_) { /* noop */ }
  };

  return (
    <div className="space-y-6" data-testid="discounts-tab">
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
        <h3 className="font-bold text-lg text-[#0F172A] mb-4">🎟️ Nieuwe kortingscode</h3>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="overline block mb-1">Code *</label><input required className="hac-input uppercase font-mono" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().slice(0, 32) })} placeholder="WELCOME10" data-testid="discount-form-code" /></div>
          <div><label className="overline block mb-1">% korting *</label><input required type="number" min="0" max="100" className="hac-input" value={form.percent_off} onChange={(e) => setForm({ ...form, percent_off: e.target.value })} data-testid="discount-form-percent" /></div>
          <div className="md:col-span-2"><label className="overline block mb-1">Beschrijving (intern + bij valid op submit-app pagina zichtbaar)</label><input className="hac-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Nieuwsbrief korting januari 2026" data-testid="discount-form-description" /></div>
          <div><label className="overline block mb-1">Max gebruik (0 = onbeperkt)</label><input type="number" min="0" className="hac-input" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="discount-form-maxuses" /></div>
          <div><label className="overline block mb-1">Vervaldatum (optioneel)</label><input type="date" className="hac-input" value={form.expires_at ? form.expires_at.slice(0, 10) : ""} onChange={(e) => setForm({ ...form, expires_at: e.target.value ? `${e.target.value}T23:59:59+00:00` : "" })} data-testid="discount-form-expires" /></div>
          <div className="md:col-span-2 flex items-center gap-3">
            <label className="text-sm inline-flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="discount-form-active" /> Actief</label>
            <button type="submit" className="bg-[#2563EB] text-white px-5 py-2.5 rounded-full text-sm font-medium ml-auto" data-testid="discount-form-save">Opslaan</button>
          </div>
        </form>
        {msg && <p className="text-sm mt-3">{msg}</p>}
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
        <h3 className="font-bold text-lg text-[#0F172A] mb-3">Bestaande codes ({items.length})</h3>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase text-[#94A3B8] border-b border-[#E2E8F0]"><tr><th className="text-left py-2">Code</th><th>% korting</th><th>Gebruik</th><th>Actief</th><th>Vervalt</th><th>Beschrijving</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-[#94A3B8]">Nog geen codes</td></tr>}
            {items.map((c) => (
              <tr key={c.code} className="border-b border-[#F1F5F9]" data-testid={`discount-row-${c.code}`}>
                <td className="py-2 font-mono font-bold text-[#0F172A]">{c.code}</td>
                <td className="text-center"><strong>{c.percent_off}%</strong></td>
                <td className="text-center text-xs">{c.uses || 0}{c.max_uses ? `/${c.max_uses}` : ""}</td>
                <td className="text-center">{c.active ? "✅" : "❌"}</td>
                <td className="text-center text-xs text-[#64748B]">{c.expires_at ? c.expires_at.slice(0, 10) : "—"}</td>
                <td className="text-xs text-[#64748B]">{c.description || "—"}</td>
                <td className="text-right"><button onClick={() => remove(c.code)} className="text-xs text-red-600 hover:underline" data-testid={`discount-delete-${c.code}`}>Verwijderen</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AiAdStudio = ({ onPick }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    app_name: "",
    tagline: "",
    audience: "horse owners, riders, trainers",
    brand_color: "#2563EB",
    format: "banner",
    count: 3,
    extra: "",
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const run = async () => {
    setError(""); setResult(null);
    if (!form.app_name || !form.tagline) { setError("App name + tagline required"); return; }
    setBusy(true);
    try {
      const data = await adminAiGenerateAds(form);
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-5 border border-violet-200 rounded-xl overflow-hidden bg-gradient-to-br from-violet-50 to-indigo-50" data-testid="ai-ad-studio">
      <button type="button" onClick={() => setOpen(!open)} className="w-full px-4 py-3 flex items-center justify-between text-left text-sm font-bold text-violet-700 hover:bg-violet-100/40">
        <span className="inline-flex items-center gap-2"><Sparkles size={14} /> AI Creative Studio</span>
        <ChevronRight size={14} className={`transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="p-4 space-y-2 border-t border-violet-200 bg-white/50">
          <p className="text-xs text-[#64748B] mb-1">Generate 1-5 ad creatives via Gemini Nano Banana. Pick a variant to auto-fill the image URL.</p>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="overline block mb-1">App name *</label><input className="hac-input" value={form.app_name} onChange={(e) => setForm({ ...form, app_name: e.target.value })} data-testid="ai-app-name" /></div>
            <div><label className="overline block mb-1">Tagline *</label><input className="hac-input" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="ai-tagline" /></div>
          </div>
          <div><label className="overline block mb-1">Audience</label><input className="hac-input" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} data-testid="ai-audience" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><label className="overline block mb-1">Brand color</label><input type="color" className="hac-input p-1 h-9" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} data-testid="ai-color" /></div>
            <div><label className="overline block mb-1">Format</label><select className="hac-input" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} data-testid="ai-format"><option value="banner">Banner 3:1</option><option value="square">Square 1:1</option></select></div>
            <div><label className="overline block mb-1">Variants</label><select className="hac-input" value={form.count} onChange={(e) => setForm({ ...form, count: parseInt(e.target.value) })} data-testid="ai-count">{[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}</select></div>
          </div>
          <div><label className="overline block mb-1">Extra direction (optional)</label><input className="hac-input" value={form.extra} onChange={(e) => setForm({ ...form, extra: e.target.value })} placeholder="e.g. dressage focus, warm sunset light" data-testid="ai-extra" /></div>
          <button type="button" onClick={run} disabled={busy} className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm px-4 py-2 rounded-full inline-flex items-center gap-1.5" data-testid="ai-generate-btn">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {busy ? "Generating (30-60s)…" : "Generate creatives"}
          </button>
          {error && <div className="text-xs text-red-600 mt-1" data-testid="ai-error">{error}</div>}
          {result && (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {result.creatives?.map((c, i) => (
                  <div key={i} className="border rounded-lg overflow-hidden bg-white" data-testid={`ai-variant-${i}`}>
                    {c.error ? (
                      <div className="p-3 text-xs text-red-600">{c.error}</div>
                    ) : (
                      <>
                        <img src={c.url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${c.url}` : c.url} alt={`variant ${i + 1}`} className="w-full h-32 object-cover" />
                        <button type="button" onClick={() => onPick(c.url)} className="w-full text-xs text-violet-700 hover:bg-violet-50 py-1.5" data-testid={`ai-pick-${i}`}>Use this →</button>
                      </>
                    )}
                  </div>
                ))}
              </div>
              {(result.headlines || []).length > 0 && (
                <div className="bg-white border border-violet-200 rounded-lg p-2 text-xs">
                  <div className="font-bold text-violet-700 mb-1.5">Suggested headlines (EN / NL)</div>
                  <ul className="space-y-1">
                    {result.headlines.map((h, i) => (
                      <li key={i} className="border-b last:border-0 border-violet-100 pb-1 last:pb-0"><strong>{h.headline_en}</strong> — {h.subtitle_en}<br /><span className="text-[#64748B]"><strong>{h.headline_nl}</strong> — {h.subtitle_nl}</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};




const ReviewsModerationTab = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");  // "", "approved", "hidden", "pending_review"
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    adminListReviews(filter || undefined).then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, status) => {
    try { await adminSetReviewStatus(id, status); refresh(); } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Permanently delete this review and its replies? This cannot be undone.")) return;
    try { await adminDeleteReview(id); refresh(); } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-bold text-lg text-[#0F172A]">Reviews moderation</h2>
          <p className="text-xs text-[#64748B]">{items.length} review{items.length === 1 ? "" : "s"}{filter ? ` · ${filter}` : ""}</p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {[{ k: "", label: "All" }, { k: "approved", label: "Approved" }, { k: "pending_review", label: "Pending" }, { k: "hidden", label: "Hidden" }].map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)} className={`text-xs px-3 py-1 rounded-full border ${filter === f.k ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-[#E2E8F0] text-[#64748B] hover:border-[#0F172A]"}`} data-testid={`reviews-filter-${f.k || "all"}`}>{f.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-[#94A3B8]"><Loader2 size={20} className="animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-[#94A3B8] italic">No reviews{filter ? ` with status "${filter}"` : ""}.</p>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className={`border rounded-lg p-4 ${r.status === "hidden" ? "border-red-200 bg-red-50/30" : r.status === "pending_review" ? "border-amber-300 bg-amber-50/30" : "border-[#E2E8F0]"}`} data-testid={`admin-review-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <div className="flex">{[1,2,3,4,5].map((n) => <Star key={n} size={12} className={n <= r.rating ? "fill-amber-500 text-amber-500" : "text-[#CBD5E1]"} />)}</div>
                    <span className="text-xs font-bold text-[#0F172A]">{r.author_name}</span>
                    <span className="text-[10px] text-[#94A3B8]">{r.author_email}</span>
                    <span className="text-[10px] text-[#94A3B8]">· {r.slug}</span>
                    <span className="text-[10px] text-[#94A3B8]">· {new Date(r.created_at).toLocaleDateString()}</span>
                    <span className={`text-[10px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded ${r.status === "approved" ? "bg-emerald-100 text-emerald-700" : r.status === "hidden" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{r.status}</span>
                    {r.flag_count > 0 && <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">🚩 {r.flag_count}</span>}
                  </div>
                  {r.title && <h3 className="font-bold text-[#0F172A] text-sm">{r.title}</h3>}
                  <p className="text-sm text-[#0F172A] mt-1 whitespace-pre-wrap break-words">{r.body}</p>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {r.status !== "approved" && (
                    <button onClick={() => setStatus(r.id, "approved")} className="text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded inline-flex items-center gap-1" data-testid={`admin-review-approve-${r.id}`}><Check size={10} /> Approve</button>
                  )}
                  {r.status !== "hidden" && (
                    <button onClick={() => setStatus(r.id, "hidden")} className="text-[11px] bg-amber-600 hover:bg-amber-700 text-white px-2 py-1 rounded inline-flex items-center gap-1" data-testid={`admin-review-hide-${r.id}`}><ShieldOff size={10} /> Hide</button>
                  )}
                  <button onClick={() => remove(r.id)} className="text-[11px] bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded inline-flex items-center gap-1" data-testid={`admin-review-delete-${r.id}`}><Trash2 size={10} /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdvertQueueTab = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const refresh = () => {
    setLoading(true);
    setError(null);
    adminListAdvertSubmissions(filter || undefined)
      .then(setItems)
      .catch((e) => {
        const msg = e?.response?.data?.detail || e?.message || "Unknown error";
        const status = e?.response?.status;
        setError(`API ${status || "?"} — ${msg}`);
        setItems([]);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (id) => {
    setBusy(id);
    try { await adminApproveAdvert(id); refresh(); } catch (e) { alert(e?.response?.data?.detail || "Failed"); } finally { setBusy(null); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Reject reason (visible to advertiser)?", "");
    if (reason === null) return;
    setBusy(id);
    try { await adminRejectAdvert(id, reason); refresh(); } catch (e) { alert(e?.response?.data?.detail || "Failed"); } finally { setBusy(null); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try { await adminDeleteAdvertSubmission(id); refresh(); } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-bold text-lg text-[#0F172A]">Advert queue</h2>
          <p className="text-xs text-[#64748B]">{items.length} submission{items.length === 1 ? "" : "s"} · {filter || "all"}</p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {[{ k: "pending", label: "Pending" }, { k: "approved", label: "Approved" }, { k: "rejected", label: "Rejected" }, { k: "", label: "All" }].map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)} className={`text-xs px-3 py-1 rounded-full border ${filter === f.k ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-[#E2E8F0] text-[#64748B] hover:border-[#0F172A]"}`} data-testid={`advert-filter-${f.k || "all"}`}>{f.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-[#94A3B8]"><Loader2 size={20} className="animate-spin inline" /></div>
      ) : error ? (
        <div className="border border-red-300 bg-red-50 rounded-lg p-4 text-sm text-red-800" data-testid="advert-queue-error">
          <strong>Could not load advert queue.</strong>
          <div className="text-xs mt-1 font-mono break-all">{error}</div>
          <button onClick={refresh} className="mt-2 text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded">Retry</button>
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-[#94A3B8] italic">No submissions{filter ? ` with status "${filter}"` : ""}.</p>
      ) : (
        <div className="space-y-4">
          {items.map((s) => (
            <div key={s.id} className={`border rounded-xl p-4 ${s.status === "pending" ? "border-amber-300 bg-amber-50/30" : s.status === "approved" ? "border-emerald-300 bg-emerald-50/30" : "border-red-200 bg-red-50/30"}`} data-testid={`advert-sub-${s.id}`}>
              <div className="grid md:grid-cols-[200px_1fr_auto] gap-4 items-start">
                <img src={s.image_url && s.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${s.image_url}` : s.image_url} alt="ad" className="w-full max-w-[200px] rounded-lg border border-[#E2E8F0]" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-[10px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded ${s.status === "approved" ? "bg-emerald-100 text-emerald-700" : s.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{s.status}</span>
                    <span className="text-[10px] bg-[#F1F5F9] text-[#64748B] px-1.5 py-0.5 rounded uppercase font-mono">{s.source}</span>
                    <span className="text-[10px] bg-[#F1F5F9] text-[#64748B] px-1.5 py-0.5 rounded uppercase font-mono">{s.type || s.format}</span>
                    {(s.placements || []).map((p) => <span key={p} className="text-[10px] bg-[#EFF6FF] text-[#2563EB] px-1.5 py-0.5 rounded font-mono">{p}</span>)}
                    {s.discount_code && s.discount_valid && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">🎟️ {s.discount_code} -{s.discount_percent_off}%</span>}
                  </div>
                  <h3 className="font-bold text-[#0F172A]">{s.title}</h3>
                  {s.subtitle && <p className="text-sm text-[#64748B]">{s.subtitle}</p>}
                  <a href={s.target_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#2563EB] hover:underline break-all">{s.target_url}</a>
                  <div className="mt-2 text-xs text-[#0F172A] grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-0.5">
                    <div><strong>{s.contact_name}</strong> · {s.contact_email}</div>
                    <div>{s.company}{s.vat_number ? ` · BTW ${s.vat_number}` : ""}</div>
                    <div className="text-[#64748B]">{s.address}{s.country ? ` · ${s.country}` : ""}</div>
                    <div className="text-[#64748B]">📧 Factuur: {s.billing_email}</div>
                  </div>
                  {s.notes && <p className="text-xs text-[#64748B] italic mt-1">"{s.notes}"</p>}
                  {s.reject_reason && <p className="text-xs text-red-700 mt-1"><strong>Reject reason:</strong> {s.reject_reason}</p>}
                  <p className="text-[10px] text-[#94A3B8] mt-1">{new Date(s.created_at).toLocaleString()}</p>
                </div>
                <div className="flex flex-col gap-1">
                  {s.status === "pending" && (
                    <>
                      <button onClick={() => approve(s.id)} disabled={busy === s.id} className="text-[11px] bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-3 py-1.5 rounded inline-flex items-center gap-1" data-testid={`advert-approve-${s.id}`}>{busy === s.id ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} Approve & publish</button>
                      <button onClick={() => reject(s.id)} disabled={busy === s.id} className="text-[11px] bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-3 py-1.5 rounded inline-flex items-center gap-1" data-testid={`advert-reject-${s.id}`}><X size={11} /> Reject</button>
                    </>
                  )}
                  <button onClick={() => remove(s.id)} className="text-[11px] text-[#94A3B8] hover:text-red-600 inline-flex items-center gap-1" data-testid={`advert-delete-${s.id}`}><Trash2 size={11} /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};



// ============== Backup Tab ==============

const _formatBytes = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const _formatRelative = (iso, lang = "en") => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(lang === "nl" ? "nl-NL" : "en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const BackupTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [runningWeekly, setRunningWeekly] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [retention, setRetention] = useState(10);
  const [showRestore, setShowRestore] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const reload = () => {
    setLoading(true);
    return adminListBackups()
      .then((d) => {
        setItems(d.items || []);
        setRetention(d.retention || 10);
      })
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    adminRestoreHistory().then((d) => setHistory(d.items || [])).catch(() => {});
  }, []);

  const createNow = async () => {
    if (creating) return;
    setError("");
    setNotice("");
    setCreating(true);
    try {
      const d = await adminCreateBackup();
      // Trigger download immediately of the freshly-uploaded ZIP
      const a = document.createElement("a");
      a.href = d.url;
      a.download = d.filename || "backup.zip";
      a.rel = "noopener";
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      await reload();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Backup failed");
    } finally {
      setCreating(false);
    }
  };

  const runWeekly = async () => {
    if (runningWeekly) return;
    setError("");
    setNotice("");
    setRunningWeekly(true);
    try {
      const d = await adminRunWeeklyBackup();
      const msg = d.email_dispatched
        ? `Weekly backup created and notification email sent to ${d.email_recipient || "admin"} (${d.filename}).`
        : `Weekly backup created (${d.filename}). Email skipped — SMTP not configured. The backup is downloadable below.`;
      setNotice(msg);
      await reload();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Weekly run failed");
    } finally {
      setRunningWeekly(false);
    }
  };

  const removeOne = async (id) => {
    if (!window.confirm("Permanently delete this backup from R2? This cannot be undone.")) return;
    try {
      await adminDeleteBackup(id);
      await reload();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-backup-tab">
      <SeoBoostPanel />
      <SmtpSmokeTestPanel />
      <PublisherDigestPanel />
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div>
            <h2 className="font-heading text-2xl text-[#0F172A]">Database backup</h2>
            <p className="text-sm text-[#64748B] mt-1 max-w-2xl">
              Creates a ZIP file containing one JSON-lines file per MongoDB collection
              plus a <code className="text-[12px] bg-[#F1F5F9] px-1 rounded">manifest.json</code>.
              The last {retention} backups are kept on Cloudflare R2 — older ones are
              auto-pruned. A scheduled backup runs every <strong>Monday 02:00 UTC</strong> and
              emails the admin with the download link.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <button
              type="button"
              onClick={createNow}
              disabled={creating || runningWeekly}
              data-testid="admin-backup-create-btn"
              className="inline-flex items-center gap-2 bg-[#0F172A] text-white font-medium rounded-full px-5 py-2.5 disabled:opacity-60 disabled:cursor-not-allowed hover:bg-[#1E293B] transition-colors whitespace-nowrap"
            >
              {creating ? <Loader2 size={16} className="animate-spin" /> : <Package size={16} />}
              {creating ? "Building backup…" : "Create + download backup"}
            </button>
            <button
              type="button"
              onClick={runWeekly}
              disabled={creating || runningWeekly}
              data-testid="admin-backup-run-weekly-btn"
              className="inline-flex items-center gap-2 border border-[#E2E8F0] text-[#0F172A] font-medium rounded-full px-5 py-2.5 disabled:opacity-60 disabled:cursor-not-allowed hover:border-[#0F172A] hover:bg-[#F8FAFC] transition-colors whitespace-nowrap"
              title="Run the weekly job manually (creates a scheduled-source backup + emails the admin)"
            >
              {runningWeekly ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
              {runningWeekly ? "Running…" : "Run weekly job now"}
            </button>
            <button
              type="button"
              onClick={() => setShowRestore(true)}
              disabled={creating || runningWeekly || items.length === 0}
              data-testid="admin-backup-restore-btn"
              className="inline-flex items-center gap-2 border border-amber-300 text-amber-700 font-medium rounded-full px-5 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-amber-50 transition-colors whitespace-nowrap"
              title="Restore data from a backup (opens wizard with dry-run + confirmation)"
            >
              <RotateCcw size={16} />
              Restore…
            </button>
          </div>
        </div>
        {notice && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded-lg px-3 py-2 mb-2" data-testid="admin-backup-notice">
            {notice}
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2" data-testid="admin-backup-error">
            {error}
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
          <h3 className="font-heading text-lg text-[#0F172A]">Recent backups</h3>
          <span className="text-xs text-[#94A3B8]">retention: {retention}</span>
        </div>
        {loading ? (
          <div className="px-6 py-12 text-center text-[#94A3B8] text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-6 py-12 text-center text-[#94A3B8] text-sm" data-testid="admin-backup-empty">
            No backups yet — click <strong>Create + download backup</strong> to make the first one.
          </div>
        ) : (
          <ul className="divide-y divide-[#E2E8F0]" data-testid="admin-backup-list">
            {items.map((b) => (
              <li key={b.id} className="px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid={`admin-backup-row-${b.id}`}>
                <div className="min-w-0">
                  <div className="font-mono text-sm text-[#0F172A] truncate flex items-center gap-2" title={b.filename}>
                    <span className="truncate">{b.filename}</span>
                    {b.source === "scheduled" && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full shrink-0" data-testid={`admin-backup-source-${b.id}`}>
                        <Mail size={9} /> Auto
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#94A3B8] mt-0.5">
                    {_formatRelative(b.created_at)} · {_formatBytes(b.size_bytes)} · {b.total_records ?? 0} records
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={b.url}
                    download={b.filename}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`admin-backup-download-${b.id}`}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#EFF6FF] text-[#2563EB] border border-[#2563EB] rounded-full hover:bg-[#2563EB] hover:text-white transition-colors"
                  >
                    <ExternalLink size={12} /> Download
                  </a>
                  <button
                    type="button"
                    onClick={() => removeOne(b.id)}
                    data-testid={`admin-backup-delete-${b.id}`}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 text-[#94A3B8] hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ===== Restore history accordion ===== */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden" data-testid="admin-restore-history">
        <button
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-[#F8FAFC] transition-colors"
        >
          <div className="flex items-center gap-2">
            <RotateCcw size={16} className="text-amber-700" />
            <h3 className="font-heading text-lg text-[#0F172A]">Restore history</h3>
            <span className="text-xs text-[#94A3B8]">({history.length})</span>
          </div>
          <ChevronDown size={18} className={`text-[#94A3B8] transition-transform ${historyOpen ? "rotate-180" : ""}`} />
        </button>
        {historyOpen && (
          <div className="border-t border-[#E2E8F0]">
            {history.length === 0 ? (
              <div className="px-6 py-8 text-center text-[#94A3B8] text-sm">No restores yet.</div>
            ) : (
              <ul className="divide-y divide-[#E2E8F0]">
                {history.map((h) => (
                  <li key={h.id} className="px-6 py-3 text-sm" data-testid={`admin-restore-history-${h.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-mono text-xs text-[#0F172A] truncate">{h.source}</div>
                        <div className="text-xs text-[#94A3B8] mt-0.5">
                          {_formatRelative(h.started_at)} · by {h.admin_email}
                        </div>
                      </div>
                      <div className="text-xs text-[#64748B] tabular-nums whitespace-nowrap">
                        <span className="text-emerald-700">+{h.totals?.inserted ?? 0}</span>{" "}
                        <span className="text-blue-700">~{h.totals?.updated ?? 0}</span>{" "}
                        <span className="text-red-700">−{h.totals?.deleted ?? 0}</span>
                        {h.totals?.errors > 0 && <span className="text-red-600 ml-2">{h.totals.errors} error(s)</span>}
                      </div>
                    </div>
                    {h.pre_restore_backup_url && (
                      <a
                        href={h.pre_restore_backup_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-amber-700 hover:underline mt-1 inline-block"
                      >
                        ↩ Download rollback backup
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <RestoreWizard
        open={showRestore}
        onClose={() => setShowRestore(false)}
        onCompleted={(result) => {
          setNotice(`Restore complete — ${result.totals.inserted} inserted, ${result.totals.updated} updated${result.totals.deleted ? `, ${result.totals.deleted} deleted` : ""}. Pre-restore backup saved.`);
          reload();
          adminRestoreHistory().then((d) => setHistory(d.items || [])).catch(() => {});
        }}
        backups={items}
      />
    </div>
  );
};


// ---- Data integrity tab — scan all collections for unserialisable docs ----
const DataIntegrityTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState(null);

  const scan = () => {
    setLoading(true);
    setError(null);
    adminDataIntegrity()
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Scan failed"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { scan(); }, []);

  const deleteRecord = async (collection, recordId) => {
    if (!window.confirm(`Permanently delete record ${recordId} from ${collection}?`)) return;
    setBusy(`${collection}:${recordId}`);
    try {
      await adminDataIntegrityDelete(collection, recordId);
      scan();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6" data-testid="data-integrity-tab">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-bold text-lg text-[#0F172A]">Data integrity scanner</h2>
          <p className="text-xs text-[#64748B]">
            Scans every key collection for documents that fail JSON serialisation (legacy BSON types, malformed values). Broken records are silently skipped by listing endpoints — fix or delete them here.
          </p>
        </div>
        <button
          onClick={scan}
          disabled={loading}
          className="text-xs bg-[#0F172A] hover:bg-[#1E293B] disabled:opacity-50 text-white px-4 py-2 rounded-full inline-flex items-center gap-1.5"
          data-testid="data-integrity-rescan"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
          Rescan
        </button>
      </div>

      {error && (
        <div className="border border-red-300 bg-red-50 rounded-lg p-4 text-sm text-red-800 mb-4">
          <strong>Scan failed.</strong>
          <div className="text-xs mt-1 font-mono break-all">{error}</div>
        </div>
      )}

      {loading && !data && (
        <div className="text-center py-12 text-[#94A3B8]">
          <Loader2 size={24} className="animate-spin inline" />
          <div className="text-xs mt-2">Scanning collections…</div>
        </div>
      )}

      {data && (
        <>
          <div className={`mb-5 rounded-xl p-4 border ${data.total_broken > 0 ? "border-red-300 bg-red-50" : "border-emerald-300 bg-emerald-50"}`} data-testid="data-integrity-summary">
            <div className="flex items-center gap-3">
              {data.total_broken > 0 ? (
                <span className="text-2xl">⚠️</span>
              ) : (
                <CheckCircle2 size={28} className="text-emerald-600" />
              )}
              <div>
                <div className="font-bold text-[#0F172A]">
                  {data.total_broken > 0
                    ? `${data.total_broken} broken record${data.total_broken === 1 ? "" : "s"} found`
                    : "All collections healthy"}
                </div>
                <div className="text-xs text-[#64748B]">
                  Scanned {data.total_records} records across {data.collections.length} collections · {new Date(data.scanned_at).toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            {data.collections.map((c) => {
              const hasBroken = c.broken > 0;
              const isOpen = expanded[c.collection];
              return (
                <div
                  key={c.collection}
                  className={`border rounded-lg ${hasBroken ? "border-red-300 bg-red-50/30" : "border-[#E2E8F0]"}`}
                  data-testid={`data-integrity-row-${c.collection}`}
                >
                  <button
                    onClick={() => hasBroken && setExpanded({ ...expanded, [c.collection]: !isOpen })}
                    disabled={!hasBroken}
                    className={`w-full px-4 py-3 flex items-center justify-between gap-3 text-left ${hasBroken ? "cursor-pointer hover:bg-red-50/50" : "cursor-default"}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {hasBroken ? (
                        <span className="text-red-600 text-lg">●</span>
                      ) : (
                        <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="font-bold text-sm text-[#0F172A]">{c.label}</div>
                        <div className="text-[10px] text-[#94A3B8] font-mono">{c.collection}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 text-xs">
                      <span className="text-[#64748B]">Total <strong className="text-[#0F172A] font-mono">{c.total}</strong></span>
                      <span className="text-emerald-700">Healthy <strong className="font-mono">{c.healthy}</strong></span>
                      <span className={hasBroken ? "text-red-700" : "text-[#94A3B8]"}>
                        Broken <strong className="font-mono">{c.broken}</strong>
                      </span>
                      {hasBroken && <ChevronDown size={14} className={`text-[#64748B] transition-transform ${isOpen ? "rotate-180" : ""}`} />}
                    </div>
                  </button>
                  {hasBroken && isOpen && (
                    <div className="px-4 pb-3 border-t border-red-200 bg-white">
                      <div className="text-[11px] text-[#64748B] py-2 italic">
                        Showing first {c.broken_ids.length} of {c.broken} broken records. Delete carefully — these are skipped by the UI but still occupy DB space.
                      </div>
                      <div className="space-y-1.5">
                        {c.broken_ids.map((b) => {
                          const key = `${c.collection}:${b._id}`;
                          return (
                            <div
                              key={b._id}
                              className="flex items-start gap-2 text-[11px] font-mono bg-[#F8FAFC] border border-[#E2E8F0] rounded px-2.5 py-1.5"
                              data-testid={`broken-record-${b._id}`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="text-[#0F172A] break-all"><strong>_id:</strong> {b._id}{b.id && <> · <strong>id:</strong> {b.id}</>}</div>
                                <div className="text-red-700 text-[10px] mt-0.5 break-all">{b.error}</div>
                              </div>
                              <button
                                onClick={() => deleteRecord(c.collection, b._id)}
                                disabled={busy === key}
                                className="text-[10px] text-red-600 hover:bg-red-100 disabled:opacity-50 px-2 py-1 rounded inline-flex items-center gap-1 shrink-0"
                                data-testid={`delete-broken-${b._id}`}
                              >
                                {busy === key ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />}
                                Delete
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// ---- Auto SEO Boost panel — shown above Backup section ----
const SeoBoostPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");

  const refresh = () => {
    setLoading(true);
    adminSeoBoostStatus()
      .then(setStatus)
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  const runNow = async () => {
    if (running) return;
    setRunning(true);
    setError("");
    try {
      const r = await adminSeoBoostRunNow();
      alert(`✓ SEO Boost sent\n\n${r.url_count} URLs pinged to IndexNow.\nBing & Yandex will index within minutes.`);
      refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  const last = status?.last_run;
  const lastWhen = last?.ts ? new Date(last.ts).toLocaleString() : "Never";
  const nextWhen = status?.next_run ? new Date(status.next_run).toLocaleString() : "—";

  return (
    <div className="bg-gradient-to-br from-[#EFF6FF] to-white border border-[#DBEAFE] rounded-2xl p-6 md:p-8" data-testid="admin-seo-boost-panel">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 shrink-0 bg-[#2563EB] text-white rounded-xl flex items-center justify-center">
            <Rocket size={22} />
          </div>
          <div className="min-w-0">
            <h2 className="font-heading text-xl text-[#0F172A]">Auto SEO Boost</h2>
            <p className="text-sm text-[#64748B] mt-1 max-w-2xl leading-relaxed">
              Pings IndexNow with every active app + key pages so <strong>Bing &amp; Yandex</strong> re-index within minutes.
              Runs automatically <strong>every day at 03:00 UTC</strong> + on every app create/update.
            </p>
            <p className="text-[11px] text-[#64748B] mt-2 max-w-2xl leading-relaxed bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
              <strong>Heads-up on Google:</strong> Google deprecated the public sitemap-ping endpoint in 2023 — no service can "actively push" to Google anymore. If GSC shows <em>"Crawled — currently not indexed"</em>, your URLs <strong>are</strong> in Google's crawl cache; they just haven't been added to the index yet (Google's content-quality verdict). Fix: stronger descriptions, more internal links, and authority backlinks.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={runNow}
          disabled={running || loading}
          className="inline-flex items-center gap-2 bg-[#2563EB] text-white font-medium rounded-full px-5 py-2.5 disabled:opacity-60 disabled:cursor-not-allowed hover:bg-[#1D4ED8] transition-colors whitespace-nowrap shrink-0"
          data-testid="admin-seo-boost-run-now"
        >
          {running ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />}
          {running ? "Pinging…" : "Boost now"}
        </button>
      </div>

      {error && (
        <div className="mt-4 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</div>
      )}

      {loading ? (
        <div className="mt-4 text-xs text-[#94A3B8]"><Loader2 size={12} className="animate-spin inline" /> Loading status…</div>
      ) : (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white border border-[#E2E8F0] rounded-xl px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#64748B]">Last run</div>
            <div className="font-bold text-[#0F172A] mt-1 text-sm">{lastWhen}</div>
            {last && (
              <div className="text-[11px] text-[#64748B] mt-0.5">
                {last.url_count} URLs · <span className="font-mono">{last.source}</span>
              </div>
            )}
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#64748B]">Next scheduled run</div>
            <div className="font-bold text-[#0F172A] mt-1 text-sm">{nextWhen}</div>
            <div className="text-[11px] text-[#64748B] mt-0.5">03:00 UTC · daily{status?.runs_last_24h ? ` · ${status.runs_last_24h} runs in last 24h` : ""}</div>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#64748B]">What gets pinged</div>
            <div className="text-[11px] text-[#0F172A] mt-1 leading-snug">
              All active app pages + directory + Top 100 + categories + best-of lists.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ---- Content Quality modal — scan every app's description completeness ----
const STATUS_BADGE = {
  good:           { label: "GOOD",          cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  ok:             { label: "OK",            cls: "bg-blue-100 text-blue-700 border-blue-300" },
  "needs-upgrade":{ label: "UPGRADE",       cls: "bg-amber-100 text-amber-800 border-amber-300" },
  empty:          { label: "EMPTY",         cls: "bg-red-100 text-red-700 border-red-300" },
};

const ContentQualityModal = ({ onClose, onEdit }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    adminContentQuality()
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = (data?.apps || []).filter((a) => filter === "all" || a.status === filter);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="content-quality-modal">
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[88vh] flex flex-col">
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl text-[#0F172A] inline-flex items-center gap-2"><FileText size={18} className="text-[#2563EB]" /> Content quality scanner</h2>
            <p className="text-xs text-[#64748B] mt-0.5">See which app pages need a content upgrade for SEO — descriptions, translations, markdown formatting, pricing, features.</p>
          </div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A] p-1.5" data-testid="content-quality-close"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="p-12 text-center text-[#94A3B8]"><Loader2 size={24} className="animate-spin inline" /></div>
        ) : error ? (
          <div className="p-6 text-sm text-red-700 bg-red-50 m-6 rounded">{error}</div>
        ) : data && (
          <>
            <div className="px-6 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC] flex items-center gap-2 flex-wrap">
              {[
                { value: "all",            label: `All ${data.summary.total}` },
                { value: "empty",          label: `Empty ${data.summary.empty}` },
                { value: "needs-upgrade",  label: `Upgrade ${data.summary.needs_upgrade}` },
                { value: "ok",             label: `OK ${data.summary.ok}` },
                { value: "good",           label: `Good ${data.summary.good}` },
              ].map((f) => (
                <button
                  key={f.value}
                  onClick={() => setFilter(f.value)}
                  className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${filter === f.value ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-[#E2E8F0] text-[#64748B] hover:border-[#94A3B8]"}`}
                  data-testid={`content-quality-filter-${f.value}`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="overflow-auto flex-1">
              <table className="w-full text-sm">
                <thead className="bg-[#F8FAFC] text-[10px] uppercase tracking-widest text-[#64748B] font-bold sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2.5">App</th>
                    <th className="text-left px-2 py-2.5">Status</th>
                    <th className="text-center px-2 py-2.5">Score</th>
                    <th className="text-center px-2 py-2.5">EN</th>
                    <th className="text-center px-2 py-2.5">NL</th>
                    <th className="text-center px-2 py-2.5">MD</th>
                    <th className="text-left px-2 py-2.5">Next action</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a) => {
                    const b = STATUS_BADGE[a.status] || STATUS_BADGE.empty;
                    return (
                      <tr key={a.slug} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`content-quality-row-${a.slug}`}>
                        <td className="px-4 py-2.5">
                          <div className="font-bold text-[#0F172A]">{a.name}</div>
                          <div className="text-[10px] font-mono text-[#94A3B8]">{a.slug}</div>
                        </td>
                        <td className="px-2 py-2.5"><span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${b.cls}`}>{b.label}</span></td>
                        <td className="px-2 py-2.5 text-center font-mono tabular-nums text-[#0F172A]">{a.score}</td>
                        <td className="px-2 py-2.5 text-center text-xs text-[#64748B] font-mono tabular-nums">{a.description_len || "—"}</td>
                        <td className="px-2 py-2.5 text-center text-xs text-[#64748B] font-mono tabular-nums">{a.description_nl_len || "—"}</td>
                        <td className="px-2 py-2.5 text-center text-xs">
                          {a.has_markdown_en && <span title="EN uses markdown" className="text-emerald-600">●</span>}
                          {a.has_markdown_nl && <span title="NL uses markdown" className="text-emerald-600 ml-0.5">●</span>}
                          {!a.has_markdown_en && !a.has_markdown_nl && <span className="text-[#CBD5E1]">—</span>}
                        </td>
                        <td className="px-2 py-2.5 text-xs text-[#64748B] max-w-[280px] truncate" title={a.recommendations.join(" · ")}>
                          {a.recommendations[0] || (a.status === "good" ? "✓ Looking good" : "—")}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <button onClick={() => onEdit(a.slug)} className="text-xs text-[#2563EB] hover:underline inline-flex items-center gap-1" data-testid={`content-quality-edit-${a.slug}`}><Edit3 size={11} /> Edit</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="text-center py-12 text-sm text-[#94A3B8] italic">No apps match this filter.</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};


// ---- Publisher Digest panel — weekly mail status + preview/send tools ----
// ---- SMTP smoke test panel — one-click full pipeline check -----------------

const SmtpSmokeTestPanel = () => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const run = async () => {
    setBusy(true); setResult(null); setErr("");
    try {
      const r = await adminEmailSendTest();
      setResult(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Send failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 mb-4" data-testid="smtp-smoke-test-panel">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 shrink-0 bg-emerald-500 text-white rounded-xl flex items-center justify-center">
          <Send size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-[#0F172A] text-base">SMTP smoke test</h3>
          <p className="text-xs text-[#475569] mt-0.5 leading-relaxed">
            Sends a real probe email to <code className="bg-white px-1 rounded">ADMIN_NOTIFY_EMAIL</code> — verifies config + DNS + SMTP auth + recipient delivery in one click. Use after every deploy.
          </p>
          {result && (
            <div className="mt-2 text-xs text-emerald-800 bg-white border border-emerald-200 rounded-lg p-2.5 leading-relaxed" data-testid="smtp-smoke-test-result">
              ✅ Sent to <strong>{result.sent_to}</strong> at <span className="font-mono">{result.sent_at?.slice(11,19)}Z</span> — check that inbox in 30s.
            </div>
          )}
          {err && (
            <div className="mt-2 text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg p-2.5 leading-relaxed" data-testid="smtp-smoke-test-error">
              ❌ {err}
            </div>
          )}
        </div>
        <button
          onClick={run}
          disabled={busy}
          className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5 disabled:opacity-60"
          data-testid="smtp-smoke-test-run"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {busy ? "Sending…" : "Send test email"}
        </button>
      </div>
    </div>
  );
};


const PublisherDigestPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [previewEmail, setPreviewEmail] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const refresh = () => {
    setLoading(true);
    adminDigestStatus()
      .then(setStatus)
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  const runNow = async () => {
    if (!window.confirm("Send the weekly digest email to ALL verified publishers right now?")) return;
    setRunning(true);
    setError("");
    try {
      const r = await adminDigestRunNow();
      alert(`✓ Digest sent\n\n${r.sent} / ${r.candidates} publishers received the email.`);
      refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  const doPreview = async () => {
    if (!previewEmail.trim()) return;
    setPreviewing(true);
    setError("");
    try {
      const r = await adminDigestPreview(previewEmail.trim().toLowerCase());
      if (!r.ok) {
        setError(r.reason || "No data for this email");
        setPreview(null);
      } else {
        setPreview(r);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const last = status?.last_run;
  const lastWhen = last?.ts ? new Date(last.ts).toLocaleString() : "Never";
  const nextWhen = status?.next_run ? new Date(status.next_run).toLocaleString() : "—";

  return (
    <div className="bg-gradient-to-br from-[#FEF3C7] to-white border border-[#FDE68A] rounded-2xl p-6 md:p-8" data-testid="admin-digest-panel">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 shrink-0 bg-amber-500 text-white rounded-xl flex items-center justify-center">
            <Send size={20} />
          </div>
          <div className="min-w-0">
            <h2 className="font-heading text-xl text-[#0F172A]">Publisher digest</h2>
            <p className="text-sm text-[#64748B] mt-1 max-w-2xl leading-relaxed">
              Weekly email to every verified publisher with their clicks, content health score and 1-2 quick wins.
              Runs automatically every <strong>Tuesday 09:00 UTC</strong>.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={runNow}
          disabled={running || loading}
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-full px-5 py-2.5 disabled:opacity-60 whitespace-nowrap shrink-0"
          data-testid="admin-digest-run-now"
        >
          {running ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          {running ? "Sending…" : "Send now"}
        </button>
      </div>

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mb-4">{error}</div>
      )}

      {!loading && status && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          <div className="bg-white border border-[#E2E8F0] rounded-xl px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#64748B]">Last run</div>
            <div className="font-bold text-[#0F172A] mt-1 text-sm">{lastWhen}</div>
            {last && <div className="text-[11px] text-[#64748B] mt-0.5">{last.sent}/{last.candidates} sent · {last.source}</div>}
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#64748B]">Next scheduled run</div>
            <div className="font-bold text-[#0F172A] mt-1 text-sm">{nextWhen}</div>
            <div className="text-[11px] text-[#64748B] mt-0.5">Tuesday 09:00 UTC · weekly</div>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#64748B] mb-2">Preview digest for a publisher</div>
        <div className="flex gap-2 mb-3">
          <input
            type="email"
            value={previewEmail}
            onChange={(e) => setPreviewEmail(e.target.value)}
            placeholder="publisher@example.com"
            className="flex-1 border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
            data-testid="admin-digest-preview-email"
          />
          <button
            onClick={doPreview}
            disabled={previewing || !previewEmail.trim()}
            className="bg-[#0F172A] hover:bg-[#1E293B] text-white px-4 py-2 rounded-lg text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="admin-digest-preview-btn"
          >
            {previewing ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
            Preview
          </button>
        </div>
        {preview && (
          <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="admin-digest-preview-result">
            <div className="bg-[#F8FAFC] px-3 py-2 text-xs border-b border-[#E2E8F0]">
              <strong className="text-[#0F172A]">Subject:</strong> <span className="text-[#475569]">{preview.subject}</span>
            </div>
            <iframe
              srcDoc={preview.html}
              title="Digest preview"
              className="w-full"
              style={{ height: "60vh", minHeight: 500, border: 0 }}
              sandbox=""
            />
          </div>
        )}
      </div>
    </div>
  );
};

